import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/App";
import { useSearchParams } from "react-router-dom";
import usePageTitle from "@/hooks/usePageTitle";
import { formatDateDashDisplay, compareByMonthYear, parseDateComponents } from "@/utils/dateUtils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CrDrEntriesPanel from "@/components/CrDrEntriesPanel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Search,
  Filter,
  RefreshCw,
  DollarSign,
  Building,
  Calendar,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  BookPlus,
  CheckSquare,
  Loader2,
  FileText,
  Banknote,
  Receipt,
  Upload,
  Database,
  FileCheck,
  FileSpreadsheet,
  FileDown,
  Printer,
  Download,
  Users,
  Package,
  Settings,
  Truck,
  Calculator,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  SlidersHorizontal,
  Wrench,
  Trash2,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Info,
  BarChart3,
  ShieldAlert,
  TrendingDown,
  FileWarning,
  Repeat,
  Gift,
  ExternalLink,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import jsPDF from "jspdf";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";
import StaleUploadsCleanupDialog from "@/components/payment-verification/StaleUploadsCleanupDialog";
import "jspdf-autotable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Small confidence dot rendered next to a payment date. Green = came straight
// from the sheet's Order Date column, amber = extracted from narration text,
// grey = filename fallback, red = last-resort today's date. Hover shows why.
const DateSourceDot = ({ source }) => {
  if (!source || source === "row_column" || source === "sales_lookup") {
    return (
      <span title="Date came from the sheet's Order Date column (high confidence)" className="inline-block w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" data-testid={`date-source-dot-${source || "row"}`} />
    );
  }
  if (source === "text_extract") {
    return (
      <span title="Date extracted from narration/payment mode text (medium confidence)" className="inline-block w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" data-testid="date-source-dot-text" />
    );
  }
  if (source === "filename") {
    return (
      <span title="Date inferred from the uploaded filename (low confidence)" className="inline-block w-2 h-2 rounded-full bg-slate-400 flex-shrink-0" data-testid="date-source-dot-filename" />
    );
  }
  // "today" or "unknown"
  return (
    <span title="Fallback to today's date — please verify" className="inline-block w-2 h-2 rounded-full bg-rose-500 flex-shrink-0 animate-pulse" data-testid="date-source-dot-today" />
  );
};

const PaymentVerificationPage = () => {
  usePageTitle("Payment Verification");
  const { token, user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const [payments, setPayments] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [showZeroOnly, setShowZeroOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    pending: 0,
    verified_at_upload: 0,
    verified_full: 0,
    verified_adjusted: 0,
    rejected: 0,
    total_claimed: 0,
    total_verified: 0,
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [reshipOnly, setReshipOnly] = useState(false);
  const [latestUploadOnly, setLatestUploadOnly] = useState(false);
  const [rawVerifiedOnly, setRawVerifiedOnly] = useState(false);
  const [lowConfidenceDatesOnly, setLowConfidenceDatesOnly] = useState(false);
  const [clientFilter, setClientFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Count active filters
  const activeFilterCount = [
    statusFilter !== "all",
    clientFilter !== "all",
    searchQuery !== "",
    dateFrom !== "",
    dateTo !== ""
  ].filter(Boolean).length;

  // Verification Dialog
  const [verifyDialog, setVerifyDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [cleaningData, setCleaningData] = useState(false);
  const [fixingDates, setFixingDates] = useState(false);
  
  // Wipe Data Dialog
  const [wipeDialog, setWipeDialog] = useState(false);
  const [wipePassword, setWipePassword] = useState("");
  const [wipeType, setWipeType] = useState("client"); // "client", "date_range", "all"
  const [wipeClientCode, setWipeClientCode] = useState("");
  const [wipeDateFrom, setWipeDateFrom] = useState("");
  const [wipeDateTo, setWipeDateTo] = useState("");
  const [wiping, setWiping] = useState(false);

  // Delete Selected Dialog
  const [deleteSelectedDialog, setDeleteSelectedDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deletingSelected, setDeletingSelected] = useState(false);

  // Deletion Logs
  const [deletionLogs, setDeletionLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [clearLogsDialog, setClearLogsDialog] = useState(false);
  const [clearLogsPassword, setClearLogsPassword] = useState("");
  const [clearingLogs, setClearingLogs] = useState(false);
  const [selectedLogIds, setSelectedLogIds] = useState(new Set());

  // Uploaded Sheets (Partner Ledger uploads — original Excel download)
  const [uploadedSheets, setUploadedSheets] = useState([]);
  const [uploadedSheetsTotal, setUploadedSheetsTotal] = useState(0);
  const [uploadedSheetsPage, setUploadedSheetsPage] = useState(1);
  const [uploadedSheetsLimit] = useState(50);
  const [loadingUploadedSheets, setLoadingUploadedSheets] = useState(false);
  const [uploadedSheetsPartner, setUploadedSheetsPartner] = useState("");
  const [downloadingSheetId, setDownloadingSheetId] = useState(null);
  const [staleCleanupOpen, setStaleCleanupOpen] = useState(false);

  // Raw Funds (accounting team's bank-receipts sheet — feeds auto-verification)
  const [rawFundsItems, setRawFundsItems] = useState([]);
  const [rawFundsTotal, setRawFundsTotal] = useState(0);
  const [rawFundsSumInr, setRawFundsSumInr] = useState(0);
  const [rawFundsPage, setRawFundsPage] = useState(1);
  const [rawFundsPageSize] = useState(50);
  const [rawFundsLoading, setRawFundsLoading] = useState(false);
  const [rawFundsUploads, setRawFundsUploads] = useState([]);
  const [rawFundsSearch, setRawFundsSearch] = useState("");
  const [rawFundsClient, setRawFundsClient] = useState("");
  const [rawFundsDateFrom, setRawFundsDateFrom] = useState("");
  const [rawFundsDateTo, setRawFundsDateTo] = useState("");
  // Suspense filter — visible to both admin and uploader. When ON, only
  // rows without a client_code (the resolution queue) are shown.
  const [rawFundsOnlySuspense, setRawFundsOnlySuspense] = useState(false);
  // Recent client-code reassignment / suspense-resolution history. Available
  // to admin AND uploader (the upload team self-services its own audit).
  const [rawFundsHistoryOpen, setRawFundsHistoryOpen] = useState(false);
  const [rawFundsHistoryLogs, setRawFundsHistoryLogs] = useState([]);
  const [rawFundsHistoryLoading, setRawFundsHistoryLoading] = useState(false);
  const fetchRawFundsHistory = async () => {
    setRawFundsHistoryLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/activity-history?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setRawFundsHistoryLogs(data.logs || []);
    } catch (e) {
      toast.error("Failed to load history");
    } finally {
      setRawFundsHistoryLoading(false);
    }
  };
  const [rawFundsSummary, setRawFundsSummary] = useState(null);
  const [rawFundsClientOptions, setRawFundsClientOptions] = useState([]);
  const [rawFundsCoverage, setRawFundsCoverage] = useState({ with_customer: 0, with_narration: 0, with_country: 0 });
  const [rawFundsSumUsd2, setRawFundsSumUsd2] = useState(0);
  const [rawFundsSortKey, setRawFundsSortKey] = useState("date");
  const [rawFundsSortDir, setRawFundsSortDir] = useState("desc");
  const [rawFundsSelected, setRawFundsSelected] = useState(new Set());
  const [rawFundsBulkPassword, setRawFundsBulkPassword] = useState("");
  const [rawFundsBulkDeleting, setRawFundsBulkDeleting] = useState(false);
  const [rawFundsBulkDialogOpen, setRawFundsBulkDialogOpen] = useState(false);
  // Inline client_code editor on Raw Data table
  const [rawCanonicalCodes, setRawCanonicalCodes] = useState([]);
  const [rawEditingRowId, setRawEditingRowId] = useState(null);
  const [rawEditingSearch, setRawEditingSearch] = useState("");
  const [rawEditingSaving, setRawEditingSaving] = useState(false);
  // Source filter + summary
  const [rawFundsSource, setRawFundsSource] = useState("");
  // --- Raw Funds sheet upload with preview/verify flow ---
  const rawFundsUploadInputRef = useRef(null);
  const [rawFundsPreview, setRawFundsPreview] = useState(null);
  const [rawFundsPreviewOpen, setRawFundsPreviewOpen] = useState(false);
  const [rawFundsPendingFile, setRawFundsPendingFile] = useState(null);
  const [rawFundsUploadMode, setRawFundsUploadMode] = useState("add");
  const [rawFundsVerified, setRawFundsVerified] = useState(false);
  const [rawFundsUploadingSheet, setRawFundsUploadingSheet] = useState(false);
  const [rawFundsPreviewing, setRawFundsPreviewing] = useState(false);
  // --- Raw Funds password-gated inline field editing ---
  const [rawFieldEdit, setRawFieldEdit] = useState(null); // {rowId, field, value}
  const [rawFieldSaving, setRawFieldSaving] = useState(false);
  const [rawEditPassword, setRawEditPassword] = useState("");
  const [rawPwDialogOpen, setRawPwDialogOpen] = useState(false);
  const [rawPwInput, setRawPwInput] = useState("");
  // --- Duplicate payment scanner ---
  const [dupScanOpen, setDupScanOpen] = useState(false);
  const [dupScanLoading, setDupScanLoading] = useState(false);
  const [dupScan, setDupScan] = useState(null);
  const [dupMerging, setDupMerging] = useState("");
  const [dupPw, setDupPw] = useState("");
  const [rawSourceOptions, setRawSourceOptions] = useState([]);
  const [rawSourceSummaryOpen, setRawSourceSummaryOpen] = useState(false);
  const [rawSourceSummaryData, setRawSourceSummaryData] = useState(null);
  const [rawSourceSummaryLoading, setRawSourceSummaryLoading] = useState(false);
  const [rawSourceSummaryFrom, setRawSourceSummaryFrom] = useState("");
  const [rawSourceSummaryTo, setRawSourceSummaryTo] = useState("");
  // Client code summary (twin of Source Summary)
  const [rawClientSummaryOpen, setRawClientSummaryOpen] = useState(false);
  const [rawClientSummaryData, setRawClientSummaryData] = useState(null);
  const [rawClientSummaryLoading, setRawClientSummaryLoading] = useState(false);
  const [rawClientSummaryFrom, setRawClientSummaryFrom] = useState("");
  const [rawClientSummaryTo, setRawClientSummaryTo] = useState("");

  // Verification Form - Updated for Double Verification System
  const [verificationForm, setVerificationForm] = useState({
    verification_status: "verified_full",
    actual_amount: "",
    received_date: "",
    bank_reference: "",
    difference_reason: "", // Bank Charges, Forex Charges, Error, Lesser Amount Came
    bank_charges: "",
    forex_charges: "",
    error_amount: "",
    lesser_amount: "",
    other_notes: "",
    verification_notes: "",
  });

  // Bulk selection
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [bulkVerifying, setBulkVerifying] = useState(false);

  // Reports Section State
  const [activeTab, setActiveTab] = useState("verification");
  const [searchParams] = useSearchParams();
  
  // Read tab from URL query param on mount
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && ["verification", "verified-done", "pending-sales", "follow-up", "auto-generate", "logs", "raw-funds", "uploaded-sheets", "foc", "crdr"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);
  const [pendingSalesData, setPendingSalesData] = useState(null);
  const [autoGenerateData, setAutoGenerateData] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportDateFrom, setReportDateFrom] = useState("");
  const [reportDateTo, setReportDateTo] = useState("");
  const [reportSearchTerm, setReportSearchTerm] = useState("");
  const [expandedPartners, setExpandedPartners] = useState(new Set());
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [reportExportMode, setReportExportMode] = useState("single");
  const [reportSortConfig, setReportSortConfig] = useState({ key: 'total_orders', direction: 'desc' });

  // Suspicious ledger cleanup modal (admin-only)
  const [cleanupModal, setCleanupModal] = useState({
    open: false,
    code: "",
    orderCount: 0,
    action: "delete", // "delete" | "rename"
    newCode: "",
    password: "",
  });
  const [cleanupLoading, setCleanupLoading] = useState(false);

  // Cleanup history (recent delete/rename actions with restore capability)
  const [cleanupHistory, setCleanupHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [restorePromptId, setRestorePromptId] = useState(null);
  const [restorePassword, setRestorePassword] = useState("");
  const [restoreLoading, setRestoreLoading] = useState(false);

  // Table sorting state (for main payments table)
  const [tableSortConfig, setTableSortConfig] = useState({ key: 'payment_date', direction: 'desc' });

  // Verified Done tab state
  const [verifiedSearchQuery, setVerifiedSearchQuery] = useState("");
  const [verifiedSelectedPayments, setVerifiedSelectedPayments] = useState([]);
  const [verifiedTableSortConfig, setVerifiedTableSortConfig] = useState({ key: 'payment_date', direction: 'desc' });
  const [verifiedReshipOnly, setVerifiedReshipOnly] = useState(false);
  // Extra pill/dropdown filters mirrored on Verification + Verified Done + FOC tabs
  const [modeFilter, setModeFilter] = useState("all"); // payment_mode dropdown (Verification tab)
  const [verifiedModeFilter, setVerifiedModeFilter] = useState("all"); // payment_mode dropdown (Verified Done tab)
  const [verifiedRawVerifiedOnly, setVerifiedRawVerifiedOnly] = useState(false);
  const [verifiedZeroOnly, setVerifiedZeroOnly] = useState(false);
  // FOC sub-panel state (verified rows that were reship OR zero-payment)
  const [focClientFilter, setFocClientFilter] = useState("all");
  const [focSearchQuery, setFocSearchQuery] = useState("");
  const [focModeFilter, setFocModeFilter] = useState("all");
  const [focTypeFilter, setFocTypeFilter] = useState("all"); // "all" | "reship" | "zero"

  // Follow Up Payment Recovery states
  const [followUpData, setFollowUpData] = useState({});
  const [followUpPartners, setFollowUpPartners] = useState([]);
  const [followUpFirstOrders, setFollowUpFirstOrders] = useState({});
  const [followUpSaleMonths, setFollowUpSaleMonths] = useState({});
  const [followUpHandlers, setFollowUpHandlers] = useState({});
  const [followUpHandlerFilter, setFollowUpHandlerFilter] = useState("all");
  const [showInactiveFollowUp, setShowInactiveFollowUp] = useState(false);
  const [handlerEdit, setHandlerEdit] = useState(null); // {partner, value}
  const [handlerPwOpen, setHandlerPwOpen] = useState(false);
  const [handlerPwInput, setHandlerPwInput] = useState("");
  const [handlerSaving, setHandlerSaving] = useState(false);
  const [followUpSelectedPartners, setFollowUpSelectedPartners] = useState([]);
  const [followUpYear, setFollowUpYear] = useState(new Date().getFullYear());
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // {partner, month, field}
  const [editingValue, setEditingValue] = useState("");
  const [followUpStatusFilter, setFollowUpStatusFilter] = useState("all");
  const [followUpMonthFilter, setFollowUpMonthFilter] = useState("all"); // "all" or 1-12
  const [verifiedClientFilter, setVerifiedClientFilter] = useState("all");
  const [verifiedFiltersExpanded, setVerifiedFiltersExpanded] = useState(false);
  const [verifiedDateFrom, setVerifiedDateFrom] = useState("");
  const [verifiedDateTo, setVerifiedDateTo] = useState("");

  // Pagination state
  const [pendingPage, setPendingPage] = useState(1);
  const [verifiedPage, setVerifiedPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const verifiedActiveFilterCount = [
    verifiedClientFilter !== "all",
    verifiedSearchQuery !== "",
    verifiedDateFrom !== "",
    verifiedDateTo !== ""
  ].filter(Boolean).length;

  // Handle table sort - toggle direction on same column, default desc on new column
  const handleTableSort = (key) => {
    setTableSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Table sort icon component
  const TableSortIcon = ({ columnKey }) => {
    if (tableSortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    }
    return tableSortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" />
      : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />;
  };

  // Verified Done tab sort handler
  const handleVerifiedTableSort = (key) => {
    setVerifiedTableSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const VerifiedSortIcon = ({ columnKey }) => {
    if (verifiedTableSortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    }
    return verifiedTableSortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-green-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-green-600" />;
  };

  // Convert DD-MM-YY payment_date to YYYY-MM-DD for comparison with date inputs
  const parseDateToISO = (dateStr) => {
    if (!dateStr) return "";
    const s = String(dateStr).trim();
    // Handle DD-MM-YY format
    if (s.length === 8 && s[2] === "-" && s[5] === "-") {
      const [dd, mm, yy] = s.split("-");
      const year = parseInt(yy) < 50 ? `20${yy}` : `19${yy}`;
      return `${year}-${mm}-${dd}`;
    }
    // Handle DDMMYY format (no dashes)
    if (s.length === 6 && /^\d+$/.test(s)) {
      const dd = s.substring(0, 2);
      const mm = s.substring(2, 4);
      const yy = s.substring(4, 6);
      const year = parseInt(yy) < 50 ? `20${yy}` : `19${yy}`;
      return `${year}-${mm}-${dd}`;
    }
    // Already YYYY-MM-DD
    if (s.length >= 10 && s[4] === "-") return s.substring(0, 10);
    return s;
  };

  // Fetch payments
  const fetchPayments = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      let url = `${BACKEND_URL}/api/payment-verification/payments?limit=100000&include_zero=true`;
      // Only pass client filter to API - status filtering is done client-side
      // so that summary cards always reflect full partner figures
      if (clientFilter !== "all") url += `&client_code=${clientFilter}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments || []);
        setSummary(data.summary || {});
      } else {
        // Surface backend errors (esp. HTTP 503 timeouts from a slow Atlas node)
        // instead of silently leaving the page with empty summary counts, which
        // used to look like "data loss".
        let detail = "";
        try {
          const err = await response.json();
          detail = err.detail || err.message || "";
        } catch (_) {
          // non-JSON body — keep default detail
        }
        const label = detail || `Server responded ${response.status}`;
        setLoadError(label);
        if (response.status === 503) {
          toast.error("Database is busy — please retry in a moment.");
        } else {
          toast.error(`Failed to load payments: ${label}`);
        }
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
      setLoadError(error.message || "Network error");
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  // Fetch clients
  const fetchClients = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/payment-verification/clients`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setClients(data.clients || []);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  // Cleanup existing data (fix dates, client codes, remove duplicates)
  const handleCleanupData = async () => {
    if (!window.confirm(
      "This will clean up existing payment data:\n\n" +
      "• Fix Excel serial dates to readable format (DD-MM-YY)\n" +
      "• Remove month prefixes from client codes (e.g., 'DEC AHEIRON' → 'AHEIRON')\n" +
      "• Remove duplicate entries\n\n" +
      "This operation cannot be undone. Continue?"
    )) {
      return;
    }
    
    setCleaningData(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/payment-verification/cleanup-existing-data`,
        {
          method: "POST",
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(
          `Cleanup completed!\n` +
          `• ${data.stats.uploads_processed} uploads processed\n` +
          `• ${data.stats.dates_fixed} dates fixed\n` +
          `• ${data.stats.client_codes_normalized} client codes normalized\n` +
          `• ${data.stats.duplicates_removed} duplicates removed`
        );
        // Refresh the data
        fetchPayments();
        fetchClients();
      } else {
        const error = await response.json();
        toast.error(`Cleanup failed: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error cleaning data:", error);
      toast.error("Failed to cleanup data");
    } finally {
      setCleaningData(false);
    }
  };

  // Handle fix date formats
  const handleFixDates = async () => {
    if (!window.confirm(
      "This will convert all Excel serial number dates (like 45748) to readable DD-MM-YY format in existing payment records.\n\nContinue?"
    )) return;
    
    setFixingDates(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/payment-verification/fix-dates`,
        {
          method: "POST",
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        toast.success(
          `Date fix completed!\n` +
          `${data.stats.upload_dates_fixed} upload dates fixed\n` +
          `${data.stats.sales_dates_fixed} sales dates fixed`
        );
        fetchPayments();
      } else {
        const error = await response.json();
        toast.error(`Fix dates failed: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error fixing dates:", error);
      toast.error("Failed to fix dates");
    } finally {
      setFixingDates(false);
    }
  };

  // Handle wipe data
  const handleWipeData = async () => {
    if (!wipePassword) {
      toast.error("Password is required");
      return;
    }
    
    if (wipeType === "client" && !wipeClientCode) {
      toast.error("Please select a client code");
      return;
    }
    
    if (wipeType === "date_range" && !wipeDateFrom && !wipeDateTo) {
      toast.error("Please select at least one date");
      return;
    }
    
    const confirmMsg = wipeType === "all" 
      ? "⚠️ WARNING: This will DELETE ALL payment verification data! Are you absolutely sure?"
      : wipeType === "client"
      ? `This will delete all payment data for ${wipeClientCode}. Continue?`
      : `This will delete all payment data between ${wipeDateFrom || 'beginning'} and ${wipeDateTo || 'now'}. Continue?`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setWiping(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/payment-verification/wipe-data`,
        {
          method: "POST",
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            password: wipePassword,
            wipe_type: wipeType,
            client_code: wipeClientCode,
            date_from: wipeDateFrom,
            date_to: wipeDateTo
          })
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        toast.success(
          `Wipe completed!\n` +
          `• ${data.stats.uploads_deleted} uploads deleted\n` +
          `• ${data.stats.entries_deleted || 0} entries removed`
        );
        setWipeDialog(false);
        setWipePassword("");
        setWipeClientCode("");
        setWipeDateFrom("");
        setWipeDateTo("");
        fetchPayments();
        fetchClients();
      } else {
        toast.error(`Wipe failed: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error wiping data:", error);
      toast.error("Failed to wipe data");
    } finally {
      setWiping(false);
    }
  };

  // Handle delete selected payments
  const handleDeleteSelected = async () => {
    if (!deletePassword) {
      toast.error("Password is required");
      return;
    }
    
    const paymentsToDelete = activeTab === "verified-done" ? verifiedSelectedPayments : selectedPayments;
    
    if (paymentsToDelete.length === 0) {
      toast.error("No payments selected");
      return;
    }
    
    setDeletingSelected(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/payment-verification/delete-selected`,
        {
          method: "POST",
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            password: deletePassword,
            payments: paymentsToDelete.map(p => ({
              upload_id: p.upload_id,
              partner_code: p.client_code || p.partner_code,
              tracking: p.tracking,
              payment_id: p.payment_id,
              source: p.source || "upload"
            }))
          })
        }
      );

      // Read body safely - some browsers consume the stream early
      let data = {};
      try {
        const cloned = response.clone();
        data = await cloned.json();
      } catch {
        try { data = JSON.parse(await response.text()); } catch { /* ignore */ }
      }
      
      if (response.ok || response.status === 200) {
        toast.success(`Deleted ${data.deleted_count || paymentsToDelete.length} payment(s)`);
        setDeleteSelectedDialog(false);
        setDeletePassword("");
        if (activeTab === "verified-done") {
          setVerifiedSelectedPayments([]);
        } else {
          setSelectedPayments([]);
        }
        fetchPayments();
      } else if (response.status === 403) {
        toast.error(data.detail || "Invalid password");
      } else {
        toast.error(data.detail || `Delete failed (${response.status})`);
      }
    } catch (error) {
      toast.error("Network error — please check your connection and try again");
    } finally {
      setDeletingSelected(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchClients();
  }, [clientFilter]);

  // ============================================================
  // REPORTS SECTION FUNCTIONS
  // ============================================================

  // Fetch Pending Sales Report
  const fetchPendingSalesReport = async (overrideDateFrom, overrideDateTo) => {
    setReportsLoading(true);
    try {
      const params = new URLSearchParams();
      // Guard against accidental React-event passing from onClick={fetchFn}
      // — only honour explicit string overrides.
      const dfOverride = typeof overrideDateFrom === "string" ? overrideDateFrom : undefined;
      const dtOverride = typeof overrideDateTo === "string" ? overrideDateTo : undefined;
      const df = dfOverride !== undefined ? dfOverride : reportDateFrom;
      const dt = dtOverride !== undefined ? dtOverride : reportDateTo;
      if (df) params.append('date_from', df);
      if (dt) params.append('date_to', dt);
      
      const url = `${BACKEND_URL}/api/reports/pending-sales${params.toString() ? `?${params}` : ''}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingSalesData(data);
      } else {
        toast.error("Failed to fetch Pending Sales Report");
      }
    } catch (error) {
      toast.error("Error loading report");
    } finally {
      setReportsLoading(false);
    }
  };

  // Fetch Auto Generate Report
  const fetchAutoGenerateReport = async (overrideDateFrom, overrideDateTo) => {
    setReportsLoading(true);
    try {
      const params = new URLSearchParams();
      // Guard against accidental React-event passing from onClick={fetchFn}
      const dfOverride = typeof overrideDateFrom === "string" ? overrideDateFrom : undefined;
      const dtOverride = typeof overrideDateTo === "string" ? overrideDateTo : undefined;
      const df = dfOverride !== undefined ? dfOverride : reportDateFrom;
      const dt = dtOverride !== undefined ? dtOverride : reportDateTo;
      if (df) params.append('date_from', df);
      if (dt) params.append('date_to', dt);
      
      const url = `${BACKEND_URL}/api/reports/pending-auto-generate${params.toString() ? `?${params}` : ''}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAutoGenerateData(data);
      } else {
        toast.error("Failed to fetch Auto Generate Report");
      }
    } catch (error) {
      toast.error("Error loading report");
    } finally {
      setReportsLoading(false);
    }
  };

  // ============================================================
  // FOLLOW UP RECOVERY TRACKING
  // ============================================================
  const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const fetchFollowUpData = async () => {
    setFollowUpLoading(true);
    try {
      const params = new URLSearchParams({ year: followUpYear });
      if (followUpSelectedPartners.length > 0) {
        params.append("partner_code", followUpSelectedPartners.join(","));
      }
      const res = await fetch(`${BACKEND_URL}/api/payment-followups?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Build lookup: { "PARTNER|3": { field: value, ... } }
        const lookup = {};
        (data.records || []).forEach(r => {
          lookup[`${r.partner_code}|${r.month}`] = r;
        });
        setFollowUpData(lookup);
        setFollowUpPartners(data.partner_list || []);
        setFollowUpFirstOrders(data.first_order_dates || {});
        setFollowUpSaleMonths(data.sale_months || {});
        setFollowUpHandlers(data.handlers || {});
      } else {
        toast.error("Failed to load follow-up data");
      }
    } catch (err) {
      console.error("Error fetching follow-ups:", err);
      toast.error("Error loading follow-up data");
    } finally {
      setFollowUpLoading(false);
    }
  };

  const saveFollowUpCell = async (partnerCode, month, field, value) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment-followups/update`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ partner_code: partnerCode, year: followUpYear, month, field, value })
      });
      if (res.ok) {
        // Update local state
        setFollowUpData(prev => {
          const key = `${partnerCode}|${month}`;
          return { ...prev, [key]: { ...(prev[key] || {}), partner_code: partnerCode, month, year: followUpYear, [field]: value } };
        });
      } else {
        toast.error("Failed to save");
      }
    } catch {
      toast.error("Network error saving follow-up");
    }
  };

  const handleFollowUpDoubleClick = (partnerCode, month, field) => {
    if (field === "ac_settled" && user?.role !== "super_admin") {
      toast.error("Only admin can update A/c Settled");
      return;
    }
    const key = `${partnerCode}|${month}`;
    const current = followUpData[key]?.[field] || "";
    if (!current) {
      // Auto-fill today's date
      const today = format(new Date(), "dd-MM-yy");
      saveFollowUpCell(partnerCode, month, field, today);
    } else {
      // Open inline edit
      setEditingCell({ partner: partnerCode, month, field });
      setEditingValue(current);
    }
  };

  const commitFollowUpEdit = () => {
    if (editingCell) {
      saveFollowUpCell(editingCell.partner, editingCell.month, editingCell.field, editingValue);
      setEditingCell(null);
      setEditingValue("");
    }
  };

  const handleFollowUpKeyDown = (e) => {
    if (e.key === "Enter") { commitFollowUpEdit(); }
    if (e.key === "Escape") { setEditingCell(null); setEditingValue(""); }
  };

  // Partners to display in the follow-up table
  const { list: followUpDisplayPartners, hiddenCount: followUpHiddenCount } = useMemo(() => {
    let list = followUpSelectedPartners.length > 0
      ? followUpSelectedPartners
      : followUpPartners.map(p => p.code);
    if (followUpHandlerFilter !== "all") {
      list = list.filter(pc => followUpHandlerFilter === "__unassigned"
        ? !followUpHandlers[pc]
        : followUpHandlers[pc] === followUpHandlerFilter);
    }
    // Partners with ZERO sales in the entire selected year (closed dealing / moved)
    // are hidden — the peek toggle reveals them
    const yearPrefix = `${followUpYear}-`;
    const isActive = (pc) => (followUpSaleMonths[pc] || []).some(ym => ym.startsWith(yearPrefix));
    const active = list.filter(isActive);
    return { list: showInactiveFollowUp ? list : active, hiddenCount: list.length - active.length };
  }, [followUpSelectedPartners, followUpPartners, followUpHandlerFilter, followUpHandlers, followUpSaleMonths, followUpYear, followUpData, showInactiveFollowUp]);

  // Save "Handle By" (password protected — Om@88 only)
  const commitHandlerEdit = async (pw) => {
    if (!handlerEdit) return;
    setHandlerSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment-followups/handler`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ partner_code: handlerEdit.partner, handled_by: handlerEdit.value, password: pw }),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Save failed"); return; }
      setFollowUpHandlers(prev => {
        const next = { ...prev };
        if (d.handled_by) next[handlerEdit.partner] = d.handled_by;
        else delete next[handlerEdit.partner];
        return next;
      });
      toast.success(d.handled_by ? `${handlerEdit.partner} → handled by ${d.handled_by}` : `Handler cleared for ${handlerEdit.partner}`);
      setHandlerEdit(null);
      setHandlerPwOpen(false);
      setHandlerPwInput("");
    } catch (e) {
      toast.error("Network error: " + e.message);
    } finally {
      setHandlerSaving(false);
    }
  };

  // Calculate closing balance totals
  const followUpClosingTotal = useMemo(() => {
    let total = 0;
    followUpDisplayPartners.forEach(pc => {
      for (let m = 1; m <= 12; m++) {
        const val = parseFloat(followUpData[`${pc}|${m}`]?.closing_balance || 0);
        if (!isNaN(val)) total += val;
      }
    });
    return total;
  }, [followUpDisplayPartners, followUpData]);

  // Build flat list of all partner+month rows with status classification
  const followUpRows = useMemo(() => {
    const rows = [];
    const now = new Date();
    const curY = now.getFullYear();
    const curM = now.getMonth() + 1;
    followUpDisplayPartners.forEach(pc => {
      const fo = followUpFirstOrders[pc];
      const saleSet = new Set(followUpSaleMonths[pc] || []);
      for (let m = 1; m <= 12; m++) {
        const key = `${pc}|${m}`;
        const row = followUpData[key] || {};
        // Months before the partner's FIRST parcel are not valid for follow-up
        const isPreStart = !!fo && (followUpYear < fo.year || (followUpYear === fo.year && m < fo.month));
        // Fully-elapsed months (strictly before the current month) with zero
        // sales/orders are locked — nothing to follow up on. The current month
        // stays editable since sales can still arrive until month-end.
        const isPastMonth = followUpYear < curY || (followUpYear === curY && m < curM);
        const isNoSale = !isPreStart && isPastMonth && !saleSet.has(`${followUpYear}-${m}`);
        const hasSent = !!row.pending_sales_sent;
        const hasFU1 = !!row.first_followup;
        const hasFU2 = !!row.second_followup;
        const hasMgmt1 = !!row.mgmt_followup;
        const hasMgmt2 = !!row.mgmt_followup_2;
        const hasReceived = !!row.received_date;
        const hasSettled = !!row.ac_settled;

        let status = "not-sent";
        if (hasReceived && hasSettled) status = "settled";
        else if (hasSent && (hasFU1 || hasFU2 || hasMgmt1 || hasMgmt2) && !hasReceived) status = "no-response";
        else if (hasMgmt1 || hasMgmt2) status = "mgmt-followup";
        else if (hasFU2) status = "followup-2";
        else if (hasFU1) status = "followup-1";
        else if (hasSent) status = "sent-pending";
        // else "not-sent" (default)

        rows.push({ partner: pc, month: m, key, row, status, isPreStart, isNoSale, hasSent, hasFU1, hasFU2, hasMgmt1, hasMgmt2, hasReceived, hasSettled });
      }
    });
    return rows;
  }, [followUpDisplayPartners, followUpData, followUpFirstOrders, followUpSaleMonths, followUpYear]);

  // Status filter stats
  const followUpStats = useMemo(() => {
    const stats = {
      total: 0,
      sent: 0,           // matches the "sent-pending" filter exactly (hasSent and no FU/Received yet)
      notSent: 0,
      followUp1: 0,
      followUp2: 0,
      mgmtFollowUp: 0,
      noResponse: 0,
      settled: 0,
      receivedData: 0,
      balanceOpen: 0,    // NEW: partners with NO received & NO settled data (anything still outstanding)
    };
    // When a month is selected, stats should reflect only that month's data
    const sourceRows = followUpMonthFilter !== "all"
      ? followUpRows.filter(r => r.month === Number(followUpMonthFilter))
      : followUpRows;
    sourceRows.forEach(r => {
      if (r.isPreStart || r.isNoSale) return; // pre-start & no-sale months don't count in follow-up stats
      stats.total++;
      if (r.hasSent) {
        // Match the "sent-pending" filter EXACTLY so card count == row count
        if (!r.hasFU1 && !r.hasFU2 && !r.hasMgmt1 && !r.hasMgmt2 && !r.hasReceived) {
          stats.sent++;
        }
      } else {
        stats.notSent++;
      }
      if (r.status === "followup-1") stats.followUp1++;
      if (r.status === "followup-2") stats.followUp2++;
      if (r.status === "mgmt-followup") stats.mgmtFollowUp++;
      if (r.status === "no-response") stats.noResponse++;
      if (r.status === "settled") stats.settled++;
      if (r.hasReceived) stats.receivedData++;
      // Balance / open: anything that hasn't closed yet (no received AND no settled)
      if (!r.hasReceived && !r.hasSettled) stats.balanceOpen++;
    });
    return stats;
  }, [followUpRows, followUpMonthFilter]);

  // Apply status + month filter
  const followUpFilteredRows = useMemo(() => {
    return followUpRows.filter(r => {
      if (followUpMonthFilter !== "all" && r.month !== Number(followUpMonthFilter)) return false;
      if (r.isPreStart || r.isNoSale) return followUpStatusFilter === "all"; // visible (greyed) only without status filter
      if (followUpStatusFilter === "all") return true;
      if (followUpStatusFilter === "not-sent") return !r.hasSent;
      if (followUpStatusFilter === "sent-pending") return r.hasSent && !r.hasFU1 && !r.hasFU2 && !r.hasMgmt1 && !r.hasMgmt2 && !r.hasReceived;
      if (followUpStatusFilter === "followup-1") return r.hasFU1;
      if (followUpStatusFilter === "followup-2") return r.hasFU2;
      if (followUpStatusFilter === "mgmt-followup") return r.hasMgmt1 || r.hasMgmt2;
      if (followUpStatusFilter === "no-response") return r.status === "no-response";
      if (followUpStatusFilter === "received-data") return r.hasReceived;
      if (followUpStatusFilter === "settled") return r.status === "settled";
      if (followUpStatusFilter === "in-progress") return r.hasSent && !r.hasSettled;
      if (followUpStatusFilter === "balance-open") return !r.hasReceived && !r.hasSettled;
      return true;
    });
  }, [followUpRows, followUpStatusFilter, followUpMonthFilter]);

  // Group filtered rows by partner for rendering
  const followUpGroupedFiltered = useMemo(() => {
    const grouped = {};
    followUpFilteredRows.forEach(r => {
      if (!grouped[r.partner]) grouped[r.partner] = [];
      grouped[r.partner].push(r);
    });
    return grouped;
  }, [followUpFilteredRows]);

  // Fetch Deletion Logs
  const fetchDeletionLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment-verification/deletion-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDeletionLogs(data.logs || []);
      }
    } catch {
      toast.error("Failed to fetch logs");
    } finally {
      setLoadingLogs(false);
    }
  };

  // ----- Uploaded Sheets (Partner Ledger original-file download) -----
  const fetchUploadedSheets = async (page = 1, partner = uploadedSheetsPartner) => {
    setLoadingUploadedSheets(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(uploadedSheetsLimit),
      });
      if (partner.trim()) params.append("partner_code", partner.trim());
      const res = await fetch(`${BACKEND_URL}/api/payment-verification/uploaded-sheets?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error("Failed to fetch uploaded sheets");
        return;
      }
      const data = await res.json();
      setUploadedSheets(data.uploads || []);
      setUploadedSheetsTotal(data.total || 0);
      setUploadedSheetsPage(page);
    } catch (e) {
      toast.error("Network error: " + e.message);
    } finally {
      setLoadingUploadedSheets(false);
    }
  };

  // ----- Raw Funds (accounting team upload — auto-verifies partner ledger) -----
  // Accepts optional overrides so callers can pass freshly-set values (e.g.
  // the sort toggle) without relying on React's async state batching.
  const fetchRawFunds = async (page = 1, overrides = {}) => {
    setRawFundsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(rawFundsPageSize),
      });
      if (rawFundsSearch.trim()) params.append("search", rawFundsSearch.trim());
      if (rawFundsClient.trim()) params.append("client_code", rawFundsClient.trim());
      if (rawFundsSource.trim()) params.append("source", rawFundsSource.trim());
      if (rawFundsDateFrom) params.append("date_from", rawFundsDateFrom);
      if (rawFundsDateTo) params.append("date_to", rawFundsDateTo);
      const onlySuspense = overrides.only_suspense !== undefined ? overrides.only_suspense : rawFundsOnlySuspense;
      if (onlySuspense) params.append("only_suspense", "true");
      const sortKey = overrides.sort_key !== undefined ? overrides.sort_key : rawFundsSortKey;
      const sortDir = overrides.sort_dir !== undefined ? overrides.sort_dir : rawFundsSortDir;
      if (sortKey) params.append("sort_key", sortKey);
      if (sortDir) params.append("sort_dir", sortDir);
      const [rowsRes, upRes, sumRes, cliRes, srcRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/payment-verification/raw-funds?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/uploads`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/clients`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/sources`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (!rowsRes.ok) { toast.error("Failed to fetch raw funds rows"); return; }
      const d = await rowsRes.json();
      setRawFundsItems(d.items || []);
      setRawFundsTotal(d.total || 0);
      setRawFundsSumInr(d.sum_inr || 0);
      setRawFundsPage(d.page || page);
      setRawFundsCoverage({
        with_customer: d.with_customer || 0,
        with_narration: d.with_narration || 0,
        with_country: d.with_country || 0,
      });
      setRawFundsSumUsd2(d.sum_usd2_inr || 0);
      // Drop any selected ids that aren't on this page anymore.
      setRawFundsSelected((prev) => {
        const ids = new Set((d.items || []).map((i) => i.id));
        const next = new Set();
        prev.forEach((x) => { if (ids.has(x)) next.add(x); });
        return next;
      });
      if (upRes.ok) {
        const u = await upRes.json();
        setRawFundsUploads(u.items || []);
      }
      if (sumRes.ok) setRawFundsSummary(await sumRes.json());
      if (cliRes.ok) {
        const c = await cliRes.json();
        setRawFundsClientOptions(c.items || []);
      }
      if (srcRes.ok) {
        const s = await srcRes.json();
        setRawSourceOptions(s.sources || []);
      }
    } catch (e) {
      toast.error("Network error: " + e.message);
    } finally {
      setRawFundsLoading(false);
    }
  };

  // --- Raw Funds: pick file → preview (totals + duplicates) → verify → upload ---
  const handleRawFundsFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (rawFundsUploadInputRef.current) rawFundsUploadInputRef.current.value = "";
    if (!file) return;
    setRawFundsPreviewing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/preview`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) { toast.error(typeof data.detail === "string" ? data.detail : "Preview failed"); return; }
      setRawFundsPendingFile(file);
      setRawFundsPreview(data);
      setRawFundsUploadMode(data.duplicate_rows > 0 ? "skip" : "add");
      setRawFundsVerified(false);
      setRawFundsPreviewOpen(true);
    } catch (err) {
      toast.error("Network error: " + err.message);
    } finally {
      setRawFundsPreviewing(false);
    }
  };

  const confirmRawFundsUpload = async () => {
    if (!rawFundsPendingFile || !rawFundsVerified) return;
    setRawFundsUploadingSheet(true);
    try {
      const fd = new FormData();
      fd.append("file", rawFundsPendingFile);
      fd.append("mode", rawFundsUploadMode);
      if (rawFundsPreview?.file_duplicate) fd.append("force", "true");
      const res = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = typeof data.detail === "string" ? data.detail : data.detail?.message || "Upload failed";
        toast.error(msg);
        return;
      }
      const bits = [`${data.row_count} rows imported`];
      if (data.skipped_duplicates) bits.push(`${data.skipped_duplicates} duplicates skipped`);
      if (data.replaced_rows) bits.push(`${data.replaced_rows} old rows replaced`);
      toast.success(bits.join(" · "));
      setRawFundsPreviewOpen(false);
      setRawFundsPendingFile(null);
      setRawFundsPreview(null);
      fetchRawFunds(1);
    } catch (err) {
      toast.error("Network error: " + err.message);
    } finally {
      setRawFundsUploadingSheet(false);
    }
  };

  // --- Raw Funds: password-gated inline field edit ---
  const rawFieldInitial = (row, field) => {
    if (field === "date") return row.date_ddmmyy || "";
    const v = row[field];
    return v === null || v === undefined ? "" : String(v);
  };

  const commitRawFieldEdit = async (pwOverride) => {
    const edit = rawFieldEdit;
    if (!edit) return;
    const password = pwOverride ?? rawEditPassword;
    if (!password) { setRawPwInput(""); setRawPwDialogOpen(true); return; }
    setRawFieldSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/rows/${edit.rowId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password, fields: { [edit.field]: edit.value } }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403) {
          setRawEditPassword("");
          setRawPwInput("");
          setRawPwDialogOpen(true);
          toast.error("Invalid edit password");
          return;
        }
        toast.error(typeof data.detail === "string" ? data.detail : "Update failed");
        return;
      }
      setRawEditPassword(password);
      setRawPwDialogOpen(false);
      if (data.row) setRawFundsItems(prev => prev.map(r => (r.id === edit.rowId ? data.row : r)));
      setRawFieldEdit(null);
      const hadChanges = Object.keys(data.changed || {}).length > 0;
      toast.success(hadChanges ? "Row updated" : "No changes");
      // Amount/date edits shift the totals — refresh summary + filtered totals
      if (hadChanges && (data.changed.inward_inr || data.changed.usd2_inr || data.changed.date)) {
        fetchRawFunds(rawFundsPage);
      }
    } catch (err) {
      toast.error("Network error: " + err.message);
    } finally {
      setRawFieldSaving(false);
    }
  };

  const scanDuplicatePayments = async () => {
    setDupScanOpen(true);
    setDupScanLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/duplicates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (!res.ok) { toast.error("Scan failed"); return; }
      setDupScan(d);
    } catch (e) {
      toast.error("Network error: " + e.message);
    } finally {
      setDupScanLoading(false);
    }
  };

  const mergeDuplicateGroup = async (group) => {
    const pw = dupPw || rawEditPassword;
    if (!pw) { toast.error("Enter your login password (or admin password) in the box above first"); return; }
    setDupMerging(group.keep_id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/duplicates/merge`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ keep_id: group.keep_id, remove_ids: group.remove_ids, password: pw }),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Merge failed"); return; }
      setRawEditPassword(pw);
      toast.success(`Merged — kept 1 copy, removed ${d.removed} duplicate${d.removed > 1 ? "s" : ""} (archived to trash)`);
      setDupScan(prev => prev ? {
        ...prev,
        groups: prev.groups.filter(g => g.keep_id !== group.keep_id),
        group_count: prev.group_count - 1,
        extra_rows: prev.extra_rows - group.remove_ids.length,
        extra_inr: prev.extra_inr - group.inward_inr * group.remove_ids.length,
      } : prev);
      fetchRawFunds(rawFundsPage);
    } catch (e) {
      toast.error("Network error: " + e.message);
    } finally {
      setDupMerging("");
    }
  };

  const rawEditableCell = (row, field, display, tdCls = "p-2") => {
    const isEditing = rawFieldEdit?.rowId === row.id && rawFieldEdit?.field === field;
    return (
      <td
        className={`${tdCls} cursor-pointer hover:bg-indigo-50/50`}
        title="Double-click to edit (password protected)"
        onDoubleClick={() => setRawFieldEdit({ rowId: row.id, field, value: rawFieldInitial(row, field) })}
        data-testid={`raw-funds-edit-${field}-${row.id}`}
      >
        {isEditing ? (
          <Input
            autoFocus
            value={rawFieldEdit.value}
            disabled={rawFieldSaving}
            onChange={(e) => setRawFieldEdit(p => ({ ...p, value: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRawFieldEdit();
              if (e.key === "Escape") setRawFieldEdit(null);
            }}
            className="h-7 text-xs p-1 min-w-[80px]"
            data-testid={`raw-funds-edit-input-${field}-${row.id}`}
          />
        ) : display}
      </td>
    );
  };

  const downloadRawFundsUpload = async (upload_id, filename) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/uploads/${upload_id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error("Original file no longer available"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "raw_funds.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed: " + e.message);
    }
  };

  // Password-gated delete of a whole upload (archives rows to trash server-side)
  const [rawDelUpload, setRawDelUpload] = useState(null); // {upload_id, filename, row_count}
  const [rawDelPw, setRawDelPw] = useState("");
  const [rawDelBusy, setRawDelBusy] = useState(false);
  const deleteRawFundsUpload = (upload_id, filename, row_count) => {
    setRawDelPw("");
    setRawDelUpload({ upload_id, filename, row_count });
  };
  const confirmDeleteRawFundsUpload = async () => {
    if (!rawDelUpload || !rawDelPw) return;
    setRawDelBusy(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/payment-verification/raw-funds/uploads/${rawDelUpload.upload_id}?password=${encodeURIComponent(rawDelPw)}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      const d = await res.json();
      if (!res.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Delete failed"); return; }
      toast.success(`Deleted ${d.rows_deleted} rows from "${rawDelUpload.filename}" (archived to trash)`);
      setRawDelUpload(null);
      fetchRawFunds(1);
    } catch (e) {
      toast.error("Delete failed: " + e.message);
    } finally {
      setRawDelBusy(false);
    }
  };

  const downloadRawFundsTemplate = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error("Template download failed"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "raw_funds_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed: " + e.message);
    }
  };

  // Toggle sort: same key flips dir, new key resets to desc.
  // We pass the new sort values directly to fetchRawFunds so the request
  // uses the just-clicked values immediately — React's async state batching
  // would otherwise let fetchRawFunds read the OLD sortKey/sortDir from
  // closure, leaving the table sorted only within the current page.
  const toggleRawFundsSort = (key) => {
    let newDir;
    let newKey = key;
    if (rawFundsSortKey === key) {
      newDir = rawFundsSortDir === "asc" ? "desc" : "asc";
      setRawFundsSortDir(newDir);
    } else {
      newDir = "desc";
      setRawFundsSortKey(key);
      setRawFundsSortDir(newDir);
    }
    fetchRawFunds(1, { sort_key: newKey, sort_dir: newDir });
  };

  const handleRawFundsBulkDelete = async () => {
    if (rawFundsSelected.size === 0) {
      toast.error("Select at least one row");
      return;
    }
    if (!rawFundsBulkPassword) {
      toast.error("Enter admin password");
      return;
    }
    setRawFundsBulkDeleting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/bulk-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ids: Array.from(rawFundsSelected),
          password: rawFundsBulkPassword,
        }),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || "Delete failed"); return; }
      toast.success(`Deleted ${d.deleted} row(s)`);
      setRawFundsBulkDialogOpen(false);
      setRawFundsBulkPassword("");
      setRawFundsSelected(new Set());
      fetchRawFunds(rawFundsPage);
    } catch (e) {
      toast.error("Delete failed: " + e.message);
    } finally {
      setRawFundsBulkDeleting(false);
    }
  };

  // Lazy-load canonical ledger codes (only once, on first inline edit).
  const ensureCanonicalCodesLoaded = async () => {
    if (rawCanonicalCodes.length > 0) return;
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/payment-verification/raw-funds/canonical-codes`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        const d = await res.json();
        setRawCanonicalCodes(d.codes || []);
      }
    } catch {/* silent */}
  };

  const openRawClientCodeEditor = async (rowId) => {
    await ensureCanonicalCodesLoaded();
    setRawEditingSearch("");
    setRawEditingRowId(rowId);
  };

  const openClientSummary = async () => {
    setRawClientSummaryOpen(true);
    setRawClientSummaryLoading(true);
    try {
      const params = new URLSearchParams();
      const df = rawClientSummaryFrom || rawFundsDateFrom;
      const dt = rawClientSummaryTo || rawFundsDateTo;
      if (df) params.append("date_from", df);
      if (dt) params.append("date_to", dt);
      if (rawFundsSource.trim()) params.append("source", rawFundsSource.trim());
      const res = await fetch(
        `${BACKEND_URL}/api/payment-verification/raw-funds/client-summary?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || "Failed to load client summary"); return; }
      setRawClientSummaryData(d);
    } catch (e) {
      toast.error("Network error: " + e.message);
    } finally {
      setRawClientSummaryLoading(false);
    }
  };

  const openSourceSummary = async () => {
    setRawSourceSummaryOpen(true);
    setRawSourceSummaryLoading(true);
    try {
      const params = new URLSearchParams();
      const df = rawSourceSummaryFrom || rawFundsDateFrom;
      const dt = rawSourceSummaryTo || rawFundsDateTo;
      if (df) params.append("date_from", df);
      if (dt) params.append("date_to", dt);
      if (rawFundsClient.trim()) params.append("client_code", rawFundsClient.trim());
      const res = await fetch(
        `${BACKEND_URL}/api/payment-verification/raw-funds/source-summary?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || "Failed to load source summary"); return; }
      setRawSourceSummaryData(d);
    } catch (e) {
      toast.error("Network error: " + e.message);
    } finally {
      setRawSourceSummaryLoading(false);
    }
  };

  const saveRawClientCode = async (rowId, newCode) => {
    setRawEditingSaving(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/payment-verification/raw-funds/rows/${encodeURIComponent(rowId)}/client-code`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ client_code: newCode }),
        },
      );
      const d = await res.json();
      if (!res.ok) {
        toast.error(d.detail || "Failed to assign client code");
        return;
      }
      // Update row in-place so the table reflects immediately without a full refetch.
      setRawFundsItems((prev) =>
        prev.map((r) => (r.id === rowId ? { ...r, client_code: newCode, client_code_manual: true } : r)),
      );
      toast.success(newCode ? `Assigned → ${newCode}` : "Cleared (back to suspense)");
      setRawEditingRowId(null);
    } catch (e) {
      toast.error("Save failed: " + e.message);
    } finally {
      setRawEditingSaving(false);
    }
  };



  const downloadOriginalSheet = async (upload) => {
    setDownloadingSheetId(upload.upload_id);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/payment-verification/uploaded-sheets/${upload.upload_id}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const t = await res.text();
        let msg = t;
        try { msg = JSON.parse(t).detail || t; } catch {}
        toast.error(msg || "Download failed");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = upload.filename || `${upload.upload_id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Original sheet downloaded");
    } catch (e) {
      toast.error("Download failed: " + e.message);
    } finally {
      setDownloadingSheetId(null);
    }
  };

  // Clear Deletion Logs (Admin only)
  const handleClearLogs = async () => {
    if (!clearLogsPassword) {
      toast.error("Password is required");
      return;
    }
    setClearingLogs(true);
    try {
      const logIds = selectedLogIds.size > 0 ? Array.from(selectedLogIds) : [];
      const res = await fetch(`${BACKEND_URL}/api/payment-verification/deletion-logs/clear`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password: clearLogsPassword, log_ids: logIds })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setClearLogsDialog(false);
        setClearLogsPassword("");
        setSelectedLogIds(new Set());
        fetchDeletionLogs();
      } else {
        toast.error(data.detail || "Failed to clear logs");
      }
    } catch {
      toast.error("Error clearing logs");
    } finally {
      setClearingLogs(false);
    }
  };

  // Toggle partner expansion
  const togglePartner = (code) => {
    setExpandedPartners(prev => {
      const newSet = new Set(prev);
      newSet.has(code) ? newSet.delete(code) : newSet.add(code);
      return newSet;
    });
  };

  // Toggle client expansion
  const toggleClient = (code) => {
    setExpandedClients(prev => {
      const newSet = new Set(prev);
      newSet.has(code) ? newSet.delete(code) : newSet.add(code);
      return newSet;
    });
  };

  // Handle sort for reports
  const handleReportSort = (key) => {
    setReportSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ columnKey }) => {
    if (reportSortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return reportSortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-emerald-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-emerald-600" />;
  };

  // Filter and sort partners
  const getFilteredPartners = () => {
    if (!pendingSalesData?.partners) return [];
    let filtered = pendingSalesData.partners;
    if (reportSearchTerm) {
      const term = reportSearchTerm.toLowerCase();
      filtered = filtered.filter(p => p.client_code?.toLowerCase().includes(term));
    }
    return [...filtered].sort((a, b) => {
      const aVal = a[reportSortConfig.key] || 0;
      const bVal = b[reportSortConfig.key] || 0;
      if (reportSortConfig.key === 'client_code') {
        return reportSortConfig.direction === 'asc' 
          ? (aVal || '').localeCompare(bVal || '')
          : (bVal || '').localeCompare(aVal || '');
      }
      return reportSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  // Filter and sort clients
  const getFilteredClients = () => {
    if (!autoGenerateData?.clients) return [];
    let filtered = autoGenerateData.clients;
    if (reportSearchTerm) {
      const term = reportSearchTerm.toLowerCase();
      filtered = filtered.filter(c => c.client_code?.toLowerCase().includes(term));
    }
    return [...filtered].sort((a, b) => {
      const aVal = a[reportSortConfig.key] || 0;
      const bVal = b[reportSortConfig.key] || 0;
      if (reportSortConfig.key === 'client_code') {
        return reportSortConfig.direction === 'asc' 
          ? (aVal || '').localeCompare(bVal || '')
          : (bVal || '').localeCompare(aVal || '');
      }
      return reportSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  // Helper: sort orders by date ascending using parseDateComponents
  const sortOrdersByDateAsc = (orders) => {
    return [...orders].sort((a, b) => {
      const da = parseDateComponents(a.order_date);
      const db = parseDateComponents(b.order_date);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      const dateA = new Date(da.year, da.month - 1, da.day);
      const dateB = new Date(db.year, db.month - 1, db.day);
      return dateA - dateB;
    });
  };

  // Export to CSV - Pending Sales
  const exportPendingSalesCSV = (partnerCode = null) => {
    if (!pendingSalesData?.partners) return;
    const partners = partnerCode 
      ? getFilteredPartners().filter(p => p.client_code === partnerCode)
      : getFilteredPartners();
    
    const rows = [];
    partners.forEach(partner => {
      rows.push(["Tracking", "Order Date", "Product", "Qty", "Service", "Country"]);
      const sorted = sortOrdersByDateAsc(partner.orders || []);
      sorted.forEach(o => rows.push([o.tracking, formatDateDashDisplay(o.order_date), o.product, o.qty, o.service, o.country]));
      rows.push([]);
    });
    
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = partnerCode ? `pending_sales_${partnerCode}_${format(new Date(), 'yyyy-MM-dd')}.csv` : `pending_sales_report_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported!");
  };

  // Open an interactive report viewer in a new browser tab.
  //   reportType: "pending-sales" | "negative-gp" | "no-invoice"
  const openClientReport = (reportType, partnerCode) => {
    if (!partnerCode) {
      toast.error("Partner code missing");
      return;
    }
    const params = new URLSearchParams();
    if (reportDateFrom) params.append("date_from", reportDateFrom);
    if (reportDateTo) params.append("date_to", reportDateTo);
    const qs = params.toString() ? `?${params}` : "";
    const url = `/reports/client/${reportType}/${encodeURIComponent(partnerCode)}${qs}`;
    const win = window.open(url, "_blank", "noopener");
    if (!win) toast.error("Pop-up blocked — please allow pop-ups for this site");
  };

  // Direct CSV download for account-view reports (negative-gp / no-invoice)
  // without opening the viewer. Fetches partner-ledger-account and filters locally.
  const downloadAccountReportCSV = async (reportType, partnerCode) => {
    if (!partnerCode) return;
    try {
      toast.info(`Fetching ${reportType.replace("-", " ")} for ${partnerCode}…`);
      const res = await fetch(
        `${BACKEND_URL}/api/partner-ledger-account/${encodeURIComponent(partnerCode)}?view_mode=ledger`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const flag = reportType === "negative-gp" ? "has_negative_gp" : "has_no_invoice";
      const filtered = (data?.ledger_entries || []).filter((e) => e[flag]);
      if (filtered.length === 0) {
        toast.success(`No ${reportType.replace("-", " ")} entries for ${partnerCode}`);
        return;
      }
      const esc = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      let header, rows;
      if (reportType === "negative-gp") {
        header = ["Tracking", "Date", "Customer", "Purchase (₹)", "Sale (₹)", "GP (₹)", "GP %"];
        rows = filtered.map((e) => {
          const purchase = Number(e?.parcel_summary?.total_debit || 0);
          const sale = Number(e?.sale_amount || 0);
          const gp = Number(e?.gp || 0);
          const gpPct = purchase > 0 ? ((gp / purchase) * 100).toFixed(2) : "0.00";
          return [e.tracking, formatDateDashDisplay(e.date), e.customer, purchase.toFixed(2), sale.toFixed(2), gp.toFixed(2), gpPct];
        });
      } else {
        header = ["Tracking", "Date", "Customer", "Purchase (₹)", "Payment Mode"];
        rows = filtered.map((e) => {
          const purchase = Number(e?.parcel_summary?.total_debit || 0);
          const paymentMode = e?.entries?.find((x) => x.credit > 0)?.description || e?.payment_mode || "";
          return [e.tracking, formatDateDashDisplay(e.date), e.customer, purchase.toFixed(2), paymentMode];
        });
      }
      const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportType}_${partnerCode}_${format(new Date(), "yyyy-MM-dd")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filtered.length} ${reportType.replace("-", " ")} rows`);
    } catch (err) {
      toast.error(`Failed to download: ${err.message}`);
    }
  };

  // Export Partner Ledger Sales Template (styled Excel from backend)
  // When `includeAll=true`, the backend returns ALL parcels for the partner
  // (not just unpaid) and shades already-submitted rows pink with pre-filled
  // amounts — so the partner sees the complete order list.
  const exportPartnerLedgerTemplate = async (partnerCode, { includeAll = false } = {}) => {
    try {
      toast.info("Generating ledger template...");
      const params = new URLSearchParams();
      if (reportDateFrom) params.append('date_from', reportDateFrom);
      if (reportDateTo) params.append('date_to', reportDateTo);
      if (includeAll) params.append('include_all', 'true');
      const qs = params.toString() ? `?${params}` : '';
      const res = await fetch(`${BACKEND_URL}/api/reports/pending-sales/ledger-template/${partnerCode}${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to generate template");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const tag = includeAll ? "ALL" : "Pending";
      a.download = `Partner_Ledger_Sales_Template_${partnerCode}_${tag}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Template exported (${includeAll ? "all parcels — pink rows already submitted" : "pending only"})!`);
    } catch (err) {
      toast.error("Failed to export ledger template");
    }
  };

  // Export to CSV - Auto Generate
  const exportAutoGenerateCSV = (clientCode = null) => {
    if (!autoGenerateData?.clients) return;
    const clientsList = clientCode 
      ? getFilteredClients().filter(c => c.client_code === clientCode)
      : getFilteredClients();
    
    const rows = [];
    clientsList.forEach(client => {
      rows.push([`Client: ${client.client_code}`, `Orders: ${client.total_orders}`, `Product Issues: ${client.zero_medicine_cost}`, `Shipping Issues: ${client.zero_shipping_cost}`]);
      rows.push(["Tracking", "Order Date", "Product", "Qty", "Issue"]);
      client.orders?.forEach(o => rows.push([o.tracking, formatDateDashDisplay(o.order_date), o.product, o.qty, o.issue_text]));
      rows.push([]);
    });
    
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = clientCode ? `auto_generate_${clientCode}_${format(new Date(), 'yyyy-MM-dd')}.csv` : `auto_generate_report_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported!");
  };

  // View Auto Generate orders for one client in a new tab (HTML preview)
  // Useful for finance to quickly verify all orders without downloading first.
  const viewAutoGenerateInBrowser = (clientCode = null) => {
    if (!autoGenerateData?.clients) return;
    const clientsList = clientCode
      ? getFilteredClients().filter((c) => c.client_code === clientCode)
      : getFilteredClients();
    if (clientsList.length === 0) {
      toast.error("No client matches");
      return;
    }
    const esc = (s) =>
      String(s == null ? "" : s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    const dateLabel = (reportDateFrom && reportDateTo)
      ? `${formatDateDashDisplay(reportDateFrom)} → ${formatDateDashDisplay(reportDateTo)}`
      : "(no date filter)";
    const generatedAt = format(new Date(), "dd MMM yyyy, HH:mm");

    const totalOrders = clientsList.reduce((s, c) => s + (c.total_orders || 0), 0);
    const totalProductIssues = clientsList.reduce((s, c) => s + (c.zero_medicine_cost || 0), 0);
    const totalShippingIssues = clientsList.reduce((s, c) => s + (c.zero_shipping_cost || 0), 0);

    const clientSections = clientsList.map((client) => {
      const orders = client.orders || [];
      const rows = orders.map((o, i) => `
        <tr class="${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}">
          <td class="px-3 py-2 font-mono text-xs text-blue-700">${esc(o.tracking || '—')}</td>
          <td class="px-3 py-2 text-xs">${esc(formatDateDashDisplay(o.order_date) || '—')}</td>
          <td class="px-3 py-2 text-xs">${esc(o.product || '—')}</td>
          <td class="px-3 py-2 text-xs">${esc(o.qty || '—')}</td>
          <td class="px-3 py-2 text-xs">${esc(o.country || '—')}</td>
          <td class="px-3 py-2 text-xs">${esc(o.service || '—')}</td>
          <td class="px-3 py-2 text-xs text-red-600">${esc(o.issue_text || '—')}</td>
        </tr>`).join("");
      return `
        <section class="mb-8 bg-white rounded-lg shadow-sm border border-slate-200">
          <header class="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-slate-200 rounded-t-lg">
            <div class="flex items-center justify-between flex-wrap gap-2">
              <h2 class="text-lg font-bold text-slate-800">${esc(client.client_code)}</h2>
              <div class="text-xs text-slate-600 flex gap-4 flex-wrap">
                <span>Orders: <b class="text-slate-900">${client.total_orders || 0}</b></span>
                <span>Product Issues: <b class="text-amber-600">${client.zero_medicine_cost || 0}</b></span>
                <span>Shipping Issues: <b class="text-rose-600">${client.zero_shipping_cost || 0}</b></span>
              </div>
            </div>
          </header>
          ${orders.length === 0
            ? `<div class="px-4 py-6 text-center text-slate-400 text-sm">No orders listed</div>`
            : `<div class="overflow-x-auto">
                 <table class="w-full text-sm">
                   <thead class="bg-slate-100">
                     <tr>
                       <th class="px-3 py-2 text-left font-semibold text-xs uppercase tracking-wider text-slate-600">Tracking</th>
                       <th class="px-3 py-2 text-left font-semibold text-xs uppercase tracking-wider text-slate-600">Date</th>
                       <th class="px-3 py-2 text-left font-semibold text-xs uppercase tracking-wider text-slate-600">Product</th>
                       <th class="px-3 py-2 text-left font-semibold text-xs uppercase tracking-wider text-slate-600">Qty</th>
                       <th class="px-3 py-2 text-left font-semibold text-xs uppercase tracking-wider text-slate-600">Country</th>
                       <th class="px-3 py-2 text-left font-semibold text-xs uppercase tracking-wider text-slate-600">Service</th>
                       <th class="px-3 py-2 text-left font-semibold text-xs uppercase tracking-wider text-slate-600">Issue</th>
                     </tr>
                   </thead>
                   <tbody>${rows}</tbody>
                 </table>
               </div>`}
        </section>`;
    }).join("");

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Auto Generate Preview — ${esc(clientCode || 'All Clients')} — EximFlow</title>
<script src="https://cdn.tailwindcss.com"></script>
<style>
  body { font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif; }
  @media print { .no-print { display: none !important; } body { padding: 0 !important; } }
</style>
</head>
<body class="bg-slate-100 text-slate-900 p-6">
  <div class="max-w-7xl mx-auto">
    <div class="bg-white rounded-lg shadow-sm border border-slate-200 mb-6">
      <div class="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 class="text-xl font-bold text-slate-800">Auto Generate Preview</h1>
          <p class="text-xs text-slate-500 mt-0.5">${clientCode ? `Client: <b class="text-slate-700">${esc(clientCode)}</b>` : `<b>${clientsList.length}</b> clients`} · Period: ${esc(dateLabel)} · Generated: ${esc(generatedAt)}</p>
        </div>
        <div class="flex gap-2 no-print">
          <button onclick="window.print()" class="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-semibold hover:bg-blue-700">🖨️ Print</button>
          <button onclick="window.close()" class="px-3 py-1.5 bg-slate-200 text-slate-700 rounded text-xs font-semibold hover:bg-slate-300">Close</button>
        </div>
      </div>
      <div class="px-5 py-3 flex flex-wrap gap-6 text-sm">
        <div><span class="text-slate-500">Total Orders:</span> <b class="text-slate-900">${totalOrders}</b></div>
        <div><span class="text-slate-500">Product Issues:</span> <b class="text-amber-600">${totalProductIssues}</b></div>
        <div><span class="text-slate-500">Shipping Issues:</span> <b class="text-rose-600">${totalShippingIssues}</b></div>
      </div>
    </div>
    ${clientSections}
  </div>
</body>
</html>`;
    const win = window.open("", "_blank");
    if (!win) {
      toast.error("Pop-up blocked — please allow pop-ups for this site");
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  // ---- Suspicious ledger cleanup (admin-only) ----
  const openCleanupModal = (code, orderCount, action = "delete") => {
    setCleanupModal({
      open: true,
      code,
      orderCount: orderCount || 0,
      action,
      newCode: "",
      password: "",
    });
  };

  const submitCleanup = async () => {
    if (!cleanupModal.password) {
      toast.error("Admin password is required");
      return;
    }
    if (cleanupModal.action === "rename" && !cleanupModal.newCode.trim()) {
      toast.error("Enter the new client code to rename to");
      return;
    }
    setCleanupLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/cleanup-suspicious-ledger`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          client_code: cleanupModal.code,
          action: cleanupModal.action,
          new_code: cleanupModal.newCode.trim() || undefined,
          admin_password: cleanupModal.password,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Cleanup failed");
        return;
      }
      if (cleanupModal.action === "delete") {
        toast.success(
          `Deleted '${data.client_code}'. ${data.orphan_shipment_orders} shipment order(s) remain unassigned.`
        );
      } else {
        toast.success(
          `Renamed '${data.old_code}' → '${data.new_code}'. ${data.shipment_orders_updated} order(s) updated.`
        );
      }
      setCleanupModal({ open: false, code: "", orderCount: 0, action: "delete", newCode: "", password: "" });
      // Refresh the report so the cleaned code disappears from the list
      fetchAutoGenerateReport();
      // Refresh history if it's open
      if (historyOpen) fetchCleanupHistory();
    } catch (err) {
      toast.error("Network error: " + err.message);
    } finally {
      setCleanupLoading(false);
    }
  };

  const fetchCleanupHistory = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/cleanup-suspicious-ledger/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) setCleanupHistory(data.history || []);
      else toast.error(data.detail || "Failed to load history");
    } catch (err) {
      toast.error("Network error: " + err.message);
    }
  };

  const submitRestore = async () => {
    if (!restorePromptId) return;
    if (!restorePassword) {
      toast.error("Admin password required");
      return;
    }
    setRestoreLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/cleanup-suspicious-ledger/restore`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ log_id: restorePromptId, admin_password: restorePassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Restore failed");
        return;
      }
      const s = data.restore_summary || {};
      toast.success(
        data.cleanup_action === "delete"
          ? `Ledger restored (${s.ledgers_restored || 0} row(s))`
          : `Reverted: ${s.shipment_orders_reverted || 0} order(s) + ${s.ledgers_restored || 0} ledger row(s)`
      );
      setRestorePromptId(null);
      setRestorePassword("");
      fetchCleanupHistory();
      fetchAutoGenerateReport();
    } catch (err) {
      toast.error("Network error: " + err.message);
    } finally {
      setRestoreLoading(false);
    }
  };

  // Export to PDF - Pending Sales
  const exportPendingSalesPDF = (partnerCode = null) => {
    if (!pendingSalesData?.partners) return;
    const partners = partnerCode 
      ? getFilteredPartners().filter(p => p.client_code === partnerCode)
      : getFilteredPartners();
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Pending Sales Report", pageWidth / 2, 20, { align: "center" });
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${format(new Date(), 'dd MMM yyyy, HH:mm')}`, pageWidth / 2, 28, { align: "center" });
    
    let yPos = 45;
    partners.forEach(partner => {
      if (yPos > 250) { doc.addPage(); yPos = 20; }
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setFillColor(255, 237, 213);
      doc.rect(14, yPos - 5, pageWidth - 28, 8, 'F');
      doc.text(`Partner: ${partner.client_code} | Orders: ${partner.total_orders} | Value: ₹${partner.total_value?.toLocaleString()}`, 16, yPos);
      yPos += 10;
      
      if (partner.orders?.length > 0) {
        const tableData = partner.orders.slice(0, 30).map(o => [o.tracking || '-', formatDateDashDisplay(o.order_date) || '-', (o.product || '-').substring(0, 25), o.qty || '-', o.service || '-', o.country || '-']);
        doc.autoTable({
          startY: yPos, head: [['Tracking', 'Date', 'Product', 'Qty', 'Service', 'Country']], body: tableData,
          theme: 'striped', headStyles: { fillColor: [217, 119, 6], fontSize: 8 }, bodyStyles: { fontSize: 8 },
          margin: { left: 14, right: 14 }
        });
        yPos = doc.lastAutoTable.finalY + 15;
      }
    });
    
    doc.save(partnerCode ? `pending_sales_${partnerCode}_${format(new Date(), 'yyyy-MM-dd')}.pdf` : `pending_sales_report_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success("PDF exported!");
  };

  // Export to PDF - Auto Generate
  const exportAutoGeneratePDF = (clientCode = null) => {
    if (!autoGenerateData?.clients) return;
    const clientsList = clientCode 
      ? getFilteredClients().filter(c => c.client_code === clientCode)
      : getFilteredClients();
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Pending Sale Auto Generate Report", pageWidth / 2, 20, { align: "center" });
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${format(new Date(), 'dd MMM yyyy, HH:mm')}`, pageWidth / 2, 28, { align: "center" });
    
    let yPos = 45;
    clientsList.forEach(client => {
      if (yPos > 250) { doc.addPage(); yPos = 20; }
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setFillColor(237, 233, 254);
      doc.rect(14, yPos - 5, pageWidth - 28, 8, 'F');
      doc.text(`Client: ${client.client_code} | Orders: ${client.total_orders} | Product: ${client.zero_medicine_cost} | Shipping: ${client.zero_shipping_cost}`, 16, yPos);
      yPos += 10;
      
      if (client.orders?.length > 0) {
        const tableData = client.orders.slice(0, 30).map(o => [o.tracking || '-', formatDateDashDisplay(o.order_date) || '-', (o.product || '-').substring(0, 30), o.qty || '-', (o.issue_text || '-').substring(0, 25)]);
        doc.autoTable({
          startY: yPos, head: [['Tracking', 'Date', 'Product', 'Qty', 'Issue']], body: tableData,
          theme: 'striped', headStyles: { fillColor: [147, 51, 234], fontSize: 8 }, bodyStyles: { fontSize: 8 },
          margin: { left: 14, right: 14 }
        });
        yPos = doc.lastAutoTable.finalY + 15;
      }
    });
    
    doc.save(clientCode ? `auto_generate_${clientCode}_${format(new Date(), 'yyyy-MM-dd')}.pdf` : `auto_generate_report_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success("PDF exported!");
  };

  // Print report
  const handlePrintReport = () => window.print();

  // Apply date filter preset
  const applyDateFilter = (preset) => {
    const today = new Date();
    let from = "", to = format(today, "yyyy-MM-dd");
    switch (preset) {
      case "30days": from = format(new Date(today.setDate(today.getDate() - 30)), "yyyy-MM-dd"); break;
      case "thisMonth": from = format(new Date(today.getFullYear(), today.getMonth(), 1), "yyyy-MM-dd"); break;
      case "all": from = ""; to = ""; break;
    }
    setReportDateFrom(from);
    setReportDateTo(to);
    // Pass dates explicitly since state hasn't updated yet
    if (activeTab === "pending-sales") fetchPendingSalesReport(from, to);
    else if (activeTab === "auto-generate") fetchAutoGenerateReport(from, to);
  };

  // Load reports when tab changes
  useEffect(() => {
    if (activeTab === "pending-sales") fetchPendingSalesReport();
    if (activeTab === "auto-generate") fetchAutoGenerateReport();
    if (activeTab === "follow-up") fetchFollowUpData();
    if (activeTab === "logs") fetchDeletionLogs();
    if (activeTab === "raw-funds") fetchRawFunds(1);
    // Clear selections when switching tabs
    setSelectedPayments([]);
    setVerifiedSelectedPayments([]);
  }, [activeTab]);

  // Reload follow-up when year or selected partners change
  useEffect(() => {
    if (activeTab === "follow-up") fetchFollowUpData();
  }, [followUpYear, followUpSelectedPartners]);

  // Reset pending page when filters change
  useEffect(() => { setPendingPage(1); }, [statusFilter, clientFilter, searchQuery, dateFrom, dateTo, tableSortConfig, reshipOnly, lowConfidenceDatesOnly, showZeroOnly, modeFilter]);
  // Reset verified page when filters change
  useEffect(() => { setVerifiedPage(1); }, [verifiedClientFilter, verifiedSearchQuery, verifiedDateFrom, verifiedDateTo, verifiedTableSortConfig]);

  // Open verification dialog
  const openVerifyDialog = (payment) => {
    setSelectedPayment(payment);
    
    // Pre-fill form based on payment status
    const isPreVerified = payment.verification_status === "verified_at_upload";
    const defaultStatus = payment.verification_status === "pending" || isPreVerified
      ? (payment.adjustment_needed ? "verified_adjusted" : "verified_full")
      : payment.verification_status;
    
    setVerificationForm({
      verification_status: defaultStatus,
      actual_amount:
        payment.actual_amount?.toString() ||
        payment.claimed_amount?.toString() ||
        "",
      received_date: payment.received_date || "",
      bank_reference: payment.bank_reference || "",
      difference_reason: payment.difference_reason || "",
      bank_charges: payment.adjustment_breakdown?.bank_charges?.toString() || "",
      forex_charges: payment.adjustment_breakdown?.forex_charges?.toString() || "",
      error_amount: payment.adjustment_breakdown?.error?.toString() || "",
      lesser_amount: payment.adjustment_breakdown?.lesser_amount?.toString() || "",
      other_notes: payment.adjustment_breakdown?.other_notes || "",
      verification_notes: payment.verification_notes || "",
    });
    setVerifyDialog(true);
  };
  
  // Handle double-click on row - opens verification dialog for quick verification
  const handleRowDoubleClick = (payment) => {
    // Allow double-click verification for pending and verified_at_upload statuses
    if (payment.verification_status === "pending" || payment.verification_status === "verified_at_upload") {
      openVerifyDialog(payment);
    }
  };

  // Calculate difference
  const calculateDifference = () => {
    const claimed = selectedPayment?.claimed_amount || 0;
    const actual = parseFloat(verificationForm.actual_amount) || 0;
    return claimed - actual;
  };

  // Calculate breakdown total
  const calculateBreakdownTotal = () => {
    return (
      (parseFloat(verificationForm.bank_charges) || 0) +
      (parseFloat(verificationForm.forex_charges) || 0) +
      (parseFloat(verificationForm.error_amount) || 0) +
      (parseFloat(verificationForm.lesser_amount) || 0)
    );
  };

  // Submit verification - Updated for Double Verification System
  const handleVerify = async () => {
    if (!selectedPayment) return;

    const actual = parseFloat(verificationForm.actual_amount) || 0;
    const claimed = selectedPayment.claimed_amount || 0;

    // Validation
    if (verificationForm.verification_status !== "verified_full") {
      if (!verificationForm.actual_amount) {
        toast.error("Please enter the actual amount received");
        return;
      }
    }

    setVerifying(true);
    try {
      // Use new API endpoint structure - payment_id instead of upload_id
      const response = await fetch(
        `${BACKEND_URL}/api/payment-verification/${selectedPayment.payment_id}/verify`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            payment_id: selectedPayment.payment_id,
            source: selectedPayment.source || "upload",
            upload_id: selectedPayment.upload_id,
            client_code: selectedPayment.client_code,
            tracking: selectedPayment.tracking,
            row_index: selectedPayment.row_index || 0,
            claimed_amount: claimed,
            verification_status: verificationForm.verification_status,
            actual_amount:
              verificationForm.verification_status === "verified_full"
                ? claimed
                : actual,
            received_date: verificationForm.received_date,
            bank_reference: verificationForm.bank_reference,
            difference_reason: verificationForm.difference_reason,
            payment_mode: selectedPayment.payment_mode,
            adjustment_breakdown: {
              bank_charges: parseFloat(verificationForm.bank_charges) || 0,
              forex_charges: parseFloat(verificationForm.forex_charges) || 0,
              error: parseFloat(verificationForm.error_amount) || 0,
              lesser_amount: parseFloat(verificationForm.lesser_amount) || 0,
              other_notes: verificationForm.other_notes,
            },
            verification_notes: verificationForm.verification_notes,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Payment verified successfully");

        if (result.adjustment_amount && result.adjustment_amount !== 0) {
          toast.info(
            `Ledger adjustment of ₹${Math.abs(result.adjustment_amount).toLocaleString()} created`
          );
        }

        setVerifyDialog(false);
        fetchPayments();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to verify payment");
      }
    } catch (error) {
      console.error("Verification error:", error);
      toast.error("Failed to verify payment");
    } finally {
      setVerifying(false);
    }
  };

  // Bulk verify
  const handleBulkVerify = async () => {
    if (selectedPayments.length === 0) {
      toast.error("Please select payments to verify");
      return;
    }

    setBulkVerifying(true);
    const CHUNK_SIZE = 500;
    let totalVerified = 0;
    let totalErrors = [];
    const chunks = [];
    
    for (let i = 0; i < selectedPayments.length; i += CHUNK_SIZE) {
      chunks.push(selectedPayments.slice(i, i + CHUNK_SIZE));
    }

    try {
      for (let i = 0; i < chunks.length; i++) {
        if (chunks.length > 1) {
          toast.info(`Processing batch ${i + 1} of ${chunks.length}...`, { duration: 2000 });
        }
        const response = await fetch(
          `${BACKEND_URL}/api/payment-verification/bulk-verify`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(chunks[i]),
          }
        );

        if (response.ok) {
          const result = await response.json();
          totalVerified += result.verified_count || 0;
          if (result.errors?.length) totalErrors.push(...result.errors);
        } else {
          totalErrors.push(`Batch ${i + 1} failed with status ${response.status}`);
        }
      }

      if (totalVerified > 0) {
        toast.success(`${totalVerified} payments verified successfully`);
        setSelectedPayments([]);
        fetchPayments();
      }
      if (totalErrors.length > 0) {
        toast.error(`${totalErrors.length} errors during verification`);
      }
    } catch (error) {
      console.error("Bulk verify error:", error);
      toast.error("Failed to bulk verify");
    } finally {
      setBulkVerifying(false);
    }
  };

  // Toggle payment selection
  const togglePaymentSelection = (payment) => {
    const exists = selectedPayments.find(
      (p) => p.payment_id === payment.payment_id
    );
    if (exists) {
      setSelectedPayments(
        selectedPayments.filter((p) => p.payment_id !== payment.payment_id)
      );
    } else {
      setSelectedPayments([
        ...selectedPayments,
        {
          upload_id: payment.upload_id,
          source: payment.source || "upload",
          client_code: payment.client_code,
          tracking: payment.tracking,
          row_index: payment.row_index || 0,
          claimed_amount: payment.claimed_amount,
          payment_id: payment.payment_id,
          payment_mode: payment.payment_mode,
        },
      ]);
    }
  };

  // Select all pending payments (including pre-verified)
  const selectAllPending = () => {
    const pendingPayments = filteredPayments
      .filter((p) => p.verification_status === "pending" || p.verification_status === "verified_at_upload")
      .map((p) => ({
        upload_id: p.upload_id,
        source: p.source || "upload",
        client_code: p.client_code,
        tracking: p.tracking,
        row_index: p.row_index || 0,
        claimed_amount: p.claimed_amount,
        payment_id: p.payment_id,
        payment_mode: p.payment_mode,
      }));
    setSelectedPayments(pendingPayments);
  };

  // Toggle verified payment selection
  const toggleVerifiedPaymentSelection = (payment) => {
    const exists = verifiedSelectedPayments.find((p) => p.payment_id === payment.payment_id);
    if (exists) {
      setVerifiedSelectedPayments(verifiedSelectedPayments.filter((p) => p.payment_id !== payment.payment_id));
    } else {
      setVerifiedSelectedPayments([...verifiedSelectedPayments, {
        upload_id: payment.upload_id,
        source: payment.source || "upload",
        client_code: payment.client_code,
        tracking: payment.tracking,
        row_index: payment.row_index || 0,
        claimed_amount: payment.claimed_amount,
        payment_id: payment.payment_id,
        payment_mode: payment.payment_mode,
      }]);
    }
  };

  // Select all verified payments
  const selectAllVerified = () => {
    setVerifiedSelectedPayments(verifiedDonePayments.map((p) => ({
      upload_id: p.upload_id,
      source: p.source || "upload",
      client_code: p.client_code,
      tracking: p.tracking,
      row_index: p.row_index || 0,
      claimed_amount: p.claimed_amount,
      payment_id: p.payment_id,
      payment_mode: p.payment_mode,
    })));
  };

  // Filter payments - exclude verified_full/verified_adjusted (they go to Verified Done tab)
  // Per-client "latest upload_id" map (used by the "Latest upload only" filter pill).
  // For each client_code, the upload with the largest `uploaded_at` wins.
  const latestUploadByClient = useMemo(() => {
    const m = {};
    for (const p of payments) {
      if (p.source !== "upload" || !p.upload_id || !p.client_code) continue;
      const cc = p.client_code.toUpperCase();
      const cur = m[cc];
      if (!cur || (p.uploaded_at || "") > (cur.uploaded_at || "")) {
        m[cc] = { upload_id: p.upload_id, uploaded_at: p.uploaded_at || "" };
      }
    }
    return m;
  }, [payments]);

  const filteredPayments = useMemo(() => {
    let result = payments.filter((p) => {
      // Exclude verified payments - they belong in the Verified Done tab
      if (p.verification_status === "verified_full" || p.verification_status === "verified_adjusted") {
        return false;
      }
      // Client filter — also enforced server-side, but keep here defensively so
      // the table never shows leftover rows from a previous fetch while the
      // new response is in flight (e.g. partner dropdown change race).
      if (clientFilter !== "all" && (p.client_code || "").toUpperCase() !== clientFilter.toUpperCase()) {
        return false;
      }
      // Apply status filter client-side
      if (statusFilter !== "all" && p.verification_status !== statusFilter) {
        return false;
      }
      // Reship-only pill — filters to FREE reships only (paid reships are
      // treated as regular payments now, since the partner charged for them)
      if (reshipOnly && !(p.is_reship && (Number(p.claimed_amount) || 0) === 0)) {
        return false;
      }
      // Zero-payment pill — filters to entries where the partner recorded no
      // payment (amount = 0) that are NOT free-reships (reships already have
      // their own pill). Requires the backend to have been fetched with
      // `include_zero=true` so these rows are in `payments` at all.
      if (showZeroOnly && !p.is_zero_payment) {
        return false;
      }
      // Payment-mode dropdown filter
      if (modeFilter !== "all" && (p.payment_mode || "").toLowerCase() !== modeFilter.toLowerCase()) {
        return false;
      }
      // Low-confidence date filter — show only rows whose date_source is
      // 'today' (red pulsing dot) so the team can fix them before verifying.
      if (lowConfidenceDatesOnly && p.date_source !== "today" && p.date_source !== "unknown") {
        return false;
      }
      // Latest-upload-only pill: keep only payments whose upload_id matches the
      // latest upload for THIS client. Sales-source rows (no upload_id) are
      // dropped when this filter is on, since they pre-date the upload era.
      if (latestUploadOnly) {
        const cc = (p.client_code || "").toUpperCase();
        const latest = latestUploadByClient[cc];
        if (!latest || p.upload_id !== latest.upload_id) return false;
      }
      // Raw-verified-only pill: keep only payments that have been confirmed by
      // the accounting team's Raw Funds sheet (auto-matched on client + INR ±50
      // + date ±7 days). Operators use this to identify "safe to bulk-verify"
      // payments instantly.
      if (rawVerifiedOnly && !p.raw_verified) {
        return false;
      }
      // Apply date range filter
      if (dateFrom || dateTo) {
        const pDate = parseDateToISO(p.payment_date);
        if (dateFrom && pDate < dateFrom) return false;
        if (dateTo && pDate > dateTo) return false;
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          p.tracking?.toLowerCase().includes(query) ||
          p.client_code?.toLowerCase().includes(query) ||
          p.customer?.toLowerCase().includes(query) ||
          p.payment_mode?.toLowerCase().includes(query)
        );
      }
      return true;
    });

    // Apply sorting
    if (tableSortConfig.key) {
      result = [...result].sort((a, b) => {
        const { key, direction } = tableSortConfig;
        
        // Date fields use monthly sorting (Feb 26 > Jan 26 > Dec 25)
        if (key === 'payment_date') {
          const dateA = a.payment_date || a.payment_date_display || '';
          const dateB = b.payment_date || b.payment_date_display || '';
          return compareByMonthYear(dateA, dateB, direction);
        }
        
        // Numeric fields
        if (key === 'claimed_amount' || key === 'actual_amount') {
          const aVal = a[key] || 0;
          const bVal = b[key] || 0;
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        
        // String fields
        const aVal = (a[key] || '').toString().toLowerCase();
        const bVal = (b[key] || '').toString().toLowerCase();
        if (aVal < bVal) return direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [payments, searchQuery, statusFilter, clientFilter, dateFrom, dateTo, tableSortConfig, reshipOnly, latestUploadOnly, latestUploadByClient, rawVerifiedOnly, lowConfidenceDatesOnly, showZeroOnly, modeFilter]);

  // Verified Done payments - verified_full and verified_adjusted, EXCLUDING
  // FOC rows (Zero-Payment or Reship-Free) which now live in their own tab.
  const verifiedDonePayments = useMemo(() => {
    let result = payments.filter((p) =>
      (p.verification_status === "verified_full" || p.verification_status === "verified_adjusted")
      && !p.is_reship
      && !p.is_zero_payment
    );

    if (verifiedClientFilter !== "all") {
      result = result.filter((p) => p.client_code === verifiedClientFilter);
    }

    if (verifiedReshipOnly) {
      result = result.filter((p) => p.is_reship);
    }
    if (verifiedZeroOnly) {
      result = result.filter((p) => p.is_zero_payment);
    }
    if (verifiedRawVerifiedOnly) {
      result = result.filter((p) => p.raw_verified);
    }
    if (verifiedModeFilter !== "all") {
      const m = verifiedModeFilter.toLowerCase();
      result = result.filter((p) => (p.payment_mode || "").toLowerCase() === m);
    }

    if (verifiedSearchQuery) {
      const query = verifiedSearchQuery.toLowerCase();
      result = result.filter((p) =>
        p.tracking?.toLowerCase().includes(query) ||
        p.client_code?.toLowerCase().includes(query) ||
        p.customer?.toLowerCase().includes(query) ||
        p.payment_mode?.toLowerCase().includes(query)
      );
    }

    // Apply date range filter
    if (verifiedDateFrom || verifiedDateTo) {
      result = result.filter((p) => {
        const pDate = parseDateToISO(p.payment_date);
        if (verifiedDateFrom && pDate < verifiedDateFrom) return false;
        if (verifiedDateTo && pDate > verifiedDateTo) return false;
        return true;
      });
    }

    if (verifiedTableSortConfig.key) {
      result = [...result].sort((a, b) => {
        const { key, direction } = verifiedTableSortConfig;
        if (key === 'payment_date') {
          const dateA = a.payment_date || a.payment_date_display || '';
          const dateB = b.payment_date || b.payment_date_display || '';
          return compareByMonthYear(dateA, dateB, direction);
        }
        if (key === 'claimed_amount' || key === 'actual_amount') {
          return direction === 'asc' ? (a[key] || 0) - (b[key] || 0) : (b[key] || 0) - (a[key] || 0);
        }
        const aVal = (a[key] || '').toString().toLowerCase();
        const bVal = (b[key] || '').toString().toLowerCase();
        if (aVal < bVal) return direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [payments, verifiedSearchQuery, verifiedClientFilter, verifiedDateFrom, verifiedDateTo, verifiedTableSortConfig, verifiedReshipOnly, verifiedZeroOnly, verifiedRawVerifiedOnly, verifiedModeFilter]);

  // FOC (Free-of-Cost) tab — verified rows that are Reship-Free OR Zero-Payment.
  // Shares the Verified Done filter widgets (client / search / date / mode)
  // plus a dedicated type filter (all / reship / zero).
  const focPayments = useMemo(() => {
    let result = payments.filter((p) =>
      (p.verification_status === "verified_full" || p.verification_status === "verified_adjusted")
      && (p.is_reship || p.is_zero_payment)
    );
    if (focTypeFilter === "reship") result = result.filter((p) => p.is_reship);
    if (focTypeFilter === "zero") result = result.filter((p) => p.is_zero_payment);
    if (focClientFilter !== "all") result = result.filter((p) => p.client_code === focClientFilter);
    if (focModeFilter !== "all") {
      const m = focModeFilter.toLowerCase();
      result = result.filter((p) => (p.payment_mode || "").toLowerCase() === m);
    }
    if (focSearchQuery) {
      const q = focSearchQuery.toLowerCase();
      result = result.filter((p) =>
        p.tracking?.toLowerCase().includes(q) ||
        p.client_code?.toLowerCase().includes(q) ||
        p.customer?.toLowerCase().includes(q) ||
        p.payment_mode?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [payments, focTypeFilter, focClientFilter, focModeFilter, focSearchQuery]);

  // Full list of payment_mode values seen — used to populate the mode dropdowns.
  const paymentModeOptions = useMemo(() => {
    const s = new Set();
    for (const p of payments) {
      const m = (p.payment_mode || "").trim();
      if (m) s.add(m);
    }
    return Array.from(s).sort();
  }, [payments]);

  // Computed summary for Verification tab (respects partner/client filter)
  const computedSummary = useMemo(() => {
    // Filter all payments by the selected client (if any)
    const clientPayments = clientFilter !== "all"
      ? payments.filter(p => p.client_code === clientFilter)
      : payments;
    
    const pending = clientPayments.filter(p => p.verification_status === "pending").length;
    const verified_at_upload = clientPayments.filter(p => p.verification_status === "verified_at_upload").length;
    const verified_full = clientPayments.filter(p => p.verification_status === "verified_full").length;
    const verified_adjusted = clientPayments.filter(p => p.verification_status === "verified_adjusted").length;
    const rejected = clientPayments.filter(p => p.verification_status === "rejected").length;
    const total_claimed = clientPayments.reduce((sum, p) => sum + (p.claimed_amount || 0), 0);
    const total_verified = clientPayments
      .filter(p => p.verification_status === "verified_full" || p.verification_status === "verified_adjusted")
      .reduce((sum, p) => sum + (p.actual_amount || p.claimed_amount || 0), 0);
    
    return { pending, verified_at_upload, verified_full, verified_adjusted, rejected, total_claimed, total_verified };
  }, [payments, clientFilter]);

  // Computed totals for the filtered payments table (Verification tab)
  const filteredPaymentsTotals = useMemo(() => {
    const totalClaimed = filteredPayments.reduce((sum, p) => sum + (p.claimed_amount || 0), 0);
    const totalReceived = filteredPayments.reduce((sum, p) => sum + (p.actual_amount || 0), 0);
    const totalDiff = totalClaimed - totalReceived;
    return { totalClaimed, totalReceived, totalDiff, count: filteredPayments.length };
  }, [filteredPayments]);

  // Computed summary for Verified Done tab (respects filters)
  const verifiedDoneSummary = useMemo(() => {
    const fullCount = verifiedDonePayments.filter(p => p.verification_status === "verified_full").length;
    const adjustedCount = verifiedDonePayments.filter(p => p.verification_status === "verified_adjusted").length;
    const totalClaimed = verifiedDonePayments.reduce((sum, p) => sum + (p.claimed_amount || 0), 0);
    const totalReceived = verifiedDonePayments.reduce((sum, p) => sum + (p.actual_amount || p.claimed_amount || 0), 0);
    return { fullCount, adjustedCount, totalRecords: verifiedDonePayments.length, totalClaimed, totalReceived };
  }, [verifiedDonePayments]);

  // Pagination computations
  const pendingTotalPages = Math.max(1, Math.ceil(filteredPayments.length / pageSize));
  const safePendingPage = Math.min(pendingPage, pendingTotalPages);
  const paginatedPendingPayments = useMemo(() => {
    const start = (safePendingPage - 1) * pageSize;
    return filteredPayments.slice(start, start + pageSize);
  }, [filteredPayments, safePendingPage, pageSize]);

  const verifiedTotalPages = Math.max(1, Math.ceil(verifiedDonePayments.length / pageSize));
  const safeVerifiedPage = Math.min(verifiedPage, verifiedTotalPages);
  const paginatedVerifiedPayments = useMemo(() => {
    const start = (safeVerifiedPage - 1) * pageSize;
    return verifiedDonePayments.slice(start, start + pageSize);
  }, [verifiedDonePayments, safeVerifiedPage, pageSize]);

  // Pagination Controls Component
  const PaginationControls = ({ currentPage, totalPages, totalRecords, onPageChange, testIdPrefix }) => {
    if (totalRecords <= pageSize) return null;
    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);
    
    // Generate page numbers to show
    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(totalPages, start + maxVisible - 1);
      if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    };

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t bg-slate-50/50" data-testid={`${testIdPrefix}-pagination`}>
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <span>Showing {startRecord}-{endRecord} of {totalRecords.toLocaleString()}</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); onPageChange(1); }}
            className="border rounded px-2 py-1 text-xs bg-white"
            data-testid={`${testIdPrefix}-page-size`}
          >
            <option value={25}>25/page</option>
            <option value={50}>50/page</option>
            <option value={100}>100/page</option>
            <option value={200}>200/page</option>
          </select>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => onPageChange(1)} disabled={currentPage <= 1} data-testid={`${testIdPrefix}-first-page`}>
            <ChevronsLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage <= 1} data-testid={`${testIdPrefix}-prev-page`}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          {getPageNumbers().map(p => (
            <Button key={p} variant={p === currentPage ? "default" : "outline"} size="sm"
              className={`h-8 w-8 p-0 text-xs ${p === currentPage ? "bg-slate-800 text-white" : ""}`}
              onClick={() => onPageChange(p)} data-testid={`${testIdPrefix}-page-${p}`}
            >{p}</Button>
          ))}
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages} data-testid={`${testIdPrefix}-next-page`}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages} data-testid={`${testIdPrefix}-last-page`}>
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  // Get status badge - Updated for Double Verification System
  const getStatusBadge = (status, source) => {
    switch (status) {
      case "pending":
        return (
          <Badge className="bg-amber-100 text-amber-700 gap-1">
            <Clock className="w-3 h-3" />
            Pending
          </Badge>
        );
      case "verified_at_upload":
        return (
          <Badge className="bg-cyan-100 text-cyan-700 gap-1">
            <FileCheck className="w-3 h-3" />
            Pre-Verified
          </Badge>
        );
      case "verified_full":
        return (
          <Badge className="bg-green-100 text-green-700 gap-1">
            <CheckCircle className="w-3 h-3" />
            Verified
          </Badge>
        );
      case "verified_adjusted":
        return (
          <Badge className="bg-blue-100 text-blue-700 gap-1">
            <AlertTriangle className="w-3 h-3" />
            Adjusted
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-700 gap-1">
            <XCircle className="w-3 h-3" />
            Rejected
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  // Get source badge
  const getSourceBadge = (source) => {
    if (source === "sales") {
      return (
        <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600 border-purple-200">
          <Database className="w-3 h-3 mr-1" />
          Historical
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
        <Upload className="w-3 h-3 mr-1" />
        Upload
      </Badge>
    );
  };

  return (
    <div className="p-3 lg:p-6 space-y-4 lg:space-y-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg lg:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Receipt className="w-5 h-5 lg:w-7 lg:h-7 text-emerald-600 flex-shrink-0" />
            <span className="truncate">Payment Verification</span>
          </h1>
          <p className="text-slate-500 mt-1 text-xs lg:text-sm hidden sm:block">
            Verify partner payment amounts and generate reports
          </p>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 sm:grid-cols-8 mb-4 lg:mb-6 h-auto">
          <TabsTrigger value="verification" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5 px-1.5 lg:px-3" data-testid="tab-verification">
            <CheckSquare className="w-3.5 h-3.5 lg:w-4 lg:h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Verification</span>
            <span className="sm:hidden">Verify</span>
          </TabsTrigger>
          <TabsTrigger value="verified-done" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5 px-1.5 lg:px-3" data-testid="tab-verified-done">
            <CheckCircle className="w-3.5 h-3.5 lg:w-4 lg:h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Verified Done</span>
            <span className="sm:hidden">Done</span>
          </TabsTrigger>
          <TabsTrigger value="foc" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5 px-1.5 lg:px-3" data-testid="tab-foc">
            <Gift className="w-3.5 h-3.5 lg:w-4 lg:h-4 flex-shrink-0" />
            <span className="hidden sm:inline">FOC (Free-of-Cost)</span>
            <span className="sm:hidden">FOC</span>
          </TabsTrigger>
          <TabsTrigger value="pending-sales" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5 px-1.5 lg:px-3" data-testid="tab-pending-sales">
            <Users className="w-3.5 h-3.5 lg:w-4 lg:h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Pending Sales</span>
            <span className="sm:hidden">Pending</span>
          </TabsTrigger>
          <TabsTrigger value="follow-up" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5 px-1.5 lg:px-3" data-testid="tab-follow-up">
            <Calendar className="w-3.5 h-3.5 lg:w-4 lg:h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Follow Up</span>
            <span className="sm:hidden">Follow</span>
          </TabsTrigger>
          <TabsTrigger value="auto-generate" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5 px-1.5 lg:px-3" data-testid="tab-auto-generate">
            <Calculator className="w-3.5 h-3.5 lg:w-4 lg:h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Auto Generate</span>
            <span className="sm:hidden">Auto</span>
          </TabsTrigger>
          <TabsTrigger value="logs" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5 px-1.5 lg:px-3" onClick={() => fetchDeletionLogs()} data-testid="tab-logs">
            <FileText className="w-3.5 h-3.5 lg:w-4 lg:h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Deletion Logs</span>
            <span className="sm:hidden">Logs</span>
          </TabsTrigger>
          <TabsTrigger value="uploaded-sheets" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5 px-1.5 lg:px-3" onClick={() => fetchUploadedSheets(1)} data-testid="tab-uploaded-sheets">
            <FileSpreadsheet className="w-3.5 h-3.5 lg:w-4 lg:h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Uploaded Sheets</span>
            <span className="sm:hidden">Sheets</span>
          </TabsTrigger>
          <TabsTrigger value="raw-funds" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5 px-1.5 lg:px-3" onClick={() => fetchRawFunds(1)} data-testid="tab-raw-funds">
            <Database className="w-3.5 h-3.5 lg:w-4 lg:h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Raw Data</span>
            <span className="sm:hidden">Raw</span>
          </TabsTrigger>
          <TabsTrigger value="crdr" className="gap-1 lg:gap-2 text-xs lg:text-sm py-2 lg:py-2.5 px-1.5 lg:px-3" data-testid="tab-crdr">
            <BookPlus className="w-3.5 h-3.5 lg:w-4 lg:h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Cr/Dr Entries</span>
            <span className="sm:hidden">Cr/Dr</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Payment Verification (Original Content) */}
        <TabsContent value="verification" className="space-y-4 lg:space-y-6">
          {loadError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="pv-load-error-banner">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-sm">Couldn't load payment data</p>
                <p className="text-xs text-red-600 mt-1">{loadError}</p>
                <p className="text-xs text-red-500 mt-1">Your data is safe — this is a temporary read issue. Click Refresh to retry.</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => { fetchPayments(); fetchClients(); }} className="h-8">
                Retry
              </Button>
            </div>
          )}
          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <Button 
              onClick={() => setWipeDialog(true)} 
              variant="outline" 
              size="sm" 
              className="gap-2 h-9 text-red-600 border-red-200 hover:bg-red-50" 
            >
              <Database className="w-4 h-4" />
              <span className="hidden sm:inline">Wipe Data</span>
            </Button>
            <Button 
              onClick={handleCleanupData} 
              variant="outline" 
              size="sm" 
              className="gap-2 h-9 text-orange-600 border-orange-200 hover:bg-orange-50" 
              disabled={cleaningData}
            >
              {cleaningData ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Wrench className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Fix Data</span>
            </Button>
            <Button 
              onClick={handleFixDates} 
              variant="outline" 
              size="sm" 
              className="gap-2 h-9 text-blue-600 border-blue-200 hover:bg-blue-50" 
              disabled={fixingDates}
              data-testid="fix-dates-btn"
            >
              {fixingDates ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Calendar className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Fix Dates</span>
            </Button>
            <Button onClick={() => { fetchPayments(); fetchClients(); }} variant="outline" size="sm" className="gap-2 h-9" disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>

          {/* Summary Cards - Mobile optimized grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 lg:gap-4">
            <Card className="cursor-pointer hover:shadow-md active:scale-[0.98] transition-all touch-manipulation" onClick={() => setStatusFilter("pending")}>
              <CardContent className="p-3 lg:p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-[10px] lg:text-sm text-slate-500">Pending</p>
                    <p className="text-lg lg:text-2xl font-bold text-amber-600" data-testid="card-pending-count">{computedSummary.pending}</p>
                  </div>
                  <Clock className="w-6 h-6 lg:w-8 lg:h-8 text-amber-200 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md active:scale-[0.98] transition-all touch-manipulation" onClick={() => setStatusFilter("verified_at_upload")}>
              <CardContent className="p-3 lg:p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-[10px] lg:text-sm text-slate-500 truncate">Pre-Verified</p>
                    <p className="text-lg lg:text-2xl font-bold text-cyan-600" data-testid="card-preverified-count">{computedSummary.verified_at_upload || 0}</p>
                  </div>
                  <FileCheck className="w-6 h-6 lg:w-8 lg:h-8 text-cyan-200 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

        <Card
          className="cursor-pointer hover:shadow-md active:scale-[0.98] transition-all touch-manipulation"
          onClick={() => setActiveTab("verified-done")}
        >
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] lg:text-sm text-slate-500">Verified</p>
                <p className="text-lg lg:text-2xl font-bold text-green-600" data-testid="card-verified-count">
                  {computedSummary.verified_full}
                </p>
              </div>
              <CheckCircle className="w-6 h-6 lg:w-8 lg:h-8 text-green-200 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md active:scale-[0.98] transition-all touch-manipulation"
          onClick={() => setActiveTab("verified-done")}
        >
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] lg:text-sm text-slate-500">Adjusted</p>
                <p className="text-lg lg:text-2xl font-bold text-blue-600" data-testid="card-adjusted-count">
                  {computedSummary.verified_adjusted}
                </p>
              </div>
              <AlertTriangle className="w-6 h-6 lg:w-8 lg:h-8 text-blue-200 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-md active:scale-[0.98] transition-all touch-manipulation"
          onClick={() => setStatusFilter("rejected")}
        >
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] lg:text-sm text-slate-500">Rejected</p>
                <p className="text-lg lg:text-2xl font-bold text-red-600" data-testid="card-rejected-count">
                  {computedSummary.rejected}
                </p>
              </div>
              <XCircle className="w-6 h-6 lg:w-8 lg:h-8 text-red-200 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-2 sm:col-span-1">
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] lg:text-sm text-slate-500">Total Claimed</p>
                <p className="text-lg lg:text-xl font-bold text-slate-700 truncate" data-testid="card-total-claimed">
                  ₹{(computedSummary.total_claimed || 0).toLocaleString()}
                </p>
              </div>
              <Banknote className="w-6 h-6 lg:w-8 lg:h-8 text-slate-200 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-2 sm:col-span-1">
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] lg:text-sm text-slate-500">Total Verified</p>
                <p className="text-lg lg:text-xl font-bold text-emerald-600 truncate" data-testid="card-total-verified">
                  ₹{(computedSummary.total_verified || 0).toLocaleString()}
                </p>
              </div>
              <CheckSquare className="w-6 h-6 lg:w-8 lg:h-8 text-emerald-200 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Actions - Mobile optimized with collapsible filters */}
      <Card>
        <CardContent className="p-3 lg:p-4">
          {/* Mobile: Collapsible Filter Header */}
          <div className="lg:hidden">
            <button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="w-full flex items-center justify-between p-2 -m-2 rounded-lg hover:bg-slate-50 active:bg-slate-100 transition-colors"
              data-testid="payment-filter-toggle-btn"
            >
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-medium text-slate-700">Search & Filters</span>
                {activeFilterCount > 0 && (
                  <span className="bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {activeFilterCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">{filteredPayments.length} records</span>
                {filtersExpanded ? (
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                )}
              </div>
            </button>

            {/* Mobile Expandable Content */}
            <div className={`overflow-hidden transition-all duration-200 ease-in-out ${filtersExpanded ? 'max-h-[400px] opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
              <div className="space-y-3">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search tracking, partner..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-9 text-xs"
                  />
                </div>

                {/* Filters Grid */}
                <div className="grid grid-cols-2 gap-2">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="verified_at_upload">Pre-Verified</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={clientFilter} onValueChange={setClientFilter}>
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="Partner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Partners</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client} value={client}>
                          {client}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <DMYDateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-xs" placeholder="From" data-testid="date-from-mobile" />
                  <DMYDateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-xs" placeholder="To" data-testid="date-to-mobile" />
                </div>

                {/* Bulk Actions */}
                <div className="flex items-center gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllPending}
                    disabled={!filteredPayments.some((p) => p.verification_status === "pending")}
                    className="h-8 text-xs flex-1"
                  >
                    Select All Pending
                  </Button>
                  {selectedPayments.length > 0 && (
                    <>
                      <Button
                        onClick={handleBulkVerify}
                        disabled={bulkVerifying}
                        className="bg-green-600 hover:bg-green-700 gap-2 h-8 text-xs flex-1"
                      >
                        {bulkVerifying ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <CheckCircle className="w-3 h-3" />
                        )}
                        Verify ({selectedPayments.length})
                      </Button>
                      <Button
                        onClick={() => setDeleteSelectedDialog(true)}
                        variant="destructive"
                        className="gap-2 h-8 text-xs"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete ({selectedPayments.length})
                      </Button>
                    </>
                  )}
                </div>

                {/* Clear filters */}
                {activeFilterCount > 0 && (
                  <button
                    onClick={() => {
                      setStatusFilter("all");
                      setClientFilter("all");
                      setSearchQuery("");
                      setDateFrom("");
                      setDateTo("");
                    }}
                    className="w-full text-xs text-orange-600 hover:text-orange-700 font-medium py-1"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Desktop: Always visible filter row */}
          <div className="hidden lg:flex lg:flex-col lg:gap-3">
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search tracking, partner..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10"
                />
              </div>

              {/* Filters row */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Status Filter */}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[160px] h-10">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="verified_at_upload">Pre-Verified</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>

                {/* Client Filter */}
                <Select value={clientFilter} onValueChange={setClientFilter}>
                  <SelectTrigger className="w-[150px] h-10">
                    <SelectValue placeholder="Partner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Partners</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client} value={client}>
                        {client}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Date From */}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-500 whitespace-nowrap">From:</span>
                  <DMYDateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px] h-10" data-testid="date-from-desktop" />
                </div>

                {/* Date To */}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-500 whitespace-nowrap">To:</span>
                  <DMYDateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px] h-10" data-testid="date-to-desktop" />
                </div>

                {(dateFrom || dateTo) && (
                  <Button variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); }} className="h-10 px-2 text-xs text-slate-500 hover:text-slate-700" data-testid="clear-date-filter">
                    <XCircle className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Bulk Actions */}
              <div className="flex items-center gap-2 ml-auto flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllPending}
                  disabled={!filteredPayments.some((p) => p.verification_status === "pending")}
                  className="h-9 text-sm"
                >
                  Select All Pending
                </Button>
                {selectedPayments.length > 0 && (
                  <>
                    <Button
                      onClick={handleBulkVerify}
                      disabled={bulkVerifying}
                      className="bg-green-600 hover:bg-green-700 gap-2 h-9 text-sm"
                    >
                      {bulkVerifying ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                      Verify ({selectedPayments.length})
                    </Button>
                    <Button
                      onClick={() => setDeleteSelectedDialog(true)}
                      variant="destructive"
                      className="gap-2 h-9 text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete ({selectedPayments.length})
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reship Summary — separates Free (no revenue) from Paid reships */}
      {(() => {
        const allReships = payments.filter(p => p.is_reship);
        const freeReships = allReships.filter(p => (Number(p.claimed_amount) || 0) === 0);
        const paidReships = allReships.filter(p => (Number(p.claimed_amount) || 0) > 0);
        if (allReships.length === 0) return null;
        const pendingReship = freeReships.filter(
          (p) => p.verification_status !== "verified_full" && p.verification_status !== "verified_adjusted"
        );
        const verifiedReship = freeReships.length - pendingReship.length;
        // Sum the "lost" revenue for free reships (what the parcel was worth)
        const lostValue = freeReships.reduce((sum, p) => sum + (Number(p.claimed_amount) || Number(p.actual_amount) || 0), 0);
        // Sum the amount partners have actually charged on paid reships
        const paidTotal = paidReships.reduce((sum, p) => sum + (Number(p.claimed_amount) || 0), 0);
        return (
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50/60 to-white">
            <CardContent className="py-4">
              <div className="flex items-center flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Package className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-purple-600 font-bold">Free Reships</div>
                    <div className="text-xs text-slate-500">Cost-of-reship overview</div>
                  </div>
                </div>
                <div className="h-10 border-r border-purple-200" />
                <div className="flex items-baseline gap-1.5">
                  <div className="text-2xl font-bold text-purple-700" data-testid="reship-total-count">{freeReships.length}</div>
                  <div className="text-xs text-slate-500">free</div>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <div className="text-2xl font-bold text-amber-700" data-testid="reship-pending-count">{pendingReship.length}</div>
                  <div className="text-xs text-slate-500">pending</div>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <div className="text-2xl font-bold text-emerald-700" data-testid="reship-verified-count">{verifiedReship}</div>
                  <div className="text-xs text-slate-500">verified</div>
                </div>
                {paidReships.length > 0 && (
                  <div className="flex items-baseline gap-1.5" data-testid="reship-paid-block">
                    <div className="text-2xl font-bold text-indigo-700" data-testid="reship-paid-count">{paidReships.length}</div>
                    <div className="text-xs text-slate-500">paid (₹{Math.round(paidTotal).toLocaleString()})</div>
                  </div>
                )}
                {lostValue > 0 && (
                  <div className="flex items-baseline gap-1.5">
                    <div className="text-2xl font-bold text-rose-600" data-testid="reship-lost-value">₹{Math.round(lostValue).toLocaleString()}</div>
                    <div className="text-xs text-slate-500">parcel value reshipped</div>
                  </div>
                )}
                <div className="ml-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-purple-300 text-purple-700 hover:bg-purple-100"
                    onClick={() => setReshipOnly((v) => !v)}
                    data-testid="reship-summary-toggle"
                  >
                    {reshipOnly ? "Showing only reship • Click to clear" : "Show reship only"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Payments Table */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Payment Records ({filteredPayments.length})</span>
            <div className="flex items-center gap-2">
              {/* Latest-upload-only filter pill (kicks in when a client is selected) */}
              {(() => {
                if (clientFilter === "all") return null;
                const latest = latestUploadByClient[(clientFilter || "").toUpperCase()];
                if (!latest) return null;
                const latestCount = payments.filter(
                  (p) => p.upload_id === latest.upload_id && p.verification_status !== "verified_full" && p.verification_status !== "verified_adjusted"
                ).length;
                return (
                  <button
                    type="button"
                    onClick={() => setLatestUploadOnly((v) => !v)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                      latestUploadOnly
                        ? "bg-indigo-600 text-white hover:bg-indigo-700"
                        : "bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
                    }`}
                    title={`Show only parcels from the latest upload (${new Date(latest.uploaded_at).toLocaleDateString()})`}
                    data-testid="latest-upload-only-pill"
                  >
                    <span>Latest upload only</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${latestUploadOnly ? "bg-white/20" : "bg-indigo-100"}`}>
                      {latestCount}
                    </span>
                  </button>
                );
              })()}
              {/* Payment Mode dropdown filter (Verification tab) */}
              {paymentModeOptions.length > 0 && (
                <select
                  value={modeFilter}
                  onChange={(e) => setModeFilter(e.target.value)}
                  className={`h-7 rounded-full text-xs font-semibold border px-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 ${
                    modeFilter === "all"
                      ? "bg-white text-slate-700 border-slate-200"
                      : "bg-slate-800 text-white border-slate-800"
                  }`}
                  title="Filter by payment mode"
                  data-testid="mode-filter-select"
                >
                  <option value="all">Payment Mode</option>
                  {paymentModeOptions.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              )}
              {/* Reship-only filter pill — shows FREE reships only (paid reships stay in normal payment view) */}
              {(() => {
                const reshipCount = payments.filter(p =>
                  p.is_reship
                  && (Number(p.claimed_amount) || 0) === 0
                  && p.verification_status !== 'verified_full'
                  && p.verification_status !== 'verified_adjusted'
                ).length;
                if (reshipCount === 0 && !reshipOnly) return null;
                return (
                  <button
                    type="button"
                    onClick={() => setReshipOnly(v => !v)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                      reshipOnly
                        ? "bg-purple-600 text-white hover:bg-purple-700"
                        : "bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100"
                    }`}
                    title="Show only free-reship parcels (no payment expected)"
                    data-testid="reship-only-pill"
                  >
                    <span>Reship Free</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${reshipOnly ? "bg-white/20" : "bg-purple-100"}`}>
                      {reshipCount}
                    </span>
                  </button>
                );
              })()}
              {/* Raw-verified filter pill — payments confirmed by accounting Raw Funds sheet */}
              {(() => {
                const rawCount = payments.filter(p => p.raw_verified && p.verification_status !== 'verified_full' && p.verification_status !== 'verified_adjusted').length;
                if (rawCount === 0 && !rawVerifiedOnly) return null;
                return (
                  <button
                    type="button"
                    onClick={() => setRawVerifiedOnly(v => !v)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                      rawVerifiedOnly
                        ? "bg-emerald-600 text-white hover:bg-emerald-700"
                        : "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                    }`}
                    title="Auto-confirmed by accounting Raw Funds (same client · ₹±50 · ±7 days)"
                    data-testid="raw-verified-pill"
                  >
                    <span>Raw ✓</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${rawVerifiedOnly ? "bg-white/20" : "bg-emerald-100"}`}>
                      {rawCount}
                    </span>
                  </button>
                );
              })()}
              {/* Zero-payment filter pill — parcels with no payment recorded.
                  Toggling this refetches the endpoint with include_zero=true
                  (see fetchPayments) so the rows are streamed into `payments`. */}
              {(() => {
                const zeroCount = payments.filter(p => p.is_zero_payment).length;
                return (
                  <button
                    type="button"
                    onClick={() => setShowZeroOnly(v => !v)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                      showZeroOnly
                        ? "bg-amber-600 text-white hover:bg-amber-700"
                        : "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                    }`}
                    title="Show parcels where the partner recorded no payment (amount = 0)"
                    data-testid="zero-payment-pill"
                  >
                    <span>Zero Payment</span>
                    {showZeroOnly && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/20">
                        {zeroCount}
                      </span>
                    )}
                  </button>
                );
              })()}
              {/* Low-date-confidence filter pill — surface entries that fell
                  back to today's date so team can correct them before verifying. */}
              {(() => {
                const lowCount = payments.filter(
                  (p) => (p.date_source === "today" || p.date_source === "unknown")
                    && p.verification_status !== "verified_full"
                    && p.verification_status !== "verified_adjusted"
                ).length;
                if (lowCount === 0 && !lowConfidenceDatesOnly) return null;
                return (
                  <button
                    type="button"
                    onClick={() => setLowConfidenceDatesOnly((v) => !v)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                      lowConfidenceDatesOnly
                        ? "bg-rose-600 text-white hover:bg-rose-700"
                        : "bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100"
                    }`}
                    title="Show only rows whose date defaulted to today's date — please verify"
                    data-testid="low-date-confidence-pill"
                  >
                    <span className={`w-2 h-2 rounded-full ${lowConfidenceDatesOnly ? "bg-white" : "bg-rose-500 animate-pulse"}`} />
                    <span>Date to verify</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${lowConfidenceDatesOnly ? "bg-white/20" : "bg-rose-100"}`}>
                      {lowCount}
                    </span>
                  </button>
                );
              })()}
              {(statusFilter !== "all" || reshipOnly || latestUploadOnly || rawVerifiedOnly || lowConfidenceDatesOnly || showZeroOnly || modeFilter !== "all") && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setStatusFilter("all"); setReshipOnly(false); setLatestUploadOnly(false); setRawVerifiedOnly(false); setLowConfidenceDatesOnly(false); setShowZeroOnly(false); setModeFilter("all"); }}
                  data-testid="clear-pv-filters-btn"
                >
                  Clear Filter
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No payments found</p>
              <p className="text-sm">
                Payments will appear here after partner sheet uploads are
                processed
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={
                          selectedPayments.length > 0 &&
                          selectedPayments.length ===
                            filteredPayments.filter(
                              (p) => p.verification_status === "pending" || p.verification_status === "verified_at_upload"
                            ).length
                        }
                        onCheckedChange={(checked) => {
                          if (checked) {
                            selectAllPending();
                          } else {
                            setSelectedPayments([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleTableSort('client_code')}
                    >
                      <div className="flex items-center">Partner<TableSortIcon columnKey="client_code" /></div>
                    </TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleTableSort('payment_date')}
                    >
                      <div className="flex items-center">Date<TableSortIcon columnKey="payment_date" /></div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleTableSort('tracking')}
                    >
                      <div className="flex items-center">Tracking<TableSortIcon columnKey="tracking" /></div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleTableSort('customer')}
                    >
                      <div className="flex items-center">Customer<TableSortIcon columnKey="customer" /></div>
                    </TableHead>
                    <TableHead 
                      className="text-right cursor-pointer hover:bg-slate-100"
                      onClick={() => handleTableSort('claimed_amount')}
                    >
                      <div className="flex items-center justify-end">Claimed<TableSortIcon columnKey="claimed_amount" /></div>
                    </TableHead>
                    <TableHead 
                      className="text-right cursor-pointer hover:bg-slate-100"
                      onClick={() => handleTableSort('actual_amount')}
                    >
                      <div className="flex items-center justify-end">Received<TableSortIcon columnKey="actual_amount" /></div>
                    </TableHead>
                    <TableHead className="text-right">Diff</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleTableSort('payment_mode')}
                    >
                      <div className="flex items-center">Mode<TableSortIcon columnKey="payment_mode" /></div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleTableSort('verification_status')}
                    >
                      <div className="flex items-center">Status<TableSortIcon columnKey="verification_status" /></div>
                    </TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedPendingPayments.map((payment, idx) => {
                    const isSelected = selectedPayments.some(
                      (p) => p.payment_id === payment.payment_id
                    );
                    const diff =
                      payment.verification_status !== "pending"
                        ? (payment.claimed_amount || 0) -
                          (payment.actual_amount || payment.claimed_amount || 0)
                        : null;
                    
                    // Show pre-verified amounts from upload
                    const showPreVerifiedDiff = payment.verification_status === "verified_at_upload" && payment.adjustment_needed;

                    return (
                      <TableRow
                        key={payment.payment_id || idx}
                        className={`${isSelected ? "bg-blue-50" : ""} ${
                          payment.verification_status === "verified_at_upload" ? "bg-cyan-50/50" : ""
                        } ${
                          (payment.verification_status === "pending" || payment.verification_status === "verified_at_upload") 
                            ? "cursor-pointer hover:bg-slate-100" : ""
                        }`}
                        onDoubleClick={() => handleRowDoubleClick(payment)}
                        title={payment.verification_status === "pending" || payment.verification_status === "verified_at_upload" 
                          ? "Double-click to verify" : ""}
                      >
                        <TableCell>
                          {(payment.verification_status === "pending" || payment.verification_status === "verified_at_upload") && (
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() =>
                                togglePaymentSelection(payment)
                              }
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            {payment.client_code}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {getSourceBadge(payment.source)}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          <div className="flex items-center gap-1.5">
                            <DateSourceDot source={payment.date_source} />
                            <span>{payment.payment_date_display || formatDateDashDisplay(payment.payment_date) || "-"}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          <div className="flex items-center gap-1.5">
                            <span>
                              {payment.tracking?.substring(0, 15)}
                              {payment.tracking?.length > 15 ? "..." : ""}
                            </span>
                            {payment.is_reship && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-bold uppercase tracking-wider"
                                title="Free reship — no payment expected"
                                data-testid={`reship-badge-${payment.tracking}`}
                              >
                                Reship
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate">
                          {payment.customer || "-"}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          <div className="flex items-center justify-end gap-1.5">
                            {payment.is_reship && (payment.claimed_amount || 0) === 0
                              ? <span className="text-purple-600">Free</span>
                              : <>₹{(payment.claimed_amount || 0).toLocaleString()}</>}
                            {payment.raw_verified && (
                              <span
                                title={`Raw-confirmed: ₹${(payment.raw_match_inr || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} on ${payment.raw_match_date || "-"}${payment.raw_match_source ? ` via ${payment.raw_match_source}` : ""}`}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-300"
                                data-testid={`raw-badge-${payment.payment_id}`}
                              >
                                Raw ✓
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {payment.actual_amount !== undefined &&
                          payment.actual_amount !== null ? (
                            <span className="font-semibold text-green-600">
                              ₹{payment.actual_amount.toLocaleString()}
                            </span>
                          ) : showPreVerifiedDiff ? (
                            <span className="font-semibold text-cyan-600">
                              ₹{(payment.actual_amount || 0).toLocaleString()}
                              <span className="text-xs ml-1">(pre)</span>
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {diff !== null && diff !== 0 ? (
                            <span
                              className={`font-semibold ${
                                diff > 0
                                  ? "text-red-600"
                                  : diff < 0
                                  ? "text-green-600"
                                  : "text-slate-500"
                              }`}
                            >
                              {diff > 0 ? "-" : diff < 0 ? "+" : ""}₹
                              {Math.abs(diff).toLocaleString()}
                            </span>
                          ) : showPreVerifiedDiff ? (
                            <span className="font-semibold text-amber-600">
                              -{payment.difference_reason || "Adj"}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              payment.is_company_payment
                                ? "bg-blue-50 text-blue-700 border-blue-200"
                                : "bg-amber-50 text-amber-700 border-amber-200"
                            }
                          >
                            {payment.payment_mode || "Unknown"}
                          </Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(payment.verification_status, payment.source)}</TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openVerifyDialog(payment)}
                            className="gap-1"
                          >
                            {payment.verification_status === "pending" ? (
                              <>
                                <CheckSquare className="w-4 h-4" />
                                Verify
                              </>
                            ) : payment.verification_status === "verified_at_upload" ? (
                              <>
                                <CheckCircle className="w-4 h-4 text-cyan-600" />
                                Finalize
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4" />
                                View
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Totals Row */}
                  {filteredPayments.length > 0 && (
                    <TableRow className="bg-slate-100 border-t-2 border-slate-300 font-bold" data-testid="verification-totals-row">
                      <TableCell></TableCell>
                      <TableCell className="font-bold text-sm text-slate-800">TOTAL ({filteredPaymentsTotals.count})</TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right font-bold text-sm text-slate-800" data-testid="totals-claimed">
                        ₹{filteredPaymentsTotals.totalClaimed.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-sm text-green-700" data-testid="totals-received">
                        {filteredPaymentsTotals.totalReceived > 0 ? `₹${filteredPaymentsTotals.totalReceived.toLocaleString()}` : "-"}
                      </TableCell>
                      <TableCell className="text-right font-bold text-sm" data-testid="totals-diff">
                        {filteredPaymentsTotals.totalDiff !== 0 ? (
                          <span className={filteredPaymentsTotals.totalDiff > 0 ? "text-red-600" : "text-green-600"}>
                            {filteredPaymentsTotals.totalDiff > 0 ? "-" : "+"}₹{Math.abs(filteredPaymentsTotals.totalDiff).toLocaleString()}
                          </span>
                        ) : "-"}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
          {/* Pending Tab Pagination */}
          <PaginationControls
            currentPage={safePendingPage}
            totalPages={pendingTotalPages}
            totalRecords={filteredPayments.length}
            onPageChange={setPendingPage}
            testIdPrefix="pending"
          />
        </CardContent>
      </Card>

        </TabsContent>

        {/* Tab 2: Verified Done */}
        <TabsContent value="verified-done" className="space-y-4 lg:space-y-6" data-testid="verified-done-tab-content">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 lg:gap-4">
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-3 lg:p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-[10px] lg:text-sm text-slate-500">Fully Verified</p>
                    <p className="text-lg lg:text-2xl font-bold text-green-600" data-testid="verified-full-count">{verifiedDoneSummary.fullCount}</p>
                  </div>
                  <CheckCircle className="w-6 h-6 lg:w-8 lg:h-8 text-green-200 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-3 lg:p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-[10px] lg:text-sm text-slate-500">Adjusted</p>
                    <p className="text-lg lg:text-2xl font-bold text-blue-600" data-testid="verified-adjusted-count">{verifiedDoneSummary.adjustedCount}</p>
                  </div>
                  <AlertTriangle className="w-6 h-6 lg:w-8 lg:h-8 text-blue-200 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 lg:p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-[10px] lg:text-sm text-slate-500">Total Records</p>
                    <p className="text-lg lg:text-2xl font-bold text-slate-700" data-testid="verified-total-count">{verifiedDoneSummary.totalRecords}</p>
                  </div>
                  <FileCheck className="w-6 h-6 lg:w-8 lg:h-8 text-slate-200 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 lg:p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-[10px] lg:text-sm text-slate-500">Total Verified</p>
                    <p className="text-lg lg:text-xl font-bold text-emerald-600 truncate" data-testid="verified-total-amount">₹{verifiedDoneSummary.totalReceived.toLocaleString()}</p>
                  </div>
                  <Banknote className="w-6 h-6 lg:w-8 lg:h-8 text-emerald-200 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters & Search */}
          <Card>
            <CardContent className="p-3 lg:p-4">
              {/* Mobile collapsible */}
              <div className="lg:hidden">
                <button
                  onClick={() => setVerifiedFiltersExpanded(!verifiedFiltersExpanded)}
                  className="w-full flex items-center justify-between p-2 -m-2 rounded-lg hover:bg-slate-50 active:bg-slate-100 transition-colors"
                  data-testid="verified-filter-toggle-btn"
                >
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4 text-slate-500" />
                    <span className="text-xs font-medium text-slate-700">Search & Filters</span>
                    {verifiedActiveFilterCount > 0 && (
                      <span className="bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                        {verifiedActiveFilterCount}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{verifiedDonePayments.length} records</span>
                    {verifiedFiltersExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-200 ease-in-out ${verifiedFiltersExpanded ? 'max-h-[400px] opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
                  <div className="space-y-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input placeholder="Search tracking, partner..." value={verifiedSearchQuery} onChange={(e) => setVerifiedSearchQuery(e.target.value)} className="pl-10 h-9 text-xs" data-testid="verified-search-mobile" />
                    </div>
                    <Select value={verifiedClientFilter} onValueChange={setVerifiedClientFilter}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Partner" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Partners</SelectItem>
                        {clients.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="grid grid-cols-2 gap-2">
                      <DMYDateInput value={verifiedDateFrom} onChange={(e) => setVerifiedDateFrom(e.target.value)} className="h-9 text-xs" data-testid="verified-date-from-mobile" />
                      <DMYDateInput value={verifiedDateTo} onChange={(e) => setVerifiedDateTo(e.target.value)} className="h-9 text-xs" data-testid="verified-date-to-mobile" />
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Button variant="outline" size="sm" onClick={selectAllVerified} className="h-8 text-xs flex-1">
                        Select All
                      </Button>
                      {verifiedSelectedPayments.length > 0 && (
                        <Button onClick={() => setDeleteSelectedDialog(true)} variant="destructive" className="gap-2 h-8 text-xs flex-1" data-testid="verified-delete-mobile-btn">
                          <Trash2 className="w-3 h-3" />
                          Delete ({verifiedSelectedPayments.length})
                        </Button>
                      )}
                    </div>
                    {verifiedActiveFilterCount > 0 && (
                      <button onClick={() => { setVerifiedClientFilter("all"); setVerifiedSearchQuery(""); setVerifiedDateFrom(""); setVerifiedDateTo(""); }} className="w-full text-xs text-green-600 hover:text-green-700 font-medium py-1">Clear all filters</button>
                    )}
                  </div>
                </div>
              </div>

              {/* Desktop */}
              <div className="hidden lg:flex lg:flex-col lg:gap-3">
                <div className="flex items-center gap-4">
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Search tracking, partner..." value={verifiedSearchQuery} onChange={(e) => setVerifiedSearchQuery(e.target.value)} className="pl-10 h-10" data-testid="verified-search-desktop" />
                  </div>
                  <Select value={verifiedClientFilter} onValueChange={setVerifiedClientFilter}>
                    <SelectTrigger className="w-[150px] h-10"><SelectValue placeholder="Partner" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Partners</SelectItem>
                      {clients.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-500 whitespace-nowrap">From:</span>
                    <DMYDateInput value={verifiedDateFrom} onChange={(e) => setVerifiedDateFrom(e.target.value)} className="w-[150px] h-10" data-testid="verified-date-from-desktop" />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-500 whitespace-nowrap">To:</span>
                    <DMYDateInput value={verifiedDateTo} onChange={(e) => setVerifiedDateTo(e.target.value)} className="w-[150px] h-10" data-testid="verified-date-to-desktop" />
                  </div>
                  {(verifiedDateFrom || verifiedDateTo) && (
                    <Button variant="ghost" size="sm" onClick={() => { setVerifiedDateFrom(""); setVerifiedDateTo(""); }} className="h-10 px-2 text-xs text-slate-500 hover:text-slate-700" data-testid="verified-clear-date-filter">
                      <XCircle className="w-4 h-4" />
                    </Button>
                  )}
                  <div className="flex items-center gap-2 ml-auto">
                    <Button variant="outline" size="sm" onClick={selectAllVerified} className="h-9 text-sm" data-testid="verified-select-all-btn">
                      Select All
                    </Button>
                    {verifiedSelectedPayments.length > 0 && (
                      <Button onClick={() => setDeleteSelectedDialog(true)} variant="destructive" className="gap-2 h-9 text-sm" data-testid="verified-delete-desktop-btn">
                        <Trash2 className="w-4 h-4" />
                        Delete ({verifiedSelectedPayments.length})
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Verified Payments Table */}
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-lg flex items-center justify-between flex-wrap gap-2">
                <span data-testid="verified-records-count">Verified Records ({verifiedDonePayments.length})</span>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Payment Mode dropdown on Verified Done tab */}
                  {paymentModeOptions.length > 0 && (
                    <select
                      value={verifiedModeFilter}
                      onChange={(e) => setVerifiedModeFilter(e.target.value)}
                      className={`h-7 rounded-full text-xs font-semibold border px-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 ${
                        verifiedModeFilter === "all"
                          ? "bg-white text-slate-700 border-slate-200"
                          : "bg-slate-800 text-white border-slate-800"
                      }`}
                      title="Filter by payment mode"
                      data-testid="verified-mode-filter-select"
                    >
                      <option value="all">Payment Mode</option>
                      {paymentModeOptions.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  )}
                  {/* Raw ✓ pill on Verified Done */}
                  {(() => {
                    const rawCount = payments.filter(p =>
                      p.raw_verified && !p.is_reship && !p.is_zero_payment &&
                      (p.verification_status === "verified_full" || p.verification_status === "verified_adjusted")
                    ).length;
                    if (rawCount === 0 && !verifiedRawVerifiedOnly) return null;
                    return (
                      <button
                        type="button"
                        onClick={() => setVerifiedRawVerifiedOnly(v => !v)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                          verifiedRawVerifiedOnly
                            ? "bg-emerald-600 text-white hover:bg-emerald-700"
                            : "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                        }`}
                        title="Auto-confirmed by accounting Raw Funds (same client · ₹±50 · ±7 days)"
                        data-testid="verified-raw-verified-pill"
                      >
                        <span>Raw ✓</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${verifiedRawVerifiedOnly ? "bg-white/20" : "bg-emerald-100"}`}>
                          {rawCount}
                        </span>
                      </button>
                    );
                  })()}
                  {(verifiedModeFilter !== "all" || verifiedRawVerifiedOnly) && (
                    <Button variant="ghost" size="sm" onClick={() => { setVerifiedModeFilter("all"); setVerifiedRawVerifiedOnly(false); }} data-testid="verified-clear-filters-btn">
                      Clear
                    </Button>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : verifiedDonePayments.length === 0 ? (
                <div className="text-center py-12 text-slate-500" data-testid="verified-empty-state">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No verified payments found</p>
                  <p className="text-sm">Payments will appear here after verification</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="verified-payments-table">
                    <TableHeader>
                      <TableRow className="bg-green-50/50">
                        <TableHead className="w-10">
                          <Checkbox
                            checked={verifiedSelectedPayments.length > 0 && verifiedSelectedPayments.length === verifiedDonePayments.length}
                            onCheckedChange={(checked) => { checked ? selectAllVerified() : setVerifiedSelectedPayments([]); }}
                            data-testid="verified-select-all-checkbox"
                          />
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleVerifiedTableSort('client_code')}>
                          <div className="flex items-center">Partner<VerifiedSortIcon columnKey="client_code" /></div>
                        </TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleVerifiedTableSort('payment_date')}>
                          <div className="flex items-center">Date<VerifiedSortIcon columnKey="payment_date" /></div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleVerifiedTableSort('tracking')}>
                          <div className="flex items-center">Tracking<VerifiedSortIcon columnKey="tracking" /></div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleVerifiedTableSort('customer')}>
                          <div className="flex items-center">Customer<VerifiedSortIcon columnKey="customer" /></div>
                        </TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-slate-100" onClick={() => handleVerifiedTableSort('claimed_amount')}>
                          <div className="flex items-center justify-end">Claimed<VerifiedSortIcon columnKey="claimed_amount" /></div>
                        </TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-slate-100" onClick={() => handleVerifiedTableSort('actual_amount')}>
                          <div className="flex items-center justify-end">Received<VerifiedSortIcon columnKey="actual_amount" /></div>
                        </TableHead>
                        <TableHead className="text-right">Diff</TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleVerifiedTableSort('payment_mode')}>
                          <div className="flex items-center">Mode<VerifiedSortIcon columnKey="payment_mode" /></div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleVerifiedTableSort('verification_status')}>
                          <div className="flex items-center">Status<VerifiedSortIcon columnKey="verification_status" /></div>
                        </TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedVerifiedPayments.map((payment, idx) => {
                        const isSelected = verifiedSelectedPayments.some((p) => p.payment_id === payment.payment_id);
                        const diff = (payment.claimed_amount || 0) - (payment.actual_amount || payment.claimed_amount || 0);
                        return (
                          <TableRow
                            key={payment.payment_id || idx}
                            className={isSelected ? "bg-green-50" : ""}
                            data-testid={`verified-row-${payment.payment_id}`}
                          >
                            <TableCell>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleVerifiedPaymentSelection(payment)}
                              />
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-mono">{payment.client_code}</Badge>
                            </TableCell>
                            <TableCell>{getSourceBadge(payment.source)}</TableCell>
                            <TableCell className="text-slate-600">
                              <div className="flex items-center gap-1.5">
                                <DateSourceDot source={payment.date_source} />
                                <span>{payment.payment_date_display || formatDateDashDisplay(payment.payment_date) || "-"}</span>
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              <div className="flex items-center gap-1.5">
                                <span>
                                  {payment.tracking?.substring(0, 15)}
                                  {payment.tracking?.length > 15 ? "..." : ""}
                                </span>
                                {payment.is_reship && (
                                  <span
                                    className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-bold uppercase tracking-wider"
                                    title="Free reship — no payment expected"
                                    data-testid={`verified-reship-badge-${payment.tracking}`}
                                  >
                                    Reship
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="max-w-[150px] truncate">{payment.customer || "-"}</TableCell>
                            <TableCell className="text-right font-semibold">
                              <div className="flex items-center justify-end gap-1.5">
                                {payment.is_reship && (payment.claimed_amount || 0) === 0
                                  ? <span className="text-purple-600">Free</span>
                                  : <>₹{(payment.claimed_amount || 0).toLocaleString()}</>}
                                {payment.raw_verified && (
                                  <span
                                    title={`Raw-confirmed: ₹${(payment.raw_match_inr || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} on ${payment.raw_match_date || "-"}${payment.raw_match_source ? ` via ${payment.raw_match_source}` : ""}`}
                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-300"
                                  >
                                    Raw ✓
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {payment.actual_amount ? `₹${payment.actual_amount.toLocaleString()}` : "-"}
                            </TableCell>
                            <TableCell className="text-right">
                              {diff !== 0 ? (
                                <span className={diff > 0 ? "text-red-600 font-medium" : "text-green-600 font-medium"}>
                                  {diff > 0 ? "-" : "+"}₹{Math.abs(diff).toLocaleString()}
                                </span>
                              ) : <span className="text-slate-400">-</span>}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{payment.payment_mode || "-"}</Badge>
                            </TableCell>
                            <TableCell>{getStatusBadge(payment.verification_status, payment.source)}</TableCell>
                            <TableCell className="text-center">
                              <Button variant="ghost" size="sm" onClick={() => openVerifyDialog(payment)} data-testid={`view-verified-${payment.payment_id}`}>
                                <Eye className="w-4 h-4 text-slate-400" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Totals Row */}
                      {verifiedDonePayments.length > 0 && (
                        <TableRow className="bg-slate-100 border-t-2 border-slate-300 font-semibold">
                          <TableCell></TableCell>
                          <TableCell colSpan={5} className="text-slate-700">
                            Total ({verifiedDonePayments.length} records)
                          </TableCell>
                          <TableCell className="text-right font-bold text-slate-800">
                            ₹{verifiedDoneSummary.totalClaimed.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-bold text-slate-800">
                            ₹{verifiedDoneSummary.totalReceived.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-bold">
                            {(verifiedDoneSummary.totalClaimed - verifiedDoneSummary.totalReceived) !== 0 ? (
                              <span className={(verifiedDoneSummary.totalClaimed - verifiedDoneSummary.totalReceived) > 0 ? "text-red-600" : "text-green-600"}>
                                {(verifiedDoneSummary.totalClaimed - verifiedDoneSummary.totalReceived) > 0 ? "-" : "+"}₹{Math.abs(verifiedDoneSummary.totalClaimed - verifiedDoneSummary.totalReceived).toLocaleString()}
                              </span>
                            ) : <span className="text-slate-400">-</span>}
                          </TableCell>
                          <TableCell colSpan={3}></TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
              {/* Verified Tab Pagination */}
              <PaginationControls
                currentPage={safeVerifiedPage}
                totalPages={verifiedTotalPages}
                totalRecords={verifiedDonePayments.length}
                onPageChange={setVerifiedPage}
                testIdPrefix="verified"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 2.5: FOC (Free-of-Cost) — Verified Reship-Free & Zero-Payment parcels */}
        <TabsContent value="foc" className="space-y-4 lg:space-y-6" data-testid="foc-tab-content">
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-lg flex items-center justify-between flex-wrap gap-2">
                <span data-testid="foc-records-count">FOC Records ({focPayments.length})</span>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Bulk close pending Zero-Payment items in one click */}
                  {(() => {
                    const pendingZeroCount = payments.filter(x =>
                      x.is_zero_payment && !x.is_reship &&
                      (x.verification_status || "pending") === "pending"
                    ).length;
                    if (pendingZeroCount === 0) return null;
                    return (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          if (!window.confirm(
                            `Mark ${pendingZeroCount} pending Zero-Payment parcel(s) as "No Payment Received"?\n\nThis closes them and moves them into this FOC tab. Cannot be undone in bulk.`
                          )) return;
                          try {
                            const res = await fetch(`${BACKEND_URL}/api/payment-verification/foc/bulk-close-zero`, {
                              method: "POST",
                              headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                              body: JSON.stringify({}),
                            });
                            if (!res.ok) throw new Error((await res.json()).detail || `HTTP ${res.status}`);
                            const data = await res.json();
                            toast.success(`Closed ${data.closed} zero-payment parcel(s) as "No Payment Received".`);
                            fetchPayments();
                          } catch (e) {
                            toast.error(e.message || "Bulk close failed");
                          }
                        }}
                        className="bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100"
                        data-testid="foc-bulk-close-zero-btn"
                      >
                        Mark {pendingZeroCount} as No-Payment Received
                      </Button>
                    );
                  })()}
                  <select
                    value={focTypeFilter}
                    onChange={(e) => setFocTypeFilter(e.target.value)}
                    className={`h-7 rounded-full text-xs font-semibold border px-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 ${
                      focTypeFilter === "all" ? "bg-white text-slate-700 border-slate-200" : "bg-slate-800 text-white border-slate-800"
                    }`}
                    data-testid="foc-type-filter"
                  >
                    <option value="all">All FOC ({payments.filter(p => (p.is_reship || p.is_zero_payment) && (p.verification_status === "verified_full" || p.verification_status === "verified_adjusted")).length})</option>
                    <option value="reship">Reship Free ({payments.filter(p => p.is_reship && (p.verification_status === "verified_full" || p.verification_status === "verified_adjusted")).length})</option>
                    <option value="zero">Zero Payment ({payments.filter(p => p.is_zero_payment && (p.verification_status === "verified_full" || p.verification_status === "verified_adjusted")).length})</option>
                  </select>
                  {paymentModeOptions.length > 0 && (
                    <select
                      value={focModeFilter}
                      onChange={(e) => setFocModeFilter(e.target.value)}
                      className={`h-7 rounded-full text-xs font-semibold border px-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 ${
                        focModeFilter === "all" ? "bg-white text-slate-700 border-slate-200" : "bg-slate-800 text-white border-slate-800"
                      }`}
                      data-testid="foc-mode-filter"
                    >
                      <option value="all">Payment Mode</option>
                      {paymentModeOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  )}
                  <Input
                    placeholder="Search tracking / customer / partner…"
                    value={focSearchQuery}
                    onChange={(e) => setFocSearchQuery(e.target.value)}
                    className="h-8 w-64 text-xs"
                    data-testid="foc-search-input"
                  />
                  {(focTypeFilter !== "all" || focModeFilter !== "all" || focSearchQuery || focClientFilter !== "all") && (
                    <Button variant="ghost" size="sm" onClick={() => { setFocTypeFilter("all"); setFocModeFilter("all"); setFocSearchQuery(""); setFocClientFilter("all"); }} data-testid="foc-clear-btn">
                      Clear
                    </Button>
                  )}
                </div>
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Verified parcels that were <span className="font-semibold text-purple-700">Reship-Free</span> or <span className="font-semibold text-amber-700">Zero-Payment</span> at the partner ledger stage. They live here (not in Verified Done) because no rupee value moved.
              </p>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : focPayments.length === 0 ? (
                <div className="text-center py-12 text-slate-500" data-testid="foc-empty-state">
                  <Gift className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No FOC records</p>
                  <p className="text-sm">Verified Reship-Free and Zero-Payment parcels will appear here.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="foc-payments-table">
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Partner</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Tracking</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Mode</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {focPayments.map((p) => (
                        <TableRow key={p.payment_id} data-testid={`foc-row-${p.payment_id}`}>
                          <TableCell className="font-medium">{p.client_code}</TableCell>
                          <TableCell>{p.payment_date_display || p.payment_date}</TableCell>
                          <TableCell className="font-mono text-xs">{p.tracking}</TableCell>
                          <TableCell className="max-w-[220px] truncate" title={p.customer}>{p.customer || "—"}</TableCell>
                          <TableCell>
                            {p.is_reship ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-700 border border-purple-200">
                                <Repeat className="w-3 h-3" /> Reship
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200">
                                ₹0 Zero
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-600">{p.payment_mode || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 3: Pending Sales Report */}
        <TabsContent value="pending-sales" className="space-y-6">
          {/* Report Header */}
          <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/20">
                    <Users className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-amber-800">Pending Sales Report</h2>
                    <p className="text-sm text-amber-600">Partners & Agents with shipments but no sales uploads - share with partners</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => fetchPendingSalesReport()} disabled={reportsLoading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${reportsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Select value={reportExportMode} onValueChange={setReportExportMode}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single File</SelectItem>
                      <SelectItem value="partner-wise">Partner-wise</SelectItem>
                    </SelectContent>
                  </Select>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-2" />
                        Export
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => reportExportMode === "single" ? exportPendingSalesCSV() : getFilteredPartners().forEach(p => exportPendingSalesCSV(p.client_code))}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Export CSV
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => reportExportMode === "single" ? exportPendingSalesPDF() : getFilteredPartners().forEach(p => exportPendingSalesPDF(p.client_code))}>
                        <FileDown className="w-4 h-4 mr-2" />
                        Export PDF
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handlePrintReport}>
                        <Printer className="w-4 h-4 mr-2" />
                        Print
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              
              {/* Filters */}
              <div className="mt-4 flex items-center gap-4 flex-wrap">
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => applyDateFilter("all")} className={!reportDateFrom && !reportDateTo ? "bg-amber-100" : ""}>All Time</Button>
                  <Button variant="outline" size="sm" onClick={() => applyDateFilter("30days")}>Last 30 Days</Button>
                  <Button variant="outline" size="sm" onClick={() => applyDateFilter("thisMonth")}>This Month</Button>
                </div>
                <div className="flex items-center gap-2">
                  <DMYDateInput value={reportDateFrom} onChange={(e) => setReportDateFrom(e.target.value)} className="w-36" data-testid="pending-date-from" />
                  <span className="text-sm">to</span>
                  <DMYDateInput value={reportDateTo} onChange={(e) => setReportDateTo(e.target.value)} className="w-36" data-testid="pending-date-to" />
                  <Button variant="default" size="sm" onClick={() => fetchPendingSalesReport()} disabled={reportsLoading} className="bg-amber-600 hover:bg-amber-700 text-white gap-1" data-testid="pending-load-btn">
                    {reportsLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    Load
                  </Button>
                  {(reportDateFrom || reportDateTo) && (
                    <Button variant="ghost" size="sm" onClick={() => { setReportDateFrom(""); setReportDateTo(""); fetchPendingSalesReport("", ""); }} className="px-2 text-xs text-slate-500 hover:text-slate-700" data-testid="pending-clear-dates">
                      <XCircle className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-600" />
                  <p className="text-xs font-medium text-amber-600 uppercase">Pending Partners</p>
                </div>
                <p className="text-3xl font-bold text-amber-700 mt-2">{pendingSalesData?.summary?.total_pending_partners || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-600" />
                  <p className="text-xs font-medium text-muted-foreground uppercase">Pending Orders</p>
                </div>
                <p className="text-3xl font-bold mt-2">{(pendingSalesData?.summary?.total_pending_orders || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-red-600" />
                  <p className="text-xs font-medium text-muted-foreground uppercase">Pending Value</p>
                </div>
                <p className="text-3xl font-bold mt-2">₹{(pendingSalesData?.summary?.total_pending_value || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>

          {/* Search */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by partner code..." value={reportSearchTerm} onChange={(e) => setReportSearchTerm(e.target.value)} className="pl-10" />
            </div>
            <Badge variant="outline">{getFilteredPartners().length} partners</Badge>
          </div>

          {/* Partners Table */}
          <Card>
            <CardContent className="p-0">
              {reportsLoading ? (
                <div className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-500" /></div>
              ) : getFilteredPartners().length === 0 ? (
                <div className="p-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-medium text-muted-foreground">No Pending Sales Found</h3>
                  <p className="text-sm text-muted-foreground mt-1">All partners have submitted their sales data.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-amber-50">
                      <TableHead className="w-10"></TableHead>
                      <TableHead className="cursor-pointer" onClick={() => handleReportSort('client_code')}>
                        <div className="flex items-center">Partner Code<SortIcon columnKey="client_code" /></div>
                      </TableHead>
                      <TableHead className="cursor-pointer" onClick={() => handleReportSort('total_orders')}>
                        <div className="flex items-center">Orders<SortIcon columnKey="total_orders" /></div>
                      </TableHead>
                      <TableHead className="cursor-pointer" onClick={() => handleReportSort('total_value')}>
                        <div className="flex items-center">Value<SortIcon columnKey="total_value" /></div>
                      </TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getFilteredPartners().map((partner) => (
                      <Collapsible key={partner.client_code} asChild>
                        <>
                          <TableRow className="hover:bg-amber-50/50 cursor-pointer" onClick={() => togglePartner(partner.client_code)}>
                            <TableCell>
                              <Button variant="ghost" size="sm" className="p-0 h-6 w-6">
                                {expandedPartners.has(partner.client_code) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </Button>
                            </TableCell>
                            <TableCell className="font-medium">{partner.client_code}</TableCell>
                            <TableCell><Badge variant="outline" className="bg-amber-50">{partner.total_orders}</Badge></TableCell>
                            <TableCell className="font-medium">₹{partner.total_value?.toLocaleString()}</TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="outline" size="sm" onClick={(e) => e.stopPropagation()} data-testid={`pending-sales-export-btn-${partner.client_code}`}>
                                    <Download className="w-3 h-3 mr-1" />Export
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-72">
                                  {/* View on Screen — opens interactive report in new tab */}
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); openClientReport("pending-sales", partner.client_code); }}
                                    data-testid={`pending-sales-view-${partner.client_code}`}
                                  >
                                    <Eye className="w-4 h-4 mr-2 text-blue-600" />View on Screen (New Tab)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportPendingSalesCSV(partner.client_code); }}>
                                    <FileSpreadsheet className="w-4 h-4 mr-2" />CSV
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportPartnerLedgerTemplate(partner.client_code); }} data-testid={`export-ledger-pending-${partner.client_code}`}>
                                    <FileDown className="w-4 h-4 mr-2" />Ledger Template (Pending only)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportPartnerLedgerTemplate(partner.client_code, { includeAll: true }); }} data-testid={`export-ledger-all-${partner.client_code}`}>
                                    <FileDown className="w-4 h-4 mr-2 text-rose-500" />Ledger Template (All parcels · pink = submitted)
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportPendingSalesPDF(partner.client_code); }}>
                                    <FileDown className="w-4 h-4 mr-2" />PDF
                                  </DropdownMenuItem>

                                  {/* Sales-health reports (from Ledger Reconciliation → Account view) */}
                                  <DropdownMenuSeparator />
                                  <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                    Sales-health reports
                                  </div>
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); openClientReport("negative-gp", partner.client_code); }}
                                    data-testid={`negative-gp-view-${partner.client_code}`}
                                  >
                                    <TrendingDown className="w-4 h-4 mr-2 text-red-600" />
                                    <span className="flex-1">Negative GP Report</span>
                                    <span className="text-[10px] text-slate-400 ml-2">View</span>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); downloadAccountReportCSV("negative-gp", partner.client_code); }}
                                    data-testid={`negative-gp-csv-${partner.client_code}`}
                                  >
                                    <FileSpreadsheet className="w-4 h-4 mr-2 text-red-500" />
                                    <span className="flex-1">Negative GP Report</span>
                                    <span className="text-[10px] text-slate-400 ml-2">CSV</span>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); openClientReport("no-invoice", partner.client_code); }}
                                    data-testid={`no-invoice-view-${partner.client_code}`}
                                  >
                                    <FileWarning className="w-4 h-4 mr-2 text-orange-600" />
                                    <span className="flex-1">No Invoice Report</span>
                                    <span className="text-[10px] text-slate-400 ml-2">View</span>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); downloadAccountReportCSV("no-invoice", partner.client_code); }}
                                    data-testid={`no-invoice-csv-${partner.client_code}`}
                                  >
                                    <FileSpreadsheet className="w-4 h-4 mr-2 text-orange-500" />
                                    <span className="flex-1">No Invoice Report</span>
                                    <span className="text-[10px] text-slate-400 ml-2">CSV</span>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={(e) => { e.stopPropagation(); openClientReport("reship-audit", partner.client_code); }}
                                    data-testid={`reship-audit-view-${partner.client_code}`}
                                  >
                                    <Repeat className="w-4 h-4 mr-2 text-purple-600" />
                                    <span className="flex-1">Reship Audit Trail</span>
                                    <span className="text-[10px] text-slate-400 ml-2">View</span>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                          <CollapsibleContent asChild>
                            <TableRow>
                              <TableCell colSpan={5} className="p-0 bg-slate-50">
                                <div className="p-4">
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="text-xs">
                                        <TableHead>Tracking</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Product</TableHead>
                                        <TableHead>Qty</TableHead>
                                        <TableHead>Service</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {partner.orders?.slice(0, 15).map((order, idx) => (
                                        <TableRow key={idx} className="text-xs">
                                          <TableCell className="font-mono">{order.tracking || '-'}</TableCell>
                                          <TableCell>{formatDateDashDisplay(order.order_date) || '-'}</TableCell>
                                          <TableCell className="max-w-[150px] truncate">{order.product || '-'}</TableCell>
                                          <TableCell>{order.qty || '-'}</TableCell>
                                          <TableCell><Badge variant="outline" className="text-xs">{order.service || '-'}</Badge></TableCell>
                                        </TableRow>
                                      ))}
                                      {partner.orders?.length > 15 && (
                                        <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">...and {partner.orders.length - 15} more</TableCell></TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                </div>
                              </TableCell>
                            </TableRow>
                          </CollapsibleContent>
                        </>
                      </Collapsible>
                    ))}
                    {/* Totals Row */}
                    {getFilteredPartners().length > 0 && (() => {
                      const fp = getFilteredPartners();
                      const totalOrders = fp.reduce((s, p) => s + (p.total_orders || 0), 0);
                      const totalValue = fp.reduce((s, p) => s + (p.total_value || 0), 0);
                      return (
                        <TableRow className="bg-amber-100/70 border-t-2 border-amber-300 font-semibold" data-testid="pending-totals-row">
                          <TableCell></TableCell>
                          <TableCell className="text-amber-900">
                            Total ({fp.length} partners)
                          </TableCell>
                          <TableCell className="font-bold text-amber-900">{totalOrders.toLocaleString()}</TableCell>
                          <TableCell className="font-bold text-amber-900">₹{totalValue.toLocaleString()}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Follow Up Payment Recovery */}
        <TabsContent value="follow-up" className="space-y-6" data-testid="follow-up-tab-content">
          {/* Header */}
          <Card className="border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-teal-500/20">
                    <Calendar className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-teal-800">Payment Follow Up Tracker</h2>
                    <p className="text-sm text-teal-600">Track payment recovery follow-ups with partners month by month</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={fetchFollowUpData} disabled={followUpLoading} data-testid="follow-up-refresh-btn">
                    <RefreshCw className={`w-4 h-4 mr-2 ${followUpLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Filters row */}
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                {/* Year Filter */}
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium text-teal-700">Year:</Label>
                  <Select value={String(followUpYear)} onValueChange={(v) => setFollowUpYear(Number(v))}>
                    <SelectTrigger className="w-24" data-testid="follow-up-year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Month Filter */}
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium text-teal-700">Month:</Label>
                  <Select value={followUpMonthFilter} onValueChange={setFollowUpMonthFilter}>
                    <SelectTrigger className="w-28" data-testid="follow-up-month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {MONTH_NAMES.map((m, i) => (
                        <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Multi-select Partner Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="min-w-[180px] justify-between" data-testid="follow-up-partner-dropdown">
                      <span className="truncate">
                        {followUpSelectedPartners.length === 0
                          ? "All Partners"
                          : `${followUpSelectedPartners.length} selected`}
                      </span>
                      <ChevronDown className="w-3 h-3 ml-2 flex-shrink-0" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 max-h-72 overflow-y-auto">
                    <DropdownMenuItem
                      onSelect={(e) => { e.preventDefault(); setFollowUpSelectedPartners([]); }}
                      className="font-medium text-teal-700"
                    >
                      <CheckSquare className={`w-4 h-4 mr-2 ${followUpSelectedPartners.length === 0 ? 'text-teal-600' : 'opacity-30'}`} />
                      Show All
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {followUpPartners.map(p => {
                      const isSelected = followUpSelectedPartners.includes(p.code);
                      return (
                        <DropdownMenuItem
                          key={p.code}
                          onSelect={(e) => {
                            e.preventDefault();
                            setFollowUpSelectedPartners(prev =>
                              isSelected ? prev.filter(c => c !== p.code) : [...prev, p.code]
                            );
                          }}
                        >
                          <Checkbox checked={isSelected} className="mr-2 pointer-events-none" />
                          <span className="truncate">{p.code}</span>
                          <span className="ml-auto text-xs text-muted-foreground truncate">{p.name?.slice(0, 15)}</span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Handle By filter */}
                <Select value={followUpHandlerFilter} onValueChange={setFollowUpHandlerFilter}>
                  <SelectTrigger className="h-9 w-[160px]" data-testid="follow-up-handler-filter">
                    <SelectValue placeholder="Handle By" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Handlers</SelectItem>
                    <SelectItem value="__unassigned">Unassigned</SelectItem>
                    {[...new Set(Object.values(followUpHandlers))].filter(Boolean).sort().map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Inactive (no-sale) partners peek toggle */}
                {followUpHiddenCount > 0 && (
                  <Button
                    variant={showInactiveFollowUp ? "default" : "outline"}
                    size="sm"
                    className={`h-9 text-xs gap-1.5 ${showInactiveFollowUp ? "bg-slate-700 hover:bg-slate-800" : "text-slate-500 border-dashed"}`}
                    onClick={() => setShowInactiveFollowUp(v => !v)}
                    title="Partners with no sales in this year (closed dealing / moved)"
                    data-testid="follow-up-inactive-toggle"
                  >
                    {showInactiveFollowUp ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    {showInactiveFollowUp ? `Hide ${followUpHiddenCount} inactive` : `${followUpHiddenCount} hidden (no sales)`}
                  </Button>
                )}
              </div>

              {/* Status Summary Cards */}
              <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
                {[
                  { key: "all", label: "All", count: followUpStats.total, color: "bg-slate-100 text-slate-700 border-slate-200", activeColor: "bg-slate-700 text-white" },
                  { key: "not-sent", label: "Not Sent", count: followUpStats.notSent, color: "bg-gray-50 text-gray-600 border-gray-200", activeColor: "bg-gray-600 text-white" },
                  { key: "sent-pending", label: "Sent, Awaiting", count: followUpStats.sent, color: "bg-blue-50 text-blue-700 border-blue-200", activeColor: "bg-blue-600 text-white" },
                  { key: "followup-1", label: "Follow Up 1", count: followUpStats.followUp1, color: "bg-amber-50 text-amber-700 border-amber-200", activeColor: "bg-amber-600 text-white" },
                  { key: "mgmt-followup", label: "Mgmt Follow Up", count: followUpStats.mgmtFollowUp, color: "bg-purple-50 text-purple-700 border-purple-200", activeColor: "bg-purple-600 text-white" },
                  { key: "no-response", label: "No Response", count: followUpStats.noResponse, color: "bg-red-50 text-red-700 border-red-200", activeColor: "bg-red-600 text-white" },
                  { key: "received-data", label: "Received Data", count: followUpStats.receivedData, color: "bg-green-50 text-green-800 border-green-300", activeColor: "bg-green-700 text-white" },
                  { key: "balance-open", label: "Balance / Open", count: followUpStats.balanceOpen, color: "bg-rose-50 text-rose-700 border-rose-200", activeColor: "bg-rose-600 text-white" },
                  { key: "in-progress", label: "In Progress", count: followUpStats.total - followUpStats.settled - followUpStats.notSent, color: "bg-orange-50 text-orange-700 border-orange-200", activeColor: "bg-orange-600 text-white" },
                  { key: "settled", label: "Settled", count: followUpStats.settled, color: "bg-emerald-50 text-emerald-700 border-emerald-200", activeColor: "bg-emerald-600 text-white" },
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={() => setFollowUpStatusFilter(f.key)}
                    className={`rounded-lg border px-3 py-2 text-center transition-all ${
                      followUpStatusFilter === f.key ? f.activeColor + " border-transparent shadow-sm" : f.color + " hover:shadow-sm"
                    }`}
                    data-testid={`fu-filter-${f.key}`}
                  >
                    <div className="text-lg font-bold leading-tight">{Math.max(0, f.count)}</div>
                    <div className="text-[10px] sm:text-xs leading-tight mt-0.5 whitespace-nowrap">{f.label}</div>
                  </button>
                ))}
              </div>

              {/* Active filter indicator */}
              {(followUpStatusFilter !== "all" || followUpMonthFilter !== "all") && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-teal-600 font-medium">Active filters:</span>
                  {followUpStatusFilter !== "all" && (
                    <Badge variant="secondary" className="gap-1 text-xs">
                      Status: {followUpStatusFilter.replace(/-/g, ' ')}
                      <button onClick={() => setFollowUpStatusFilter("all")} className="ml-1 hover:text-red-500">&times;</button>
                    </Badge>
                  )}
                  {followUpMonthFilter !== "all" && (
                    <Badge variant="secondary" className="gap-1 text-xs">
                      Month: {MONTH_NAMES[Number(followUpMonthFilter) - 1]}
                      <button onClick={() => setFollowUpMonthFilter("all")} className="ml-1 hover:text-red-500">&times;</button>
                    </Badge>
                  )}
                  <button onClick={() => { setFollowUpStatusFilter("all"); setFollowUpMonthFilter("all"); }} className="text-xs text-red-500 hover:underline">Clear all</button>
                  <span className="ml-auto text-xs text-teal-600">{followUpFilteredRows.length} rows shown</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Follow Up Table */}
          <Card>
            <CardContent className="p-0">
              {followUpLoading ? (
                <div className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-teal-500" /></div>
              ) : followUpDisplayPartners.length === 0 ? (
                <div className="p-12 text-center">
                  <Calendar className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-medium text-muted-foreground">No Partners Found</h3>
                  <p className="text-sm text-muted-foreground mt-1">Select partners from the dropdown or check if partner ledgers exist.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="follow-up-table">
                    <TableHeader>
                      <TableRow className="bg-teal-50">
                        <TableHead className="sticky left-0 bg-teal-50 z-10 min-w-[90px] text-xs font-bold">Partner</TableHead>
                        <TableHead className="min-w-[90px] text-xs font-bold">Handle By</TableHead>
                        <TableHead className="min-w-[60px] text-xs font-bold text-center">Month</TableHead>
                        <TableHead className="min-w-[100px] text-xs font-bold text-center">Pending Sales Sent</TableHead>
                        <TableHead className="min-w-[100px] text-xs font-bold text-center">Follow Up 1</TableHead>
                        <TableHead className="min-w-[100px] text-xs font-bold text-center">Follow Up 2</TableHead>
                        <TableHead className="min-w-[100px] text-xs font-bold text-center">Mgmt Follow Up 1</TableHead>
                        <TableHead className="min-w-[100px] text-xs font-bold text-center">Mgmt Follow Up 2</TableHead>
                        <TableHead className="min-w-[100px] text-xs font-bold text-center bg-emerald-100">Received Data</TableHead>
                        <TableHead className="min-w-[100px] text-xs font-bold text-center bg-emerald-100">A/c Settled</TableHead>
                        <TableHead className="min-w-[110px] text-xs font-bold text-center">Closing Bal.</TableHead>
                        <TableHead className="min-w-[140px] text-xs font-bold text-center">Narration</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.keys(followUpGroupedFiltered).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                            No rows match the current filters
                          </TableCell>
                        </TableRow>
                      ) : Object.entries(followUpGroupedFiltered).map(([partnerCode, rows]) => (
                        <React.Fragment key={partnerCode}>
                          {rows.map((r, rowIdx) => {
                            const month = r.month;
                            const mName = MONTH_NAMES[month - 1];
                            const row = r.row;
                            const hasReceived = r.hasReceived;
                            const hasSettled = r.hasSettled;
                            const isGreen = hasReceived && hasSettled;
                            // Pre-start months (before the partner's first parcel)
                            // are greyed out — follow-up only applies from the
                            // partner's start date onward.
                            const isPreStart = r.isPreStart;
                            const isNoSale = r.isNoSale;
                            const isLocked = isPreStart || isNoSale;
                            const partnerStart = followUpFirstOrders[partnerCode];
                            const lockTitle = isPreStart
                              ? `Before partner start date${partnerStart ? ` (${partnerStart.date})` : ""} — not applicable for follow-up`
                              : isNoSale ? "No sales/orders in this month — locked" : undefined;
                            // Light-pink highlight: data received but not yet settled —
                            // mirrors the green "settled" highlight one step earlier in
                            // the workflow so the team sees follow-ups that have
                            // delivered data and are awaiting account settlement.
                            const isPink = hasReceived && !hasSettled;

                            const DATE_FIELDS = ["pending_sales_sent", "first_followup", "second_followup", "mgmt_followup", "mgmt_followup_2", "received_date", "ac_settled"];

                            const renderDateCell = (field, colIdx) => {
                              const isEditing = editingCell?.partner === partnerCode && editingCell?.month === month && editingCell?.field === field;
                              const val = row[field] || "";
                              const greenCell = !isLocked && isGreen && colIdx <= 7;
                              const pinkCell = !isLocked && !isGreen && isPink && colIdx <= 7;
                              const isReceivedDateCol = field === "received_date";
                              const isAcSettledLockedForTeam = field === "ac_settled" && user?.role !== "super_admin";
                              return (
                                <TableCell
                                  key={field}
                                  title={lockTitle || (isAcSettledLockedForTeam ? "A/c Settled — admin only (view only)" : undefined)}
                                  className={`text-center text-xs select-none transition-colors ${
                                    isLocked ? "bg-slate-200/70 text-slate-400 cursor-not-allowed" :
                                    isAcSettledLockedForTeam ? (greenCell ? "bg-emerald-100 text-emerald-800 cursor-not-allowed" : pinkCell ? "bg-pink-100 text-pink-900 cursor-not-allowed" : "cursor-not-allowed") :
                                    greenCell ? "bg-emerald-100 text-emerald-800 cursor-pointer" : pinkCell ? "bg-pink-100 text-pink-900 cursor-pointer" : "cursor-pointer"
                                  } ${!isLocked && !isAcSettledLockedForTeam && !val && !isEditing ? "hover:bg-teal-50" : ""}`}
                                  onDoubleClick={() => { if (!isLocked) handleFollowUpDoubleClick(partnerCode, month, field); }}
                                  data-testid={`fu-cell-${partnerCode}-${month}-${field}`}
                                >
                                  {isEditing ? (
                                    <Input
                                      autoFocus
                                      value={editingValue}
                                      onChange={(e) => setEditingValue(e.target.value)}
                                      onBlur={commitFollowUpEdit}
                                      onKeyDown={handleFollowUpKeyDown}
                                      className="h-7 text-xs text-center p-1 w-full"
                                    />
                                  ) : (
                                    <span className={
                                      isLocked
                                        ? "text-slate-400 text-[10px] font-medium"
                                        : val
                                        ? (isReceivedDateCol ? "font-bold text-green-800 text-sm" : "font-medium")
                                        : "text-muted-foreground/40"
                                    }>
                                      {isLocked ? (val || "N/A") : (val || "-")}
                                    </span>
                                  )}
                                </TableCell>
                              );
                            };

                            const renderEditableCell = (field) => {
                              const isEditing = editingCell?.partner === partnerCode && editingCell?.month === month && editingCell?.field === field;
                              const val = row[field] || "";
                              const noSaleNarration = isNoSale && field === "narration";
                              return (
                                <TableCell
                                  key={field}
                                  title={lockTitle}
                                  className={`text-center text-xs select-none transition-colors ${
                                    isLocked ? "bg-slate-200/70 text-slate-400 cursor-not-allowed" :
                                    isGreen ? "bg-emerald-100 text-emerald-800 cursor-pointer" : isPink ? "bg-pink-100 text-pink-900 cursor-pointer" : "cursor-pointer"
                                  }`}
                                  onDoubleClick={() => {
                                    if (isLocked) return;
                                    setEditingCell({ partner: partnerCode, month, field });
                                    setEditingValue(val);
                                  }}
                                  data-testid={`fu-cell-${partnerCode}-${month}-${field}`}
                                >
                                  {isEditing ? (
                                    <Input
                                      autoFocus
                                      value={editingValue}
                                      onChange={(e) => setEditingValue(e.target.value)}
                                      onBlur={commitFollowUpEdit}
                                      onKeyDown={handleFollowUpKeyDown}
                                      className="h-7 text-xs text-center p-1 w-full"
                                    />
                                  ) : (
                                    <span className={isLocked ? "text-slate-400 text-[10px] font-medium" : val ? "font-medium" : "text-muted-foreground/40"}>
                                      {noSaleNarration ? (val || "no sale for this month") : isLocked ? (val || "N/A") : (val || "-")}
                                    </span>
                                  )}
                                </TableCell>
                              );
                            };

                            // Status indicator dot
                            const statusDotColor = isLocked ? "bg-slate-300"
                              : r.status === "settled" ? "bg-emerald-500"
                              : r.status === "no-response" ? "bg-red-500"
                              : r.status === "mgmt-followup" ? "bg-purple-500"
                              : r.status === "followup-2" ? "bg-orange-500"
                              : r.status === "followup-1" ? "bg-amber-500"
                              : r.status === "sent-pending" ? "bg-blue-500"
                              : "bg-gray-300";

                            return (
                              <TableRow
                                key={r.key}
                                className={`${isLocked ? "bg-slate-100/70" : isGreen ? "bg-emerald-50/60" : isPink ? "bg-pink-50/70" : ""} ${rowIdx === 0 ? "border-t-2 border-teal-200" : ""}`}
                                data-testid={`fu-row-${partnerCode}-${month}`}
                              >
                                <TableCell className={`sticky left-0 z-10 text-xs font-bold whitespace-nowrap ${isLocked ? "bg-slate-100" : isGreen ? "bg-emerald-50" : isPink ? "bg-pink-50" : "bg-white"}`}>
                                  <div className="flex items-center gap-1.5">
                                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDotColor}`}></span>
                                    {rowIdx === 0 ? (
                                      <span>
                                        {partnerCode}
                                        {partnerStart && (
                                          <span className="ml-1 text-[10px] font-medium text-slate-500" data-testid={`fu-start-date-${partnerCode}`} title="Partner start date (first parcel)">
                                            ({partnerStart.date})
                                          </span>
                                        )}
                                      </span>
                                    ) : <span className="text-muted-foreground/40">{partnerCode}</span>}
                                  </div>
                                </TableCell>
                                <TableCell
                                  className={`text-xs ${isLocked ? "bg-slate-100" : isGreen ? "bg-emerald-50" : isPink ? "bg-pink-50" : ""} ${rowIdx === 0 ? "cursor-pointer hover:bg-teal-50" : ""}`}
                  title={rowIdx === 0 ? "Double-click to assign team member" : undefined}
                                  onDoubleClick={() => {
                                    if (rowIdx !== 0) return;
                                    setHandlerEdit({ partner: partnerCode, value: followUpHandlers[partnerCode] || "" });
                                  }}
                                  data-testid={`fu-handler-${partnerCode}-${month}`}
                                >
                                  {rowIdx === 0 ? (
                                    handlerEdit?.partner === partnerCode && !handlerPwOpen ? (
                                      <Input
                                        autoFocus
                                        value={handlerEdit.value}
                                        onChange={(e) => setHandlerEdit(p => ({ ...p, value: e.target.value }))}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            if (followUpHandlers[partnerCode]) {
                                              // changing an existing handler needs the password
                                              setHandlerPwInput("");
                                              setHandlerPwOpen(true);
                                            } else {
                                              commitHandlerEdit("");
                                            }
                                          }
                                          if (e.key === "Escape") setHandlerEdit(null);
                                        }}
                                        placeholder="Team member"
                                        className="h-6 text-xs p-1 min-w-[80px]"
                                        data-testid={`fu-handler-input-${partnerCode}`}
                                      />
                                    ) : (
                                      <span className={followUpHandlers[partnerCode] ? "font-semibold text-teal-700" : "text-muted-foreground/40"}>
                                        {followUpHandlers[partnerCode] || "-"}
                                      </span>
                                    )
                                  ) : null}
                                </TableCell>
                                <TableCell className={`text-center text-xs font-medium ${isLocked ? "bg-slate-100 text-slate-400" : isGreen ? "bg-emerald-50" : isPink ? "bg-pink-50" : ""}`}>{mName}</TableCell>
                                {DATE_FIELDS.map((f, ci) => renderDateCell(f, ci + 1))}
                                {renderEditableCell("closing_balance")}
                                {renderEditableCell("narration")}
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      ))}
                      {/* Totals Row */}
                      {followUpFilteredRows.length > 0 && (() => {
                        let grandTotal = 0;
                        followUpFilteredRows.forEach(r => {
                          const val = parseFloat(r.row.closing_balance || 0);
                          if (!isNaN(val)) grandTotal += val;
                        });
                        return (
                          <TableRow className="bg-teal-100/70 border-t-2 border-teal-300 font-semibold" data-testid="follow-up-totals-row">
                            <TableCell className="sticky left-0 bg-teal-100 z-10 text-xs text-teal-900">
                              TOTALS ({Object.keys(followUpGroupedFiltered).length} partners, {followUpFilteredRows.length} rows)
                            </TableCell>
                            <TableCell colSpan={8} className="text-right text-xs text-teal-800 pr-4">
                              Closing Balance Total:
                            </TableCell>
                            <TableCell className="text-center text-sm font-bold text-teal-900" data-testid="follow-up-grand-total">
                              {grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="border-dashed border-teal-300 bg-teal-50/30">
            <CardContent className="p-4">
              <p className="text-xs text-teal-700 font-medium mb-1">How to use:</p>
              <ul className="text-xs text-teal-600 space-y-0.5 list-disc list-inside">
                <li><strong>Double-click</strong> any date cell to auto-fill today's date. Double-click an existing date to edit it.</li>
                <li><strong>Closing Balance &amp; Narration</strong>: Double-click to type/edit freely.</li>
                <li>Rows turn <span className="text-emerald-700 font-bold">green</span> when both "Received Data" and "A/c Settled" columns are filled.</li>
                <li>Press <strong>Enter</strong> to save or <strong>Escape</strong> to cancel while editing.</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 3: Auto Generate Issues */}
        <TabsContent value="auto-generate" className="space-y-6">
          {/* Report Header */}
          <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/20">
                    <Calculator className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-purple-800">Pending Sale Auto Generate</h2>
                    <p className="text-sm text-purple-600">DP Clients with missing prices or shipping rates - fix internally</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => fetchAutoGenerateReport()} disabled={reportsLoading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${reportsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Select value={reportExportMode} onValueChange={setReportExportMode}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single File</SelectItem>
                      <SelectItem value="client-wise">Client-wise</SelectItem>
                    </SelectContent>
                  </Select>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-2" />
                        Export
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => viewAutoGenerateInBrowser()} data-testid="auto-gen-view-all">
                        <Eye className="w-4 h-4 mr-2 text-blue-600" />
                        View on Screen (New Tab)
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => reportExportMode === "single" ? exportAutoGenerateCSV() : getFilteredClients().forEach(c => exportAutoGenerateCSV(c.client_code))}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Export CSV
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => reportExportMode === "single" ? exportAutoGeneratePDF() : getFilteredClients().forEach(c => exportAutoGeneratePDF(c.client_code))}>
                        <FileDown className="w-4 h-4 mr-2" />
                        Export PDF
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handlePrintReport}>
                        <Printer className="w-4 h-4 mr-2" />
                        Print
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              
              {/* Filters */}
              <div className="mt-4 flex items-center gap-4 flex-wrap">
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => applyDateFilter("all")} className={!reportDateFrom && !reportDateTo ? "bg-purple-100" : ""}>All Time</Button>
                  <Button variant="outline" size="sm" onClick={() => applyDateFilter("30days")}>Last 30 Days</Button>
                  <Button variant="outline" size="sm" onClick={() => applyDateFilter("thisMonth")}>This Month</Button>
                </div>
                <div className="flex items-center gap-2">
                  <DMYDateInput value={reportDateFrom} onChange={(e) => setReportDateFrom(e.target.value)} className="w-36" data-testid="autogen-date-from" />
                  <span className="text-sm">to</span>
                  <DMYDateInput value={reportDateTo} onChange={(e) => setReportDateTo(e.target.value)} className="w-36" data-testid="autogen-date-to" />
                  <Button variant="default" size="sm" onClick={() => fetchAutoGenerateReport()} disabled={reportsLoading} className="bg-purple-600 hover:bg-purple-700 text-white gap-1" data-testid="autogen-load-btn">
                    {reportsLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    Load
                  </Button>
                  {(reportDateFrom || reportDateTo) && (
                    <Button variant="ghost" size="sm" onClick={() => { setReportDateFrom(""); setReportDateTo(""); fetchAutoGenerateReport("", ""); }} className="px-2 text-xs text-slate-500 hover:text-slate-700" data-testid="autogen-clear-dates">
                      <XCircle className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-purple-600" />
                  <p className="text-xs font-medium text-purple-600 uppercase">DP Clients</p>
                </div>
                <p className="text-3xl font-bold text-purple-700 mt-2">{autoGenerateData?.summary?.total_clients || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-600" />
                  <p className="text-xs font-medium text-muted-foreground uppercase">Total Orders</p>
                </div>
                <p className="text-3xl font-bold mt-2">{(autoGenerateData?.summary?.total_orders || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  <p className="text-xs font-medium text-yellow-700 uppercase">No Price List</p>
                </div>
                <p className="text-3xl font-bold text-yellow-700 mt-2">{(autoGenerateData?.summary?.no_price_list_count || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-red-600" />
                  <p className="text-xs font-medium text-muted-foreground uppercase">Product Not Found</p>
                </div>
                <p className="text-3xl font-bold text-red-600 mt-2">{(autoGenerateData?.summary?.product_not_found_count || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-orange-600" />
                  <p className="text-xs font-medium text-muted-foreground uppercase">Shipping Not Found</p>
                </div>
                <p className="text-3xl font-bold text-orange-600 mt-2">{(autoGenerateData?.summary?.service_not_found_count || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>

          {/* Suspicious Ledger Codes Cleanup Panel (admin only) */}
          {isAdmin && (autoGenerateData?.suspicious_ledger_codes?.length > 0 || historyOpen) && (
            <Card className="border-amber-300 bg-amber-50" data-testid="suspicious-codes-panel">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base text-amber-800 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Junk Ledger Rows Detected ({autoGenerateData?.suspicious_ledger_codes?.length || 0})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-white text-amber-700 border-amber-400">
                      Excluded from report
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-amber-800 hover:bg-amber-100"
                      onClick={() => { const next = !historyOpen; setHistoryOpen(next); if (next) fetchCleanupHistory(); }}
                      data-testid="toggle-cleanup-history"
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      {historyOpen ? "Hide" : "View"} Recent Cleanups
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-amber-700 mt-1">
                  These look like operator captions, not real client codes. Delete the ledger row, or rename it to merge into the correct client. Every action is logged and reversible for 30 days.
                </p>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {(autoGenerateData?.suspicious_ledger_codes || []).map((s) => (
                    <div
                      key={s.client_code}
                      className="flex items-center justify-between bg-white rounded border border-amber-200 px-3 py-2"
                      data-testid={`suspicious-row-${s.client_code}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <code className="font-mono text-sm text-slate-800 truncate">{s.client_code}</code>
                        <Badge variant="secondary" className="text-[10px]">
                          {s.order_count} order{s.order_count === 1 ? "" : "s"}
                        </Badge>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openCleanupModal(s.client_code, s.order_count, "rename")}
                          data-testid={`suspicious-rename-${s.client_code}`}
                          className="h-7 text-xs"
                        >
                          <Wrench className="w-3 h-3 mr-1" />
                          Rename
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => openCleanupModal(s.client_code, s.order_count, "delete")}
                          data-testid={`suspicious-delete-${s.client_code}`}
                          className="h-7 text-xs"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {historyOpen && (
                  <div className="mt-4 pt-3 border-t border-amber-200">
                    <div className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-2">
                      <Clock className="w-3 h-3" /> Recent Cleanups (last 30 days)
                    </div>
                    {cleanupHistory.length === 0 ? (
                      <div className="text-xs text-slate-500 italic">No cleanups in the last 30 days.</div>
                    ) : (
                      <div className="space-y-1.5 max-h-80 overflow-auto">
                        {cleanupHistory.map((h) => (
                          <div
                            key={h.id}
                            className="bg-white rounded border border-slate-200 px-3 py-2 text-xs flex items-center justify-between gap-3"
                            data-testid={`cleanup-history-${h.id}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge
                                  className={
                                    h.cleanup_action === "delete"
                                      ? "bg-red-100 text-red-700 border-red-200"
                                      : "bg-blue-100 text-blue-700 border-blue-200"
                                  }
                                >
                                  {h.cleanup_action.toUpperCase()}
                                </Badge>
                                <code className="font-mono text-slate-800">{h.client_code}</code>
                                {h.cleanup_action === "rename" && (
                                  <>
                                    <ArrowUpRight className="w-3 h-3 text-slate-400" />
                                    <code className="font-mono text-slate-800">{h.new_code}</code>
                                    {h.merged_into_existing && (
                                      <Badge variant="outline" className="text-[10px]">merged</Badge>
                                    )}
                                  </>
                                )}
                                {h.restored && (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Restored</Badge>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-500 mt-0.5">
                                by <b>{h.performed_by}</b> · {format(new Date(h.timestamp), "dd MMM yyyy, HH:mm")}
                                {h.summary && (
                                  <>
                                    {" · "}
                                    {h.cleanup_action === "delete"
                                      ? `${h.summary.orphan_shipment_orders || 0} orphan order(s)`
                                      : `${h.summary.shipment_orders_updated || 0} order(s) moved`}
                                  </>
                                )}
                              </div>
                            </div>
                            {!h.restored && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs flex-shrink-0"
                                onClick={() => { setRestorePromptId(h.id); setRestorePassword(""); }}
                                data-testid={`cleanup-restore-btn-${h.id}`}
                              >
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Restore
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Search */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by client code..." value={reportSearchTerm} onChange={(e) => setReportSearchTerm(e.target.value)} className="pl-10" />
            </div>
            <Badge variant="outline">{getFilteredClients().length} clients</Badge>
          </div>

          {/* Clients Table */}
          <Card>
            <CardContent className="p-0">
              {reportsLoading ? (
                <div className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-500" /></div>
              ) : getFilteredClients().length === 0 ? (
                <div className="p-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-medium text-muted-foreground">No Auto-Generation Issues Found</h3>
                  <p className="text-sm text-muted-foreground mt-1">All orders have successfully generated sales.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-purple-50">
                      <TableHead className="w-10"></TableHead>
                      <TableHead className="cursor-pointer" onClick={() => handleReportSort('client_code')}>
                        <div className="flex items-center">Client Code<SortIcon columnKey="client_code" /></div>
                      </TableHead>
                      <TableHead className="cursor-pointer" onClick={() => handleReportSort('total_orders')}>
                        <div className="flex items-center">Orders<SortIcon columnKey="total_orders" /></div>
                      </TableHead>
                      <TableHead>Price List</TableHead>
                      <TableHead>Product Issues</TableHead>
                      <TableHead>Shipping Issues</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getFilteredClients().map((client) => (
                      <Collapsible key={client.client_code} asChild>
                        <>
                          <TableRow className="hover:bg-purple-50/50 cursor-pointer" onClick={() => toggleClient(client.client_code)}>
                            <TableCell>
                              <Button variant="ghost" size="sm" className="p-0 h-6 w-6">
                                {expandedClients.has(client.client_code) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </Button>
                            </TableCell>
                            <TableCell className="font-medium">{client.client_code}</TableCell>
                            <TableCell><Badge variant="outline" className="bg-purple-50">{client.total_orders}</Badge></TableCell>
                            <TableCell>
                              {client.pricing_status === "no_price_list" ? (
                                <Badge variant="destructive" className="bg-yellow-100 text-yellow-800 border-yellow-300">
                                  <AlertTriangle className="w-3 h-3 mr-1" />No Prices
                                </Badge>
                              ) : client.pricing_status === "partial_price_list" ? (
                                <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                                  Partial ({client.price_list_count})
                                </Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-700 border-green-200">
                                  {client.price_list_count} prices
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {client.zero_medicine_cost > 0 ? (
                                <Badge variant="destructive" className="bg-red-100 text-red-700"><DollarSign className="w-3 h-3 mr-1" />{client.zero_medicine_cost}</Badge>
                              ) : <span className="text-green-600">✓</span>}
                            </TableCell>
                            <TableCell>
                              {client.zero_shipping_cost > 0 ? (
                                <Badge className="bg-orange-100 text-orange-700 border-orange-200"><Truck className="w-3 h-3 mr-1" />{client.zero_shipping_cost}</Badge>
                              ) : <span className="text-green-600">✓</span>}
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="outline" size="sm" onClick={(e) => e.stopPropagation()}>
                                    <Download className="w-3 h-3 mr-1" />Export
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); viewAutoGenerateInBrowser(client.client_code); }} data-testid={`auto-gen-view-${client.client_code}`}>
                                    <Eye className="w-4 h-4 mr-2 text-blue-600" />View on Screen (New Tab)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportAutoGenerateCSV(client.client_code); }}>
                                    <FileSpreadsheet className="w-4 h-4 mr-2" />CSV
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportAutoGeneratePDF(client.client_code); }}>
                                    <FileDown className="w-4 h-4 mr-2" />PDF
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                          <CollapsibleContent asChild>
                            <TableRow>
                              <TableCell colSpan={7} className="p-0 bg-slate-50">
                                <div className="p-4">
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="text-xs">
                                        <TableHead>Tracking</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Product</TableHead>
                                        <TableHead>Qty</TableHead>
                                        <TableHead>Issue</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {client.orders?.slice(0, 15).map((order, idx) => (
                                        <TableRow key={idx} className="text-xs">
                                          <TableCell className="font-mono">{order.tracking || '-'}</TableCell>
                                          <TableCell>{formatDateDashDisplay(order.order_date) || '-'}</TableCell>
                                          <TableCell className="max-w-[150px] truncate">{order.product || '-'}</TableCell>
                                          <TableCell>{order.qty || '-'}</TableCell>
                                          <TableCell>
                                            <Badge variant="outline" className={order.issue_text?.includes('Product') ? 'bg-red-50 text-red-700' : 'bg-orange-50 text-orange-700'}>
                                              {order.issue_text || '-'}
                                            </Badge>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                      {client.orders?.length > 15 && (
                                        <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">...and {client.orders.length - 15} more</TableCell></TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                </div>
                              </TableCell>
                            </TableRow>
                          </CollapsibleContent>
                        </>
                      </Collapsible>
                    ))}
                    {/* Totals Row */}
                    {getFilteredClients().length > 0 && (() => {
                      const fc = getFilteredClients();
                      const totalOrders = fc.reduce((s, c) => s + (c.total_orders || 0), 0);
                      const totalProductIssues = fc.reduce((s, c) => s + (c.zero_medicine_cost || 0), 0);
                      const totalShippingIssues = fc.reduce((s, c) => s + (c.zero_shipping_cost || 0), 0);
                      return (
                        <TableRow className="bg-purple-100/70 border-t-2 border-purple-300 font-semibold" data-testid="auto-generate-totals-row">
                          <TableCell></TableCell>
                          <TableCell className="text-purple-900">
                            Total ({fc.length} clients)
                          </TableCell>
                          <TableCell className="font-bold text-purple-900">{totalOrders.toLocaleString()}</TableCell>
                          <TableCell></TableCell>
                          <TableCell className="font-bold text-red-700">{totalProductIssues.toLocaleString()}</TableCell>
                          <TableCell className="font-bold text-orange-700">{totalShippingIssues.toLocaleString()}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 4: Deletion Logs */}
        <TabsContent value="logs" className="space-y-4 lg:space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-600" />
                  Deletion Logs
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={fetchDeletionLogs} disabled={loadingLogs} data-testid="refresh-logs-btn">
                    <RefreshCw className={`w-4 h-4 mr-1 ${loadingLogs ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                  {isAdmin && deletionLogs.length > 0 && (
                    <Button variant="destructive" size="sm" onClick={() => setClearLogsDialog(true)} data-testid="clear-logs-btn">
                      <Trash2 className="w-4 h-4 mr-1" />
                      {selectedLogIds.size > 0 ? `Clear Selected (${selectedLogIds.size})` : "Clear All"}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingLogs ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : deletionLogs.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground" data-testid="no-logs-message">
                  <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">No deletion logs found</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="deletion-logs-list">
                  {deletionLogs.map((log) => (
                    <div
                      key={log.id}
                      className={`border rounded-lg p-3 lg:p-4 hover:bg-slate-50 transition-colors ${
                        selectedLogIds.has(log.id) ? "bg-red-50 border-red-200" : ""
                      }`}
                      data-testid={`log-entry-${log.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          {isAdmin && (
                            <Checkbox
                              checked={selectedLogIds.has(log.id)}
                              onCheckedChange={(checked) => {
                                setSelectedLogIds(prev => {
                                  const newSet = new Set(prev);
                                  checked ? newSet.add(log.id) : newSet.delete(log.id);
                                  return newSet;
                                });
                              }}
                              className="mt-1"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                                <Trash2 className="w-3 h-3 mr-1" />
                                Deleted {log.deleted_count} record{log.deleted_count !== 1 ? "s" : ""}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                by <span className="font-medium text-foreground">{log.display_name || log.username}</span>
                                <span className="text-muted-foreground ml-1">({log.role})</span>
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1.5">
                              {log.timestamp ? format(new Date(log.timestamp), "dd MMM yyyy, hh:mm a") : "-"}
                            </p>
                            {/* Show deleted items */}
                            {log.deleted_items?.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {log.deleted_items.slice(0, 6).map((item, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-[10px] font-mono">
                                    {item.partner_code} / {item.tracking}
                                  </Badge>
                                ))}
                                {log.deleted_items.length > 6 && (
                                  <Badge variant="secondary" className="text-[10px]">
                                    +{log.deleted_items.length - 6} more
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {log.upload_deleted > 0 && (
                            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700">
                              {log.upload_deleted} uploads
                            </Badge>
                          )}
                          {log.sales_deleted > 0 && (
                            <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700">
                              {log.sales_deleted} sales
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 7: Uploaded Sheets — partner-ledger original Excel downloads */}
        <TabsContent value="uploaded-sheets" className="space-y-4 lg:space-y-6" data-testid="uploaded-sheets-tab-content">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-base lg:text-lg">
                  <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                  Uploaded Sheets
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Original partner-ledger Excel files that feed Payment Verification. Last 30 per partner are retained for download.
                </p>
              </div>
              <div className="flex gap-2 flex-wrap items-center">
                <Input
                  placeholder="Filter by partner code…"
                  value={uploadedSheetsPartner}
                  onChange={(e) => setUploadedSheetsPartner(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") fetchUploadedSheets(1, uploadedSheetsPartner); }}
                  className="w-48 h-8 text-sm"
                  data-testid="uploaded-sheets-partner-filter"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchUploadedSheets(1, uploadedSheetsPartner)}
                  disabled={loadingUploadedSheets}
                >
                  {loadingUploadedSheets ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
                  Search
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setUploadedSheetsPartner(""); fetchUploadedSheets(1, ""); }}
                >
                  Clear
                </Button>
                {isAdmin && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setStaleCleanupOpen(true)}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 gap-1.5"
                    data-testid="open-stale-cleanup-btn"
                    title="Surface duplicate / unverified uploads that silently hide pending trackings from Pending Sales"
                  >
                    <ShieldAlert className="w-3.5 h-3.5" />
                    Cleanup Stale Uploads
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loadingUploadedSheets ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : uploadedSheets.length === 0 ? (
                <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-lg">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No uploaded sheets found.</p>
                </div>
              ) : (
                <>
                  <div className="text-xs text-slate-500 mb-2">
                    Showing {uploadedSheets.length} of {uploadedSheetsTotal.toLocaleString()} upload(s) · page {uploadedSheetsPage}
                  </div>
                  <div className="space-y-2">
                    {uploadedSheets.map((u) => (
                      <div
                        key={u.upload_id}
                        className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 flex items-center justify-between gap-3 hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
                        data-testid={`uploaded-sheet-${u.upload_id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <FileSpreadsheet className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                            <span className="font-medium text-sm text-slate-800 truncate">{u.filename || u.upload_id}</span>
                            {u.has_original_file ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
                                Original Available
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="text-slate-500 text-[10px] cursor-help"
                                title="The original file bytes for this upload are no longer stored. This happens for uploads done before the file-archive feature landed (2026-05-21) or when the per-partner retention window (last 30 per partner) has been exceeded. The parsed payment data is still intact and visible above."
                              >
                                Past Retention
                              </Badge>
                            )}
                            {u.status && u.status !== "pending_review" && (
                              <Badge variant="outline" className="text-[10px]">{u.status}</Badge>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-2">
                            <span>by <b>{u.uploaded_by || "?"}</b></span>
                            <span>·</span>
                            <span>{u.uploaded_at ? new Date(u.uploaded_at).toLocaleString() : ""}</span>
                            {u.file_size_bytes ? <><span>·</span><span>{(u.file_size_bytes / 1024).toFixed(1)} KB</span></> : null}
                            {u.partner_codes?.length > 0 && (
                              <><span>·</span><span>Partners: {u.partner_codes.join(", ")}</span></>
                            )}
                          </div>
                        </div>
                        {u.has_original_file ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={downloadingSheetId === u.upload_id}
                            onClick={() => downloadOriginalSheet(u)}
                            data-testid={`download-original-${u.upload_id}`}
                            className="gap-1.5 border-blue-300 text-blue-600 hover:bg-blue-50 flex-shrink-0"
                          >
                            {downloadingSheetId === u.upload_id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Download className="w-3.5 h-3.5" />
                            )}
                            Download
                          </Button>
                        ) : (
                          <span
                            className="flex items-center gap-1.5 text-[11px] text-slate-400 italic flex-shrink-0 cursor-help"
                            title="Original file is no longer stored — only the parsed data is retained. Future uploads will have downloadable originals (last 30 per partner)."
                            data-testid={`download-unavailable-${u.upload_id}`}
                          >
                            <Info className="w-3.5 h-3.5" />
                            File not stored
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Pagination */}
                  {uploadedSheetsTotal > uploadedSheetsLimit && (
                    <div className="flex items-center justify-between mt-4 pt-3 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={uploadedSheetsPage <= 1 || loadingUploadedSheets}
                        onClick={() => fetchUploadedSheets(uploadedSheetsPage - 1)}
                      >
                        Previous
                      </Button>
                      <span className="text-xs text-slate-500">
                        Page {uploadedSheetsPage} of {Math.ceil(uploadedSheetsTotal / uploadedSheetsLimit)}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={uploadedSheetsPage * uploadedSheetsLimit >= uploadedSheetsTotal || loadingUploadedSheets}
                        onClick={() => fetchUploadedSheets(uploadedSheetsPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 8: Raw Funds — accounting team's bank-receipts source-of-truth */}
        <TabsContent value="raw-funds" className="space-y-4 lg:space-y-6" data-testid="raw-funds-tab-content">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5 text-indigo-600" />
                    Raw Funds — Accounting Data
                  </CardTitle>
                  <CardDescription>
                    Internal source-of-truth for money landed in our bank account. Uploaded by the
                    upload team. When a partner's structured ledger sheet is processed, every entry
                    is automatically checked against this raw data and flagged{" "}
                    <Badge variant="outline" className="text-[10px] mx-0.5 border-emerald-400 text-emerald-700">RAW ✓</Badge>{" "}
                    if it matches (same client · INR ±₹50 · date ±7 days).
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    ref={rawFundsUploadInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={handleRawFundsFilePick}
                    data-testid="raw-funds-upload-input"
                  />
                  <Button
                    size="sm"
                    onClick={() => rawFundsUploadInputRef.current?.click()}
                    disabled={rawFundsPreviewing}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="raw-funds-upload-btn"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1.5" />
                    {rawFundsPreviewing ? "Reading sheet..." : "Upload Sheet"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={scanDuplicatePayments}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="raw-funds-dup-scan-btn"
                    title="Scan for exact duplicate payments (all fields identical)"
                  >
                    <Copy className="w-3.5 h-3.5 mr-1.5" />
                    Scan Duplicates
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadRawFundsTemplate} data-testid="raw-funds-template-btn">
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    Download template
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary cards (global) */}
              {rawFundsSummary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="rounded-md border border-indigo-200 bg-indigo-50/50 px-3 py-2" data-testid="raw-funds-stat-rows">
                    <div className="text-[11px] uppercase tracking-wide text-indigo-700">Total rows</div>
                    <div className="text-xl font-semibold text-indigo-900">{rawFundsSummary.total_rows.toLocaleString()}</div>
                  </div>
                  <div className="rounded-md border border-indigo-200 bg-indigo-50/50 px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-indigo-700">Uploads</div>
                    <div className="text-xl font-semibold text-indigo-900">{rawFundsSummary.uploads_count}</div>
                  </div>
                  {isAdmin && (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50/50 px-3 py-2 col-span-2" data-testid="raw-funds-stat-total-inr">
                      <div className="text-[11px] uppercase tracking-wide text-emerald-700">Total inward (INR)</div>
                      <div className="text-xl font-semibold text-emerald-900">
                        ₹{rawFundsSummary.total_inward_inr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Filtered totals — mirror of Verification tab's filteredPaymentsTotals */}
              {rawFundsTotal > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="raw-funds-filtered-totals">
                  <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">Filtered count</div>
                    <div className="text-xl font-semibold text-slate-800">{rawFundsTotal.toLocaleString()}</div>
                  </div>
                  {isAdmin && (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50/40 px-3 py-2">
                      <div className="text-[11px] uppercase tracking-wide text-emerald-700">Filtered total (INR)</div>
                      <div className="text-xl font-semibold text-emerald-800">
                        ₹{rawFundsSumInr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  )}
                  <div className="rounded-md border border-slate-200 bg-white px-3 py-2" title="Rows with a customer name in this filter">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">With customer</div>
                    <div className="text-sm font-semibold text-slate-800">
                      {rawFundsCoverage.with_customer.toLocaleString()}
                      <span className="text-slate-400 text-xs ml-1">
                        ({rawFundsTotal > 0 ? Math.round((rawFundsCoverage.with_customer / rawFundsTotal) * 100) : 0}%)
                      </span>
                    </div>
                  </div>
                  <div className="rounded-md border border-slate-200 bg-white px-3 py-2" title="Rows with narration / country populated">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">With narration / country</div>
                    <div className="text-sm font-semibold text-slate-800">
                      {rawFundsCoverage.with_narration.toLocaleString()} / {rawFundsCoverage.with_country.toLocaleString()}
                    </div>
                  </div>
                </div>
              )}

              {/* Uploads list */}
              {rawFundsUploads.length > 0 && (
                <div className="border border-slate-200 rounded-md">
                  <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-600 flex items-center justify-between">
                    <span>Recent uploads</span>
                    <span className="text-slate-400">{rawFundsUploads.length} file(s)</span>
                  </div>
                  <div className="divide-y divide-slate-100 max-h-40 overflow-y-auto">
                    {rawFundsUploads.map((u) => (
                      <div key={u.upload_id} className="flex items-center justify-between gap-2 px-3 py-2 text-xs" data-testid={`raw-funds-upload-${u.upload_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{u.filename}</div>
                          <div className="text-[10px] text-slate-500">
                            {new Date(u.uploaded_at).toLocaleString()} · {u.uploaded_by || "—"} ·{" "}
                            <span className="font-semibold">{u.row_count}</span> rows
                            {isAdmin && (
                              <> · ₹{(u.total_inward_inr || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</>
                            )}
                            {u.date_min && u.date_max && (
                              <> · {u.date_min} → {u.date_max}</>
                            )}
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => downloadRawFundsUpload(u.upload_id, u.filename)} title="Download original" data-testid={`raw-funds-download-${u.upload_id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => deleteRawFundsUpload(u.upload_id, u.filename, u.row_count)} title="Delete upload + rows (password required)" data-testid={`raw-funds-delete-${u.upload_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[180px]">
                  <label className="text-[11px] text-slate-500">Search</label>
                  <Input
                    placeholder="narration / name / client"
                    value={rawFundsSearch}
                    onChange={(e) => setRawFundsSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") fetchRawFunds(1); }}
                    className="h-9"
                    data-testid="raw-funds-search"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500">Client</label>
                  <Select value={rawFundsClient || "__all__"} onValueChange={(v) => { const next = v === "__all__" ? "" : v; setRawFundsClient(next); fetchRawFunds(1); }}>
                    <SelectTrigger className="h-9 w-40" data-testid="raw-funds-client-select">
                      <SelectValue placeholder="All clients" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All clients</SelectItem>
                      {rawFundsClientOptions.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[11px] text-slate-500">Source (bank / channel)</label>
                  <Select value={rawFundsSource || "__all__"} onValueChange={(v) => { const next = v === "__all__" ? "" : v; setRawFundsSource(next); fetchRawFunds(1); }}>
                    <SelectTrigger className="h-9 w-44" data-testid="raw-funds-source-select">
                      <SelectValue placeholder="All sources" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All sources</SelectItem>
                      {rawSourceOptions.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[11px] text-slate-500">From</label>
                  <Input type="date" value={rawFundsDateFrom} onChange={(e) => setRawFundsDateFrom(e.target.value)} className="h-9" data-testid="raw-funds-from" />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500">To</label>
                  <Input type="date" value={rawFundsDateTo} onChange={(e) => setRawFundsDateTo(e.target.value)} className="h-9" data-testid="raw-funds-to" />
                </div>
                <Button size="sm" onClick={() => fetchRawFunds(1)} disabled={rawFundsLoading} data-testid="raw-funds-apply">
                  {rawFundsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Apply"}
                </Button>
                {/* History — visible to BOTH admin and uploader so the
                    upload team can self-review recent suspense resolutions
                    and client-code reassignments without opening the
                    super_admin Activity feed. */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { const next = !rawFundsHistoryOpen; setRawFundsHistoryOpen(next); if (next) fetchRawFundsHistory(); }}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid="raw-funds-history-btn"
                  title="Recent client-code reassignments & suspense resolutions"
                >
                  <Clock className="w-3.5 h-3.5 mr-1" />
                  History
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open("/raw-funds-history", "_blank")}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50 px-2"
                  data-testid="raw-funds-history-newtab-btn"
                  title="Open full history (unlimited records) in a new tab"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </Button>
                {/* Suspense filter — visible to BOTH admin and uploader so
                    the upload team can self-serve the resolution queue
                    without admin intervention. */}
                <Button
                  size="sm"
                  variant={rawFundsOnlySuspense ? "default" : "outline"}
                  onClick={() => { const next = !rawFundsOnlySuspense; setRawFundsOnlySuspense(next); fetchRawFunds(1, { only_suspense: next }); }}
                  className={rawFundsOnlySuspense ? "bg-amber-500 hover:bg-amber-600 text-white" : "border-amber-400 text-amber-700 hover:bg-amber-50"}
                  data-testid="raw-funds-suspense-toggle"
                  title={rawFundsOnlySuspense ? "Showing only Suspense rows — click to clear" : "Show only Suspense rows (rows with no client code) to resolve"}
                >
                  {rawFundsOnlySuspense ? "🟡 Suspense (active)" : "🟡 Show Suspense"}
                </Button>
                {isAdmin && (
                  <Button size="sm" variant="outline" onClick={openSourceSummary} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="raw-funds-source-summary-btn">
                    <BarChart3 className="w-3.5 h-3.5 mr-1" />
                    Source Summary
                  </Button>
                )}
                {isAdmin && (
                  <Button size="sm" variant="outline" onClick={openClientSummary} className="border-teal-300 text-teal-700 hover:bg-teal-50" data-testid="raw-funds-client-summary-btn">
                    <BarChart3 className="w-3.5 h-3.5 mr-1" />
                    Client Code Summary
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => { setRawFundsSearch(""); setRawFundsClient(""); setRawFundsSource(""); setRawFundsDateFrom(""); setRawFundsDateTo(""); setRawFundsOnlySuspense(false); fetchRawFunds(1); }}>
                  Clear
                </Button>
              </div>

              {/* Bulk actions bar — appears only when at least one row selected */}
              {rawFundsSelected.size > 0 && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-rose-50 border border-rose-200" data-testid="raw-funds-bulk-bar">
                  <span className="text-sm text-rose-800">
                    <span className="font-semibold">{rawFundsSelected.size}</span> row(s) selected
                  </span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setRawFundsSelected(new Set())} data-testid="raw-funds-clear-selection">
                      Clear
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setRawFundsBulkDialogOpen(true)}
                      data-testid="raw-funds-bulk-delete-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                      Delete selected
                    </Button>
                  </div>
                </div>
              )}

              {/* Rows table */}
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <div className="max-h-[60vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="text-center p-2 w-8">
                          <input
                            type="checkbox"
                            checked={rawFundsItems.length > 0 && rawFundsSelected.size === rawFundsItems.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setRawFundsSelected(new Set(rawFundsItems.map(r => r.id)));
                              } else {
                                setRawFundsSelected(new Set());
                              }
                            }}
                            aria-label="Select all"
                            data-testid="raw-funds-select-all"
                          />
                        </th>
                        {[
                          { key: "date", label: "Date", align: "left" },
                          { key: "narration", label: "Narration", align: "left" },
                          { key: "inward_inr", label: "Inward INR", align: "right" },
                          { key: null, label: "USD", align: "right" },
                          { key: null, label: "USD₂ INR", align: "right" },
                          { key: "client_code", label: "Client", align: "left" },
                          { key: "customer_name", label: "Customer", align: "left" },
                          { key: "country", label: "Country", align: "left" },
                          { key: "source", label: "Source", align: "left" },
                        ].map((col) => (
                          <th key={col.label} className={`text-${col.align} p-2 select-none`}>
                            {col.key ? (
                              <button
                                type="button"
                                onClick={() => toggleRawFundsSort(col.key)}
                                className="inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                                data-testid={`raw-funds-sort-${col.key}`}
                              >
                                {col.label}
                                <span className="text-[9px] text-slate-400">
                                  {rawFundsSortKey === col.key ? (rawFundsSortDir === "asc" ? "▲" : "▼") : "↕"}
                                </span>
                              </button>
                            ) : (
                              <span className="font-semibold">{col.label}</span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rawFundsLoading ? (
                        <tr><td colSpan={10} className="text-center text-slate-500 p-6"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
                      ) : rawFundsItems.length === 0 ? (
                        <tr><td colSpan={10} className="text-center text-slate-500 p-6 italic">No raw funds rows yet. Upload from Upload Center → Ledger → Raw Funds (Accounting).</td></tr>
                      ) : rawFundsItems.map((row) => {
                        const checked = rawFundsSelected.has(row.id);
                        return (
                          <tr key={row.id} className={`border-b border-slate-100 hover:bg-slate-50 ${checked ? "bg-indigo-50" : ""}`} data-testid={`raw-funds-row-${row.id}`}>
                            <td className="p-2 text-center">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setRawFundsSelected((prev) => {
                                    const next = new Set(prev);
                                    if (e.target.checked) next.add(row.id); else next.delete(row.id);
                                    return next;
                                  });
                                }}
                                aria-label={`Select ${row.tracking || row.id}`}
                                data-testid={`raw-funds-select-${row.id}`}
                              />
                            </td>
                            {rawEditableCell(row, "date", row.date_ddmmyy || "-", "p-2 font-mono")}
                            {rawEditableCell(row, "narration", row.narration || "-", "p-2 truncate max-w-[180px]")}
                            {rawEditableCell(row, "inward_inr",
                              row.inward_inr != null ? `₹${row.inward_inr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "-",
                              "p-2 text-right font-semibold")}
                            {rawEditableCell(row, "usd", row.usd || "-", "p-2 text-right")}
                            {rawEditableCell(row, "usd2_inr",
                              row.usd2_inr != null ? row.usd2_inr.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "-",
                              "p-2 text-right")}
                            <td
                              className="p-2 relative"
                              onDoubleClick={() => openRawClientCodeEditor(row.id)}
                              title={row.client_code ? "Double-click to re-assign" : "Suspense row — double-click to assign a client code"}
                              data-testid={`raw-funds-clientcode-cell-${row.id}`}
                            >
                              {rawEditingRowId === row.id ? (
                                <div className="absolute z-50 left-1 top-1 bg-white border border-slate-300 shadow-lg rounded-md p-2 w-64">
                                  <div className="flex items-center gap-1 mb-1">
                                    <Input
                                      autoFocus
                                      value={rawEditingSearch}
                                      onChange={(e) => setRawEditingSearch(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Escape") { setRawEditingRowId(null); }
                                        if (e.key === "Enter") {
                                          const term = rawEditingSearch.trim().toUpperCase();
                                          if (rawCanonicalCodes.includes(term)) saveRawClientCode(row.id, term);
                                        }
                                      }}
                                      placeholder="Type to search..."
                                      className="h-7 text-xs"
                                      data-testid={`raw-funds-clientcode-input-${row.id}`}
                                    />
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 p-0"
                                      onClick={() => setRawEditingRowId(null)}
                                      data-testid={`raw-funds-clientcode-cancel-${row.id}`}
                                    >
                                      <XCircle className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                  <div className="max-h-48 overflow-y-auto">
                                    {(() => {
                                      const q = rawEditingSearch.trim().toUpperCase();
                                      const matches = q
                                        ? rawCanonicalCodes.filter((c) => c.includes(q)).slice(0, 30)
                                        : rawCanonicalCodes.slice(0, 30);
                                      if (matches.length === 0) {
                                        return <div className="text-[11px] text-slate-500 italic px-2 py-1">No match</div>;
                                      }
                                      return matches.map((c) => (
                                        <button
                                          key={c}
                                          type="button"
                                          className={`w-full text-left px-2 py-1 text-xs hover:bg-indigo-50 font-mono ${c === (row.client_code || "").toUpperCase() ? "bg-indigo-100 font-semibold" : ""}`}
                                          onClick={() => saveRawClientCode(row.id, c)}
                                          disabled={rawEditingSaving}
                                          data-testid={`raw-funds-clientcode-option-${row.id}-${c}`}
                                        >
                                          {c}
                                        </button>
                                      ));
                                    })()}
                                  </div>
                                  {row.client_code && (
                                    <button
                                      type="button"
                                      onClick={() => saveRawClientCode(row.id, "")}
                                      disabled={rawEditingSaving}
                                      className="w-full text-[11px] text-rose-600 hover:bg-rose-50 mt-1 py-1 rounded"
                                      data-testid={`raw-funds-clientcode-clear-${row.id}`}
                                    >
                                      Clear (back to suspense)
                                    </button>
                                  )}
                                </div>
                              ) : row.client_code ? (
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] cursor-pointer hover:bg-indigo-50 ${row.client_code_manual ? "border-indigo-400 text-indigo-700" : ""}`}
                                >
                                  {row.client_code}
                                </Badge>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] cursor-pointer border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100"
                                  data-testid={`raw-funds-suspense-${row.id}`}
                                >
                                  ⚠ Suspense
                                </Badge>
                              )}
                            </td>
                            {rawEditableCell(row, "customer_name", row.customer_name || "-", "p-2 truncate max-w-[140px]")}
                            {rawEditableCell(row, "country", row.country || "-")}
                            {rawEditableCell(row, "source", row.source || "-")}
                          </tr>
                        );
                      })}
                    </tbody>
                    {/* Footer totals — mirrors Verification tab's totals row */}
                    {rawFundsItems.length > 0 && (
                      <tfoot className="bg-slate-50 border-t-2 border-slate-300 sticky bottom-0">
                        <tr data-testid="raw-funds-totals-row">
                          <td className="p-2" colSpan={3}>
                            <span className="text-[11px] text-slate-500">Filtered totals · {rawFundsTotal.toLocaleString()} rows</span>
                          </td>
                          <td className="p-2 text-right font-bold text-emerald-800">
                            ₹{rawFundsSumInr.toLocaleString("en-IN", { maximumFractionDigits: 3 })}
                          </td>
                          <td className="p-2 text-right text-slate-500">-</td>
                          <td className="p-2 text-right font-bold text-emerald-800">
                            {rawFundsSumUsd2 ? `₹${rawFundsSumUsd2.toLocaleString("en-IN", { maximumFractionDigits: 3 })}` : "-"}
                          </td>
                          <td colSpan={4}></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              {/* Pagination + filtered total */}
              <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-xs">
                <div className="text-slate-500">
                  Page <span className="font-semibold">{rawFundsPage}</span> of{" "}
                  <span className="font-semibold">{Math.max(Math.ceil(rawFundsTotal / rawFundsPageSize), 1)}</span> ·{" "}
                  <span className="font-semibold">{rawFundsTotal.toLocaleString()}</span> rows · filtered total{" "}
                  <span className="font-semibold text-emerald-700">₹{rawFundsSumInr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => fetchRawFunds(Math.max(rawFundsPage - 1, 1))} disabled={rawFundsLoading || rawFundsPage <= 1} data-testid="raw-funds-prev">
                    Previous
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => fetchRawFunds(rawFundsPage + 1)} disabled={rawFundsLoading || rawFundsPage * rawFundsPageSize >= rawFundsTotal} data-testid="raw-funds-next">
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 9: Cr/Dr Entries — manual accounting adjustments */}
        <TabsContent value="crdr" className="space-y-4 lg:space-y-6" data-testid="crdr-tab-content">
          <CrDrEntriesPanel />
        </TabsContent>
      </Tabs>

      {/* Raw Funds — Upload Preview & Verify dialog */}
      <Dialog open={rawFundsPreviewOpen} onOpenChange={(o) => { if (!rawFundsUploadingSheet) setRawFundsPreviewOpen(o); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="raw-funds-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-4 h-4 text-indigo-600" />
              Verify Raw Funds Sheet — {rawFundsPreview?.filename}
            </DialogTitle>
            <DialogDescription>
              Review totals and duplicates below, tick Verified, then upload.
            </DialogDescription>
          </DialogHeader>
          {rawFundsPreview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-[11px] uppercase text-slate-500">Rows</div>
                  <div className="text-lg font-bold" data-testid="rf-preview-rows">{rawFundsPreview.row_count}</div>
                </div>
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 col-span-2">
                  <div className="text-[11px] uppercase text-emerald-700">Grand Total INR</div>
                  <div className="text-lg font-bold text-emerald-800" data-testid="rf-preview-grand-total">
                    ₹{(rawFundsPreview.grand_total_inr || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-[11px] uppercase text-slate-500">Period</div>
                  <div className="text-xs font-semibold">{rawFundsPreview.date_min || "?"} → {rawFundsPreview.date_max || "?"}</div>
                </div>
              </div>

              {/* Per-bank totals */}
              <div className="border rounded-md overflow-hidden">
                <div className="bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-800">Bank / Source-wise totals</div>
                <table className="w-full text-xs" data-testid="rf-preview-bank-table">
                  <tbody>
                    {(rawFundsPreview.bank_totals || []).map((b) => (
                      <tr key={b.source} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 font-mono">{b.source}</td>
                        <td className="px-3 py-1.5 text-right text-slate-500">{b.count} rows</td>
                        <td className="px-3 py-1.5 text-right font-semibold">₹{(b.sum_inr || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-indigo-200 bg-indigo-50/60 font-bold">
                      <td className="px-3 py-1.5">GRAND TOTAL</td>
                      <td className="px-3 py-1.5 text-right">{rawFundsPreview.row_count} rows</td>
                      <td className="px-3 py-1.5 text-right text-indigo-900">₹{(rawFundsPreview.grand_total_inr || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Duplicate warnings */}
              {(rawFundsPreview.file_duplicate || rawFundsPreview.duplicate_rows > 0) && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="rf-preview-duplicate-warning">
                  <div className="text-sm font-semibold text-amber-800">⚠ Duplicate detected</div>
                  {rawFundsPreview.file_duplicate && (
                    <div className="text-xs text-amber-800">
                      This exact file was already uploaded as <b>{rawFundsPreview.file_duplicate.filename}</b> on{" "}
                      {String(rawFundsPreview.file_duplicate.uploaded_at || "").slice(0, 10)} by {rawFundsPreview.file_duplicate.uploaded_by || "unknown"}.
                    </div>
                  )}
                  {rawFundsPreview.duplicate_rows > 0 && (
                    <div className="text-xs text-amber-800">
                      <b>{rawFundsPreview.duplicate_rows}</b> of {rawFundsPreview.row_count} rows (₹
                      {(rawFundsPreview.duplicate_inr || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}) already exist
                      (same date + amount + narration + client). New rows: <b>{rawFundsPreview.new_rows}</b> (₹
                      {(rawFundsPreview.new_inr || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}).
                    </div>
                  )}
                  <div className="space-y-1.5 pt-1">
                    {[
                      { v: "skip", label: "Skip duplicates", hint: "import only the new rows" },
                      { v: "replace", label: "Replace / overwrite", hint: "delete the old matching rows, then import everything from this sheet" },
                      { v: "add", label: "Upload all anyway", hint: "keep old rows AND add these (duplicates will exist)" },
                    ].map((opt) => (
                      <label key={opt.v} className="flex items-start gap-2 text-xs cursor-pointer" data-testid={`rf-mode-${opt.v}`}>
                        <input
                          type="radio"
                          name="rf-upload-mode"
                          checked={rawFundsUploadMode === opt.v}
                          onChange={() => setRawFundsUploadMode(opt.v)}
                          className="mt-0.5"
                        />
                        <span><b>{opt.label}</b> — {opt.hint}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Verified + Upload */}
              <div className="flex items-center justify-between gap-3 pt-1 border-t">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <Checkbox
                    checked={rawFundsVerified}
                    onCheckedChange={(v) => setRawFundsVerified(!!v)}
                    data-testid="rf-verified-checkbox"
                  />
                  <span className="font-medium">Verified — totals & bank amounts look correct</span>
                </label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setRawFundsPreviewOpen(false)} disabled={rawFundsUploadingSheet} data-testid="rf-cancel-btn">
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={confirmRawFundsUpload}
                    disabled={!rawFundsVerified || rawFundsUploadingSheet}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="rf-upload-confirm-btn"
                  >
                    {rawFundsUploadingSheet ? "Uploading..." : "Upload"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Raw Funds — edit password dialog */}
      <Dialog open={rawPwDialogOpen} onOpenChange={setRawPwDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="raw-funds-edit-password-dialog">
          <DialogHeader>
            <DialogTitle>Edit password required</DialogTitle>
            <DialogDescription>Enter YOUR login password (or the admin edit password) to modify raw data rows.</DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            autoFocus
            value={rawPwInput}
            onChange={(e) => setRawPwInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && rawPwInput) commitRawFieldEdit(rawPwInput); }}
            placeholder="Password"
            data-testid="raw-funds-edit-password-input"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setRawPwDialogOpen(false); setRawFieldEdit(null); }}>Cancel</Button>
            <Button size="sm" disabled={!rawPwInput || rawFieldSaving} onClick={() => commitRawFieldEdit(rawPwInput)} data-testid="raw-funds-edit-password-confirm">
              {rawFieldSaving ? "Saving..." : "Confirm & Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Raw Funds — Duplicate payment scanner dialog */}
      <Dialog open={dupScanOpen} onOpenChange={(o) => { if (!dupMerging) setDupScanOpen(o); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="raw-funds-dup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Copy className="w-4 h-4" /> Duplicate Payments Scanner
            </DialogTitle>
            <DialogDescription>
              Exact duplicates only — date, amount, narration, client, USD, country, customer and source all identical. Merging keeps the earliest copy and archives the rest to trash.
            </DialogDescription>
          </DialogHeader>
          {dupScanLoading ? (
            <div className="py-8 text-center text-sm text-slate-500">Scanning all raw payments…</div>
          ) : dupScan && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300" data-testid="dup-scan-summary">
                  {dupScan.group_count} duplicate group{dupScan.group_count === 1 ? "" : "s"} · {dupScan.extra_rows} extra row{dupScan.extra_rows === 1 ? "" : "s"} · ₹{(dupScan.extra_inr || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })} double-counted
                </Badge>
                <Input
                  type="password"
                  value={dupPw}
                  onChange={(e) => setDupPw(e.target.value)}
                  placeholder={rawEditPassword ? "Password cached ✓" : "Your login password"}
                  className="h-8 w-[190px] text-xs"
                  data-testid="dup-merge-password"
                />
              </div>
              {dupScan.group_count === 0 ? (
                <div className="py-6 text-center text-sm text-emerald-700 font-medium" data-testid="dup-scan-clean">
                  ✓ No duplicate payments found — data is clean
                </div>
              ) : (
                <div className="border rounded-md divide-y divide-slate-100">
                  {dupScan.groups.map((g) => (
                    <div key={g.keep_id} className="flex items-center gap-3 px-3 py-2 text-xs" data-testid={`dup-group-${g.keep_id}`}>
                      <Badge className="bg-amber-100 text-amber-800 border border-amber-300 flex-shrink-0">{g.count}×</Badge>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 truncate">
                          ₹{(g.inward_inr || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })} · {g.date} · {g.client_code || "(no client)"}
                        </div>
                        <div className="text-slate-500 truncate" title={g.narration}>{g.narration || "-"} {g.source ? `· ${g.source}` : ""}</div>
                      </div>
                      <Button
                        size="sm"
                        disabled={dupMerging === g.keep_id}
                        onClick={() => mergeDuplicateGroup(g)}
                        className="bg-amber-600 hover:bg-amber-700 text-white h-7 flex-shrink-0"
                        data-testid={`dup-merge-${g.keep_id}`}
                      >
                        {dupMerging === g.keep_id ? "Merging..." : `Merge → keep 1`}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Raw Funds — delete upload (password-gated) dialog */}
      <Dialog open={!!rawDelUpload} onOpenChange={(o) => { if (!o && !rawDelBusy) setRawDelUpload(null); }}>
        <DialogContent className="max-w-md" data-testid="raw-funds-delete-upload-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Delete upload — {rawDelUpload?.filename}</DialogTitle>
            <DialogDescription>
              This removes <b>{rawDelUpload?.row_count ?? "?"} rows</b> imported from "{rawDelUpload?.filename}".
              Rows are archived to trash before deletion. Enter YOUR login password (or the admin password) to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            autoFocus
            value={rawDelPw}
            onChange={(e) => setRawDelPw(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && rawDelPw) confirmDeleteRawFundsUpload(); }}
            placeholder="Admin password"
            data-testid="raw-funds-delete-upload-password"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setRawDelUpload(null)} disabled={rawDelBusy}>Cancel</Button>
            <Button size="sm" variant="destructive" disabled={!rawDelPw || rawDelBusy} onClick={confirmDeleteRawFundsUpload} data-testid="raw-funds-delete-upload-confirm">
              {rawDelBusy ? "Deleting..." : "Delete Upload"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Follow Up — Handle By password dialog */}
      <Dialog open={handlerPwOpen} onOpenChange={(o) => { if (!handlerSaving) { setHandlerPwOpen(o); if (!o) setHandlerEdit(null); } }}>
        <DialogContent className="max-w-sm" data-testid="fu-handler-password-dialog">
          <DialogHeader>
            <DialogTitle>Password required</DialogTitle>
            <DialogDescription>
              Changing the handler for {handlerEdit?.partner} to "{handlerEdit?.value || "(clear)"}" requires the admin password.
            </DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            autoFocus
            value={handlerPwInput}
            onChange={(e) => setHandlerPwInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && handlerPwInput) commitHandlerEdit(handlerPwInput); }}
            placeholder="Password"
            data-testid="fu-handler-password-input"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setHandlerPwOpen(false); setHandlerEdit(null); }} disabled={handlerSaving}>Cancel</Button>
            <Button size="sm" disabled={!handlerPwInput || handlerSaving} onClick={() => commitHandlerEdit(handlerPwInput)} data-testid="fu-handler-password-confirm">
              {handlerSaving ? "Saving..." : "Confirm & Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Raw Funds — Client Code Summary dialog */}
      <Dialog open={rawClientSummaryOpen} onOpenChange={setRawClientSummaryOpen}>
        <DialogContent className="max-w-2xl" data-testid="raw-funds-client-summary-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-700">
              <BarChart3 className="w-5 h-5" />
              Client Code Summary — Raw Funds
            </DialogTitle>
            <DialogDescription>
              Per-client_code totals for raw funds rows. Case variants &amp; ledger aliases are merged to the canonical code. Empty cells appear as "(suspense)".
              {rawFundsSource ? ` Scoped to source: ${rawFundsSource}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-end gap-2">
              <div>
                <label className="text-[11px] text-slate-500">From</label>
                <Input type="date" value={rawClientSummaryFrom} onChange={(e) => setRawClientSummaryFrom(e.target.value)} className="h-9 w-40" data-testid="raw-client-summary-from" />
              </div>
              <div>
                <label className="text-[11px] text-slate-500">To</label>
                <Input type="date" value={rawClientSummaryTo} onChange={(e) => setRawClientSummaryTo(e.target.value)} className="h-9 w-40" data-testid="raw-client-summary-to" />
              </div>
              <Button size="sm" onClick={openClientSummary} disabled={rawClientSummaryLoading} data-testid="raw-client-summary-apply">
                {rawClientSummaryLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Apply"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setRawClientSummaryFrom(""); setRawClientSummaryTo(""); setTimeout(openClientSummary, 0); }}>
                Clear
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open("/client-balance", "_blank")}
                className="border-teal-300 text-teal-700 hover:bg-teal-50"
                data-testid="open-client-balance-tab"
              >
                <BarChart3 className="w-3.5 h-3.5 mr-1" />
                Open Client Balance Tab
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={rawClientSummaryLoading || !rawClientSummaryData || rawClientSummaryData.clients.length === 0}
                onClick={() => {
                  const params = new URLSearchParams();
                  const df = rawClientSummaryFrom || rawFundsDateFrom;
                  const dt = rawClientSummaryTo || rawFundsDateTo;
                  if (df) params.append("date_from", df);
                  if (dt) params.append("date_to", dt);
                  if (rawFundsSource.trim()) params.append("source", rawFundsSource.trim());
                  fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/client-summary/export?${params}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  }).then(async (r) => {
                    if (!r.ok) { toast.error("Export failed"); return; }
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    const cd = r.headers.get("content-disposition") || "";
                    const m = cd.match(/filename="([^"]+)"/);
                    a.download = m ? m[1] : "raw_funds_client_summary.xlsx";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                    toast.success("Excel downloaded");
                  }).catch((e) => toast.error("Export failed: " + e.message));
                }}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 ml-auto"
                data-testid="raw-client-summary-export"
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                Export Excel
              </Button>
            </div>
            <div className="border border-slate-200 rounded-md overflow-hidden max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-teal-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Client Code</th>
                    <th className="text-right p-2">Rows</th>
                    <th className="text-right p-2">Sum INR</th>
                    <th className="text-right p-2">% of total</th>
                  </tr>
                </thead>
                <tbody>
                  {rawClientSummaryLoading ? (
                    <tr><td colSpan={4} className="text-center text-slate-500 p-6"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
                  ) : !rawClientSummaryData || rawClientSummaryData.clients.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-slate-500 p-6 italic">No raw funds rows in this period.</td></tr>
                  ) : rawClientSummaryData.clients.map((c) => {
                    const pct = rawClientSummaryData.grand_sum_inr > 0
                      ? (c.sum_inr / rawClientSummaryData.grand_sum_inr) * 100
                      : 0;
                    return (
                      <tr key={c.client_code} className={`border-b border-slate-100 hover:bg-slate-50 ${c.is_suspense ? "bg-amber-50" : ""}`} data-testid={`raw-client-row-${c.client_code}`}>
                        <td className="p-2 font-semibold">{c.client_code}{c.is_suspense && <span className="ml-1 text-[9px] text-amber-700">(unassigned)</span>}</td>
                        <td className="p-2 text-right">{c.count.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono">₹{c.sum_inr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                        <td className="p-2 text-right text-slate-500">{pct.toFixed(1)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
                {rawClientSummaryData && rawClientSummaryData.clients.length > 0 && (
                  <tfoot className="bg-slate-100 border-t-2 border-slate-300 sticky bottom-0">
                    <tr>
                      <td className="p-2 font-bold">Total ({rawClientSummaryData.total_clients} client{rawClientSummaryData.total_clients !== 1 ? "s" : ""})</td>
                      <td className="p-2 text-right font-bold">{rawClientSummaryData.grand_count.toLocaleString()}</td>
                      <td className="p-2 text-right font-bold text-emerald-700">₹{rawClientSummaryData.grand_sum_inr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                      <td className="p-2 text-right text-slate-500">100.0%</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Raw Funds — Activity History dialog (admin + uploader) */}
      <Dialog open={rawFundsHistoryOpen} onOpenChange={setRawFundsHistoryOpen}>
        <DialogContent className="max-w-3xl" data-testid="raw-funds-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-700">
              <Clock className="w-5 h-5" />
              Raw Funds — Recent Activity
            </DialogTitle>
            <DialogDescription>
              Last 100 client-code reassignments and suspense resolutions. Visible to admin AND uploader for self-service audit.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto border border-slate-200 rounded">
            {rawFundsHistoryLoading ? (
              <div className="p-6 text-center text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
              </div>
            ) : rawFundsHistoryLogs.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">No recent activity.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left">When</th>
                    <th className="px-2 py-2 text-left">User</th>
                    <th className="px-2 py-2 text-left">Action</th>
                    <th className="px-2 py-2 text-left">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {rawFundsHistoryLogs.map((log, idx) => (
                    <tr key={idx} className="border-t hover:bg-slate-50">
                      <td className="px-2 py-1.5 font-mono text-[10px]">
                        {log.timestamp ? new Date(log.timestamp).toLocaleString() : "-"}
                      </td>
                      <td className="px-2 py-1.5">{log.username || log.user || "—"}{log.role ? <span className="ml-1 text-[9px] text-slate-500">({log.role})</span> : null}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{log.action || "-"}</td>
                      <td className="px-2 py-1.5 text-slate-700">{log.description || log.detail || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRawFundsHistoryOpen(false)}>Close</Button>
            <Button variant="outline" onClick={fetchRawFundsHistory} disabled={rawFundsHistoryLoading}>
              {rawFundsHistoryLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Refresh"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raw Funds — Source Summary dialog */}
      <Dialog open={rawSourceSummaryOpen} onOpenChange={setRawSourceSummaryOpen}>
        <DialogContent className="max-w-2xl" data-testid="raw-funds-source-summary-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700">
              <BarChart3 className="w-5 h-5" />
              Source Summary — Bank/Channel-wise Inward INR
            </DialogTitle>
            <DialogDescription>
              Per-source totals for raw funds rows. Adjust the date range below to slice by period — defaults to the table's current date filter.
              {rawFundsClient ? ` Scoped to client: ${rawFundsClient}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-end gap-2">
              <div>
                <label className="text-[11px] text-slate-500">From</label>
                <Input type="date" value={rawSourceSummaryFrom} onChange={(e) => setRawSourceSummaryFrom(e.target.value)} className="h-9 w-40" data-testid="raw-source-summary-from" />
              </div>
              <div>
                <label className="text-[11px] text-slate-500">To</label>
                <Input type="date" value={rawSourceSummaryTo} onChange={(e) => setRawSourceSummaryTo(e.target.value)} className="h-9 w-40" data-testid="raw-source-summary-to" />
              </div>
              <Button size="sm" onClick={openSourceSummary} disabled={rawSourceSummaryLoading} data-testid="raw-source-summary-apply">
                {rawSourceSummaryLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Apply"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setRawSourceSummaryFrom(""); setRawSourceSummaryTo(""); setTimeout(openSourceSummary, 0); }}>
                Clear
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={rawSourceSummaryLoading || !rawSourceSummaryData || rawSourceSummaryData.sources.length === 0}
                onClick={() => {
                  const params = new URLSearchParams();
                  const df = rawSourceSummaryFrom || rawFundsDateFrom;
                  const dt = rawSourceSummaryTo || rawFundsDateTo;
                  if (df) params.append("date_from", df);
                  if (dt) params.append("date_to", dt);
                  if (rawFundsClient.trim()) params.append("client_code", rawFundsClient.trim());
                  // Use a one-shot anchor + fetch with token (download endpoint requires auth header).
                  fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/source-summary/export?${params}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  }).then(async (r) => {
                    if (!r.ok) { toast.error("Export failed"); return; }
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    const cd = r.headers.get("content-disposition") || "";
                    const m = cd.match(/filename="([^"]+)"/);
                    a.download = m ? m[1] : "raw_funds_source_summary.xlsx";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                    toast.success("Excel downloaded");
                  }).catch((e) => toast.error("Export failed: " + e.message));
                }}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 ml-auto"
                data-testid="raw-source-summary-export"
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                Export Excel
              </Button>
            </div>
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-indigo-50">
                  <tr>
                    <th className="text-left p-2">Source</th>
                    <th className="text-right p-2">Rows</th>
                    <th className="text-right p-2">Sum INR</th>
                    <th className="text-right p-2">% of total</th>
                  </tr>
                </thead>
                <tbody>
                  {rawSourceSummaryLoading ? (
                    <tr><td colSpan={4} className="text-center text-slate-500 p-6"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
                  ) : !rawSourceSummaryData || rawSourceSummaryData.sources.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-slate-500 p-6 italic">No raw funds rows in this period.</td></tr>
                  ) : rawSourceSummaryData.sources.map((s) => {
                    const pct = rawSourceSummaryData.grand_sum_inr > 0
                      ? (s.sum_inr / rawSourceSummaryData.grand_sum_inr) * 100
                      : 0;
                    return (
                      <tr key={s.source} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`raw-source-row-${s.source}`}>
                        <td className="p-2 font-semibold">{s.source}</td>
                        <td className="p-2 text-right">{s.count.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono">₹{s.sum_inr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                        <td className="p-2 text-right text-slate-500">{pct.toFixed(1)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
                {rawSourceSummaryData && rawSourceSummaryData.sources.length > 0 && (
                  <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                    <tr>
                      <td className="p-2 font-bold">Total ({rawSourceSummaryData.total_sources} source{rawSourceSummaryData.total_sources !== 1 ? "s" : ""})</td>
                      <td className="p-2 text-right font-bold">{rawSourceSummaryData.grand_count.toLocaleString()}</td>
                      <td className="p-2 text-right font-bold text-emerald-700">₹{rawSourceSummaryData.grand_sum_inr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                      <td className="p-2 text-right text-slate-500">100.0%</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Raw Funds bulk-delete password dialog */}
      <Dialog open={rawFundsBulkDialogOpen} onOpenChange={setRawFundsBulkDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="w-5 h-5" />
              Delete {rawFundsSelected.size} raw funds row(s)?
            </DialogTitle>
            <DialogDescription>
              This permanently removes the selected rows from the accounting raw data.
              Auto-verification on already-processed partner uploads is unaffected.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-600">Admin password</label>
            <Input
              type="password"
              value={rawFundsBulkPassword}
              onChange={(e) => setRawFundsBulkPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleRawFundsBulkDelete(); }}
              placeholder="Enter password"
              autoFocus
              data-testid="raw-funds-bulk-password"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setRawFundsBulkDialogOpen(false); setRawFundsBulkPassword(""); }} data-testid="raw-funds-bulk-cancel">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRawFundsBulkDelete}
              disabled={rawFundsBulkDeleting || !rawFundsBulkPassword}
              data-testid="raw-funds-bulk-confirm"
            >
              {rawFundsBulkDeleting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
              Permanently delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verification Dialog - Outside Tabs so it works from any tab */}
      <Dialog open={verifyDialog} onOpenChange={setVerifyDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Receipt className="w-5 h-5 text-emerald-600" />
              </div>
              Verify Payment - {selectedPayment?.client_code}
            </DialogTitle>
          </DialogHeader>

          {selectedPayment && (
            <div className="space-y-6">
              {/* Pre-Verified Notice */}
              {selectedPayment.verification_status === "verified_at_upload" && (
                <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                  <div className="flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-cyan-600" />
                    <div>
                      <p className="font-medium text-cyan-800">Pre-Verified in Excel Upload</p>
                      <p className="text-sm text-cyan-600">
                        This payment was marked as verified by {selectedPayment.verified_by || "employee"} 
                        during upload. Please review and finalize the verification.
                      </p>
                      {selectedPayment.verification_notes && (
                        <p className="text-sm text-cyan-700 mt-1">
                          <span className="font-medium">Upload Notes:</span> {selectedPayment.verification_notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* From Partner Upload */}
              <div className="p-4 bg-slate-50 rounded-lg border">
                <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  From Partner Upload
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Tracking:</span>
                    <span className="ml-2 font-mono">{selectedPayment.tracking}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Date:</span>
                    <span className="ml-2">{selectedPayment.payment_date_display || formatDateDashDisplay(selectedPayment.payment_date) || "-"}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Claimed Amount:</span>
                    <span className="ml-2 font-bold text-lg">₹{(selectedPayment.claimed_amount || 0).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Mode:</span>
                    <Badge variant="outline" className="ml-2">{selectedPayment.payment_mode}</Badge>
                  </div>
                  {selectedPayment.customer && (
                    <div className="col-span-2">
                      <span className="text-slate-500">Customer:</span>
                      <span className="ml-2">{selectedPayment.customer}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actual Bank Receipt */}
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                  <Banknote className="w-4 h-4" />
                  Actual Bank Receipt
                </h4>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Amount Received (₹)</Label>
                      <Input type="number" value={verificationForm.actual_amount} onChange={(e) => setVerificationForm({...verificationForm, actual_amount: e.target.value})} placeholder="Enter actual amount" className="mt-1" />
                    </div>
                    <div>
                      <Label>Received Date</Label>
                      <DMYDateInput
  value={verificationForm.received_date} onChange={(e) => setVerificationForm({...verificationForm, received_date: e.target.value})} className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Bank Reference / UTR</Label>
                    <Input value={verificationForm.bank_reference} onChange={(e) => setVerificationForm({...verificationForm, bank_reference: e.target.value})} placeholder="Enter bank reference number" className="mt-1" />
                  </div>
                  <div className="pt-2">
                    <Label className="mb-2 block">Verification Status</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button variant={verificationForm.verification_status === "verified_full" ? "default" : "outline"} size="sm" onClick={() => setVerificationForm({...verificationForm, verification_status: "verified_full", actual_amount: selectedPayment.claimed_amount?.toString() || ""})} className={verificationForm.verification_status === "verified_full" ? "bg-green-600 hover:bg-green-700" : ""}>
                        <CheckCircle className="w-4 h-4 mr-1" />Full Payment
                      </Button>
                      <Button variant={verificationForm.verification_status === "verified_adjusted" ? "default" : "outline"} size="sm" onClick={() => setVerificationForm({...verificationForm, verification_status: "verified_adjusted"})} className={verificationForm.verification_status === "verified_adjusted" ? "bg-blue-600 hover:bg-blue-700" : ""}>
                        <AlertTriangle className="w-4 h-4 mr-1" />Partial (with deductions)
                      </Button>
                      <Button variant={verificationForm.verification_status === "rejected" ? "default" : "outline"} size="sm" onClick={() => setVerificationForm({...verificationForm, verification_status: "rejected", actual_amount: "0"})} className={verificationForm.verification_status === "rejected" ? "bg-red-600 hover:bg-red-700" : ""}>
                        <XCircle className="w-4 h-4 mr-1" />Not Received
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Difference Breakdown */}
              {verificationForm.verification_status !== "verified_full" && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />Difference Breakdown
                  </h4>
                  <div className="mb-3 p-3 bg-white rounded border">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Difference:</span>
                      <span className={`text-xl font-bold ${calculateDifference() > 0 ? "text-red-600" : "text-green-600"}`}>
                        {calculateDifference() > 0 ? "-" : "+"}₹{Math.abs(calculateDifference()).toLocaleString()}
                        <span className="text-sm font-normal text-slate-500 ml-1">({calculateDifference() > 0 ? "Shortfall" : "Overpayment"})</span>
                      </span>
                    </div>
                  </div>
                  <div className="mb-3">
                    <Label className="text-sm">Reason for Difference</Label>
                    <Select value={verificationForm.difference_reason} onValueChange={(value) => setVerificationForm({...verificationForm, difference_reason: value})}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select reason..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Bank Charges">Bank Charges</SelectItem>
                        <SelectItem value="Forex Charges">Forex Charges</SelectItem>
                        <SelectItem value="Error">Entry Error</SelectItem>
                        <SelectItem value="Lesser Amount Came">Lesser Amount Came</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Bank Charges (₹)</Label>
                      <Input type="number" value={verificationForm.bank_charges} onChange={(e) => setVerificationForm({...verificationForm, bank_charges: e.target.value})} placeholder="0" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-sm">Forex Charges (₹)</Label>
                      <Input type="number" value={verificationForm.forex_charges} onChange={(e) => setVerificationForm({...verificationForm, forex_charges: e.target.value})} placeholder="0" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-sm">Entry Error (₹)</Label>
                      <Input type="number" value={verificationForm.error_amount} onChange={(e) => setVerificationForm({...verificationForm, error_amount: e.target.value})} placeholder="0" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-sm">Lesser Amount (₹)</Label>
                      <Input type="number" value={verificationForm.lesser_amount} onChange={(e) => setVerificationForm({...verificationForm, lesser_amount: e.target.value})} placeholder="0" className="mt-1" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-sm">Additional Notes</Label>
                      <Input value={verificationForm.other_notes} onChange={(e) => setVerificationForm({...verificationForm, other_notes: e.target.value})} placeholder="Any additional details about the deduction" className="mt-1" />
                    </div>
                  </div>
                  <div className="mt-3 p-2 bg-white rounded border flex justify-between items-center">
                    <span className="text-slate-600 text-sm">Breakdown Total:</span>
                    <span className={`font-semibold ${calculateBreakdownTotal() === Math.abs(calculateDifference()) ? "text-green-600" : "text-amber-600"}`}>
                      ₹{calculateBreakdownTotal().toLocaleString()} / ₹{Math.abs(calculateDifference()).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Ledger Adjustment Preview */}
              {verificationForm.verification_status !== "verified_full" && calculateDifference() !== 0 && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />Ledger Adjustment Preview
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="p-2 bg-white rounded border flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Original Entry: Credit ₹{(selectedPayment.claimed_amount || 0).toLocaleString()} (from upload)</span>
                    </div>
                    {calculateDifference() > 0 && (
                      <div className="p-2 bg-red-50 rounded border border-red-200 flex items-center gap-2">
                        <ArrowDownRight className="w-4 h-4 text-red-500" />
                        <span className="text-red-700">Adjustment: DEBIT ₹{calculateDifference().toLocaleString()} (reduces credit)</span>
                      </div>
                    )}
                    {calculateDifference() < 0 && (
                      <div className="p-2 bg-green-50 rounded border border-green-200 flex items-center gap-2">
                        <ArrowUpRight className="w-4 h-4 text-green-500" />
                        <span className="text-green-700">Adjustment: CREDIT ₹{Math.abs(calculateDifference()).toLocaleString()} (increases credit)</span>
                      </div>
                    )}
                    <div className="p-2 bg-emerald-100 rounded border border-emerald-300 flex items-center justify-between">
                      <span className="font-medium text-emerald-800">Net Effect:</span>
                      <span className="font-bold text-emerald-700">Credit ₹{(parseFloat(verificationForm.actual_amount) || 0).toLocaleString()} ✓</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Verification Notes */}
              <div>
                <Label>Verification Notes</Label>
                <Input value={verificationForm.verification_notes} onChange={(e) => setVerificationForm({...verificationForm, verification_notes: e.target.value})} placeholder="Add any notes (e.g., TDS certificate received, bank statement attached)" className="mt-1" />
              </div>

              {/* Already Verified Info */}
              {selectedPayment.verification_status !== "pending" && (
                <div className="p-3 bg-slate-100 rounded-lg text-sm">
                  <p className="text-slate-600">
                    <span className="font-medium">Verified by:</span> {selectedPayment.verified_by || "Unknown"} on {selectedPayment.verified_at ? new Date(selectedPayment.verified_at).toLocaleString() : "-"}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setVerifyDialog(false)}>Cancel</Button>
            <Button onClick={handleVerify} disabled={verifying} className={verificationForm.verification_status === "rejected" ? "bg-red-600 hover:bg-red-700" : verificationForm.verification_status === "verified_adjusted" ? "bg-blue-600 hover:bg-blue-700" : "bg-green-600 hover:bg-green-700"}>
              {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              {verificationForm.verification_status === "verified_full" ? "Verify - Full Match" : verificationForm.verification_status === "verified_adjusted" ? "Verify & Adjust Ledger" : "Reject & Reverse Ledger"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Logs Dialog (Admin only) */}
      <Dialog open={clearLogsDialog} onOpenChange={setClearLogsDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Permanently Clear Logs
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              {selectedLogIds.size > 0
                ? `This will permanently delete ${selectedLogIds.size} selected log entries.`
                : `This will permanently delete ALL ${deletionLogs.length} log entries.`}
              This action cannot be undone.
            </p>
            <div>
              <Label>Admin Password</Label>
              <Input
                type="password"
                placeholder="Enter your admin password"
                value={clearLogsPassword}
                onChange={(e) => setClearLogsPassword(e.target.value)}
                data-testid="clear-logs-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setClearLogsDialog(false); setClearLogsPassword(""); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleClearLogs} disabled={clearingLogs || !clearLogsPassword} data-testid="confirm-clear-logs-btn">
              {clearingLogs ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {selectedLogIds.size > 0 ? "Clear Selected" : "Clear All Logs"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wipe Data Dialog */}
      <Dialog open={wipeDialog} onOpenChange={setWipeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Wipe Payment Data
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              ⚠️ <strong>Warning:</strong> This action will permanently delete payment verification data. This cannot be undone.
            </div>
            
            <div className="space-y-2">
              <Label>Wipe Type</Label>
              <Select value={wipeType} onValueChange={setWipeType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">By Client Code</SelectItem>
                  <SelectItem value="date_range">By Date Range</SelectItem>
                  <SelectItem value="all">All Data (DANGEROUS)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {wipeType === "client" && (
              <div className="space-y-2">
                <Label>Client Code</Label>
                <Select value={wipeClientCode} onValueChange={setWipeClientCode}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client} value={client}>{client}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {wipeType === "date_range" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>From Date</Label>
                  <DMYDateInput value={wipeDateFrom} onChange={(e) => setWipeDateFrom(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>To Date</Label>
                  <DMYDateInput value={wipeDateTo} onChange={(e) => setWipeDateTo(e.target.value)} />
                </div>
              </div>
            )}
            
            {wipeType === "all" && (
              <div className="bg-red-100 border border-red-300 rounded-lg p-3 text-sm text-red-900">
                🚨 <strong>DANGER:</strong> This will delete ALL payment verification data from the system!
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Admin Password</Label>
              <Input 
                type="password" 
                placeholder="Enter password to confirm"
                value={wipePassword}
                onChange={(e) => setWipePassword(e.target.value)}
              />
              <p className="text-xs text-slate-500">Password required for security</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setWipeDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleWipeData}
              disabled={wiping || !wipePassword}
            >
              {wiping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Wipe Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Selected Dialog */}
      <Dialog open={deleteSelectedDialog} onOpenChange={setDeleteSelectedDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Selected Payments
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              You are about to delete <strong>{(activeTab === "verified-done" ? verifiedSelectedPayments : selectedPayments).length}</strong> selected payment record(s). This action cannot be undone.
            </div>
            
            <div className="space-y-2">
              <Label>Your Login Password</Label>
              <Input 
                type="password" 
                placeholder="Enter your login password to confirm"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
              />
              <p className="text-xs text-slate-500">Enter your own account password for verification</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setDeleteSelectedDialog(false);
              setDeletePassword("");
            }}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteSelected}
              disabled={deletingSelected || !deletePassword}
            >
              {deletingSelected ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete {(activeTab === "verified-done" ? verifiedSelectedPayments : selectedPayments).length} Payment(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspicious Ledger Cleanup Modal */}
      <Dialog open={cleanupModal.open} onOpenChange={(o) => !cleanupLoading && setCleanupModal((m) => ({ ...m, open: o }))}>
        <DialogContent data-testid="cleanup-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {cleanupModal.action === "delete" ? (
                <><Trash2 className="w-5 h-5 text-red-600" /> Delete Ledger Row</>
              ) : (
                <><Wrench className="w-5 h-5 text-blue-600" /> Rename Ledger Row</>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
              <div className="font-mono text-slate-800">{cleanupModal.code}</div>
              <div className="text-xs text-slate-600 mt-1">{cleanupModal.orderCount} shipment order(s) reference this code.</div>
            </div>
            {cleanupModal.action === "rename" && (
              <div>
                <Label className="text-xs">New Client Code</Label>
                <Input
                  autoFocus
                  value={cleanupModal.newCode}
                  onChange={(e) => setCleanupModal((m) => ({ ...m, newCode: e.target.value }))}
                  placeholder="e.g. CARE EXIM"
                  className="mt-1 font-mono"
                  data-testid="cleanup-new-code"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  All {cleanupModal.orderCount} shipment orders will be re-assigned to this code. If it already exists in ledgers, this entry will be merged into it.
                </p>
              </div>
            )}
            {cleanupModal.action === "delete" && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                The ledger row will be removed. Shipment orders are left intact — find them via the Orders panel if needed.
              </div>
            )}
            <div>
              <Label className="text-xs">Admin Password</Label>
              <Input
                type="password"
                value={cleanupModal.password}
                onChange={(e) => setCleanupModal((m) => ({ ...m, password: e.target.value }))}
                placeholder="Super admin password"
                className="mt-1"
                data-testid="cleanup-password"
                onKeyDown={(e) => { if (e.key === "Enter") submitCleanup(); }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCleanupModal((m) => ({ ...m, open: false }))}
              disabled={cleanupLoading}
              data-testid="cleanup-cancel"
            >
              Cancel
            </Button>
            <Button
              variant={cleanupModal.action === "delete" ? "destructive" : "default"}
              onClick={submitCleanup}
              disabled={cleanupLoading}
              data-testid="cleanup-confirm"
            >
              {cleanupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {cleanupModal.action === "delete" ? "Delete" : "Rename"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Cleanup Modal */}
      <Dialog open={!!restorePromptId} onOpenChange={(o) => !restoreLoading && !o && setRestorePromptId(null)}>
        <DialogContent data-testid="restore-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-emerald-600" /> Restore Cleanup Action
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm text-slate-600">
              This will reverse the delete or rename. Ledger row(s) will be re-inserted from the snapshot, and any shipment_orders that were moved will be reverted to the original code.
            </div>
            <div>
              <Label className="text-xs">Admin Password</Label>
              <Input
                type="password"
                value={restorePassword}
                onChange={(e) => setRestorePassword(e.target.value)}
                placeholder="Super admin password"
                className="mt-1"
                autoFocus
                data-testid="restore-password"
                onKeyDown={(e) => { if (e.key === "Enter") submitRestore(); }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestorePromptId(null)} disabled={restoreLoading} data-testid="restore-cancel">
              Cancel
            </Button>
            <Button onClick={submitRestore} disabled={restoreLoading} data-testid="restore-confirm">
              {restoreLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StaleUploadsCleanupDialog
        open={staleCleanupOpen}
        onOpenChange={setStaleCleanupOpen}
        token={token}
        isSuperAdmin={user?.role === "super_admin"}
        onArchived={() => fetchUploadedSheets(uploadedSheetsPage, uploadedSheetsPartner)}
      />
    </div>
  );
};

export default PaymentVerificationPage;
